import { createClient, FunctionsHttpError } from '@supabase/supabase-js';
import { startOfWeek, format } from 'date-fns';
import { localDB } from './localDB';
import { getLocalTodayIsoRange } from './getDateRange';
import {
  computeWeekStats,
  computeFourWeekTrend,
  computeTypeDistribution,
  computeBestCallTimes,
  computeCoachingFlags,
  computeMetricSparklines,
  mergeGoals,
  DEFAULT_GOALS,
} from './roomTypeMetrics';
import { slugify } from './funnelUtils';
import { getPmEventLiveUrl, normalizeCustomDomain } from './pmEventUtils';
import { buildPmEventRegistrationNotes } from './pmEventRegistrationNotes';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const useMock = !supabaseUrl || !supabaseAnonKey;
export const supabase = useMock ? null : createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

if (useMock && import.meta.env.PROD) {
  console.error(
    '[api] Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY in production build; ' +
    'running on localStorage mock. This is almost certainly a configuration error. ' +
    'See docs/AGENCY_ONBOARDING_CHECKLIST.md \u00a71 to fix.'
  );
}

/**
 * Production URL passed to `resetPasswordForEmail({ redirectTo })`.
 * Add the same URL in Supabase Dashboard → Authentication → URL Configuration → Redirect URLs:
 * https://crm.prosperityleaders.net/reset-password
 */
export const PASSWORD_RESET_REDIRECT_URL = 'https://crm.prosperityleaders.net/reset-password';

/** Session from local storage; avoids a round-trip to Auth on cold load (getUser). */
async function getSessionUserId() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id ?? null;
}

const PENDING_SIGNUP_KEY = 'pending_agency_signup';

let currentMockUserId = localStorage.getItem('mock_user_id');

/**
 * In-flight profile lookups, keyed by auth user id. Several callers resolve the
 * same profile in one tick (the leads list, the lead detail page), and each of
 * those round trips is a full RLS pass over `users`. Sharing the pending promise
 * collapses them without caching across ticks, so nothing goes stale.
 */
const profileRequestsInFlight = new Map();

async function fetchProfileRowForAuthUser(authUserId) {
  const pending = profileRequestsInFlight.get(authUserId);
  if (pending) return pending;

  const request = (async () => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', authUserId)
      .maybeSingle();
    if (error) throw error;
    return data;
  })();

  profileRequestsInFlight.set(authUserId, request);
  try {
    return await request;
  } finally {
    profileRequestsInFlight.delete(authUserId);
  }
}

/** Resolve lead id sets for filters that need a secondary query (AND with main query). */
async function resolveLeadsFilterIdSets(agencyId, filters) {
  let pendingLeadIds = null;
  let enrolledLeadIds = null;

  if (filters?.exclusivityPending) {
    const { data, error } = await supabase
      .from('exclusivity_requests')
      .select('lead_id')
      .eq('agency_id', agencyId)
      .eq('status', 'pending');
    if (error) throw error;
    pendingLeadIds = [...new Set((data || []).map((r) => r.lead_id).filter(Boolean))];
  }

  if (filters?.sequenceEnrolled === 'enrolled' || filters?.sequenceEnrolled === 'not_enrolled') {
    const { data, error } = await supabase
      .from('email_sequence_enrollments')
      .select('lead_id')
      .eq('agency_id', agencyId)
      .in('status', ['active', 'completed']);
    if (error) throw error;
    enrolledLeadIds = [...new Set((data || []).map((r) => r.lead_id).filter(Boolean))];
  }

  return { pendingLeadIds, enrolledLeadIds };
}

function applyLeadsQueryFilters(query, filters) {
  if (!filters) return query;
  if (filters.type) query = query.eq('lead_type', filters.type);
  if (filters.notType) query = query.neq('lead_type', filters.notType);
  if (filters.channel) query = query.eq('channel', filters.channel);
  if (filters.status) query = query.eq('status', filters.status);
  if (filters.roomType) query = query.eq('room_type', filters.roomType);
  if (filters.exclusivityStatus) query = query.eq('exclusivity_status', filters.exclusivityStatus);
  if (filters.pipelineStage) query = query.eq('pipeline_stage', filters.pipelineStage);
  if (filters.importLabel) query = query.ilike('import_label', `%${filters.importLabel}%`);
  if (filters.eventTitle) query = query.ilike('event_title', `%${filters.eventTitle}%`);
  if (filters.splitApplies === true) query = query.eq('split_applies', true);
  if (filters.hasEmail === true) query = query.not('email', 'is', null).neq('email', '');
  if (filters.hasPhone === true) query = query.not('phone', 'is', null).neq('phone', '');
  if (filters.createdAfter) query = query.gte('created_at', filters.createdAfter);
  if (filters.createdBefore) query = query.lte('created_at', filters.createdBefore);
  if (filters.lastCalledNever === true) query = query.is('last_called_at', null);
  if (filters.lastCalledAfter) query = query.gte('last_called_at', filters.lastCalledAfter);
  if (filters.lastCalledBefore && !filters.lastCalledIncludeNull) {
    query = query.lt('last_called_at', filters.lastCalledBefore);
  }
  if (filters.lastCalledBefore && filters.lastCalledIncludeNull) {
    query = query.or(`last_called_at.is.null,last_called_at.lt.${filters.lastCalledBefore}`);
  }
  return query;
}

/** Columns the leads table actually renders, plus the people embeds it displays. */
const LEADS_LIST_SELECT = [
  'id',
  'first_name',
  'last_name',
  'phone',
  'email',
  'notes',
  'lead_type',
  'status',
  'exclusivity_status',
  'created_at',
  'archived_at',
  'created_by',
  'exclusive_to',
  'co_worker_id',
  'previous_owner_name',
  'previous_owner_email',
  'assignments:lead_assignments(id,user_id,assignment_role)',
  'owner:users!leads_created_by_fkey(id,full_name,email)',
  'exclusive_holder:users!leads_exclusive_to_fkey(id,full_name,email)',
  'co_worker:users!leads_co_worker_id_fkey(id,full_name,email)',
].join(',');

/** Wide select retained for callers that still consume the whole row. */
const LEADS_FULL_SELECT =
  '*,assignments:lead_assignments(*,user:users(*)),owner:users!leads_created_by_fkey(id,full_name,email),exclusive_holder:users!leads_exclusive_to_fkey(id,full_name,email),co_worker:users!leads_co_worker_id_fkey(id,full_name,email)';

/**
 * Cap per activity source on the lead detail feed. The feed is newest-first and
 * merges three sources, so an unbounded fetch on a long-lived lead would pull
 * history nobody scrolls to.
 */
export const LEAD_ACTIVITY_LIMIT = 200;

/** Sort field → concrete leads columns (UI "name" spans two columns). */
const LEADS_SORT_COLUMNS = {
  name: ['first_name', 'last_name'],
  lead_type: ['lead_type'],
  status: ['status'],
  created_at: ['created_at'],
  archived_at: ['archived_at'],
};

/**
 * PostgREST parses `or=(...)` positionally, so a raw search term containing the
 * grammar's delimiters would corrupt the filter. Strip them rather than escape:
 * none are meaningful in a name, phone or email search.
 */
function sanitizeLeadsSearchTerm(raw) {
  return String(raw ?? '')
    .replace(/[(),*%\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** `or` group matching a search term against name / phone / email. */
function buildLeadsSearchOrFilter(rawSearch) {
  const term = sanitizeLeadsSearchTerm(rawSearch);
  if (!term) return null;

  const clauses = [
    `first_name.ilike.%${term}%`,
    `last_name.ilike.%${term}%`,
    `phone.ilike.%${term}%`,
    `email.ilike.%${term}%`,
  ];

  // "jane doe" must still match first_name=Jane last_name=Doe, which the
  // single-column clauses above cannot do on their own.
  const tokens = term.split(' ').filter(Boolean);
  if (tokens.length > 1) {
    const first = tokens[0];
    const rest = tokens.slice(1).join(' ');
    clauses.push(`and(first_name.ilike.%${first}%,last_name.ilike.%${rest}%)`);
  }

  return clauses.join(',');
}

/**
 * Resolve everything about the caller's lead visibility that needs its own
 * round trip, so the list, id and count queries can share one resolution.
 */
async function resolveLeadsScope(filters, { agentId } = {}) {
  const authId = await getSessionUserId();
  if (!authId) throw new Error('Not authenticated');
  const profile = await fetchProfileRowForAuthUser(authId);
  if (!profile?.agency_id) throw new Error('No agency profile');

  const lookups = [];

  // Setters read through an explicit `or` group rather than relying on RLS alone.
  let setterAssignedIds = null;
  if (profile.role === 'setter') {
    lookups.push(
      supabase
        .from('lead_assignments')
        .select('lead_id')
        .eq('user_id', profile.id)
        .then(({ data, error }) => {
          if (error) throw error;
          setterAssignedIds = (data || []).map((r) => r.lead_id).filter(Boolean);
        }),
    );
  }

  // The Agent filter also matches leads reached via lead_assignments.
  let agentAssignedIds = null;
  if (agentId) {
    lookups.push(
      supabase
        .from('lead_assignments')
        .select('lead_id')
        .eq('user_id', agentId)
        .then(({ data, error }) => {
          if (error) throw error;
          agentAssignedIds = (data || []).map((r) => r.lead_id).filter(Boolean);
        }),
    );
  }

  let idSets = { pendingLeadIds: null, enrolledLeadIds: null };
  lookups.push(
    resolveLeadsFilterIdSets(profile.agency_id, filters).then((r) => {
      idSets = r;
    }),
  );

  await Promise.all(lookups);

  let setterOrFilter = null;
  if (profile.role === 'setter') {
    const conditions = [];
    if (setterAssignedIds?.length) conditions.push(`id.in.(${setterAssignedIds.join(',')})`);
    conditions.push(`created_by.eq.${profile.id}`);
    conditions.push(`co_worker_id.eq.${profile.id}`);
    conditions.push(`exclusive_to.eq.${profile.id}`);
    conditions.push('exclusivity_status.eq.non_exclusive');
    setterOrFilter = conditions.join(',');
  }

  let agentOrFilter = null;
  if (agentId) {
    const conditions = [
      `created_by.eq.${agentId}`,
      `exclusive_to.eq.${agentId}`,
      `co_worker_id.eq.${agentId}`,
    ];
    if (agentAssignedIds?.length) conditions.push(`id.in.(${agentAssignedIds.join(',')})`);
    agentOrFilter = conditions.join(',');
  }

  return { profile, setterOrFilter, agentOrFilter, ...idSets };
}

/** True when a filter set can only ever match zero rows. */
function leadsScopeIsEmpty(filters, scope) {
  if (filters?.exclusivityPending && scope.pendingLeadIds && scope.pendingLeadIds.length === 0) {
    return true;
  }
  if (
    filters?.sequenceEnrolled === 'enrolled'
    && scope.enrolledLeadIds
    && scope.enrolledLeadIds.length === 0
  ) {
    return true;
  }
  return false;
}

/** Apply filters, visibility scope, search and ordering to a leads query. */
function applyLeadsScope(query, filters, scope, { archived = false, search = null, sortField, sortDir } = {}) {
  query = applyLeadsQueryFilters(query, filters);

  if (filters?.exclusivityPending && scope.pendingLeadIds?.length) {
    query = query.in('id', scope.pendingLeadIds);
  }
  if (filters?.sequenceEnrolled === 'enrolled' && scope.enrolledLeadIds?.length) {
    query = query.in('id', scope.enrolledLeadIds);
  }
  if (filters?.sequenceEnrolled === 'not_enrolled' && scope.enrolledLeadIds?.length) {
    query = query.not('id', 'in', `(${scope.enrolledLeadIds.join(',')})`);
  }

  // Repeated `.or()` groups are AND-ed together by PostgREST.
  if (scope.setterOrFilter) query = query.or(scope.setterOrFilter);
  if (scope.agentOrFilter) query = query.or(scope.agentOrFilter);

  const searchOr = buildLeadsSearchOrFilter(search);
  if (searchOr) query = query.or(searchOr);

  query = archived
    ? query.not('archived_at', 'is', null)
    : query.is('archived_at', null);

  const ascending = sortDir === 'asc';
  const columns = LEADS_SORT_COLUMNS[sortField] || [archived ? 'archived_at' : 'created_at'];
  for (const column of columns) {
    query = query.order(column, { ascending, nullsFirst: false });
  }
  // Stable tiebreaker so a row cannot straddle two pages.
  query = query.order('id', { ascending: true });

  return query;
}

const EMPTY_SNAPSHOT = {
  newLeadsCount: 0,
  callAttempts: 0,
  leadsCalledDistinct: 0,
  appointmentsSet: 0,
  dailyCallGoal: 100,
};

const EMPTY_CALL_OUTCOMES = {
  answered: 0,
  voicemail: 0,
  no_answer: 0,
  busy: 0,
  booked_meeting: 0,
  closed_business: 0,
};

/** Ensure call_logs.called_at is always a valid UTC ISO string for date filters. */
function normalizeCalledAt(value) {
  if (value == null || value === '') return new Date().toISOString();
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

/** Guard against RangeError from Date.prototype.toISOString on invalid values. */
function safeToISO(val) {
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return new Date().toISOString();
  return d.toISOString();
}

/** Parse ISO or YYYY-MM-DD into a local calendar date key. */
function parseLocalDateKey(dateStr) {
  if (dateStr == null || dateStr === '') return null;
  const s = String(dateStr).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return toLocalDateKey(d);
}

function getDefaultLast7DaysDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  start.setHours(0, 0, 0, 0);
  return { startDate: toLocalDateKey(start), endDate: toLocalDateKey(end) };
}

function normalizeChannelActivityRange({ startDate, endDate } = {}) {
  const startKey = parseLocalDateKey(startDate);
  const endKey = parseLocalDateKey(endDate);
  if (startKey && endKey) return { startDate: startKey, endDate: endKey };
  return getDefaultLast7DaysDateRange();
}

function dashboardRangeIsValid(startDate, endDate) {
  if (!startDate || !endDate) return false;
  const s = new Date(startDate);
  const e = new Date(endDate);
  return !Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime());
}

const DEFAULT_SALES_STAGES = [
  'New Lead',
  'First Contact',
  'Follow-Up',
  'Appointment Set',
  'Appointment Done',
  'Closed Won',
  'Closed Lost',
];

const DEFAULT_RECRUITING_STAGES = [
  'New Prospect',
  'Contacted',
  'Interested',
  'Interviewed',
  'Offer Made',
  'Onboarded',
];

const PAGE = 1000;

async function assertAgencyAccess(agencyId) {
  const userId = await getSessionUserId();
  if (!userId) return null;
  const profile = await fetchProfileRowForAuthUser(userId);
  if (!profile?.agency_id || profile.agency_id !== agencyId) return null;
  return profile;
}

function computeNextSendAtFromDelayDays(delayDays) {
  const at = new Date();
  at.setUTCDate(at.getUTCDate() + (Number(delayDays) || 0));
  return at.toISOString();
}

function computeExclusivityExpiresAt(timeframeDays) {
  const days = Number(timeframeDays);
  if (!days || days <= 0) return null;
  const at = new Date();
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString();
}

function mapOwnershipTransferRow(row) {
  const lead = row.lead;
  const leadName = lead
    ? [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim()
    : '';
  return {
    ...row,
    lead_name: leadName || 'Unknown lead',
    requester_name: row.requester?.full_name ?? 'Unknown',
    requester_role: row.requester?.role ?? null,
    current_owner_name: row.current_owner?.full_name ?? 'Unknown',
    resolver_name: row.resolver?.full_name ?? '—',
  };
}

const OWNERSHIP_TRANSFER_SELECT = `
  *,
  lead:leads(first_name, last_name),
  requester:users!lead_ownership_transfer_requests_requested_by_fkey(full_name, role),
  current_owner:users!lead_ownership_transfer_requests_current_owner_id_fkey(full_name),
  resolver:users!lead_ownership_transfer_requests_resolved_by_fkey(full_name)
`;

function isManagerOrAdminRole(role) {
  return ['manager', 'admin', 'super_admin'].includes(role);
}

function getMondayOfWeekForRoomType(date = new Date()) {
  return format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
}

function groupCallsByLeadForApi(calls) {
  const byLead = new Map();
  for (const c of calls) {
    if (!c.lead_id) continue;
    if (!byLead.has(c.lead_id)) byLead.set(c.lead_id, []);
    byLead.get(c.lead_id).push(c);
  }
  for (const list of byLead.values()) {
    list.sort((a, b) => new Date(a.called_at) - new Date(b.called_at));
  }
  return byLead;
}

async function fetchLeadIdsForChannel(agencyId, channel) {
  if (!channel) return null;
  const { data, error } = await supabase
    .from('leads')
    .select('id')
    .eq('agency_id', agencyId)
    .eq('channel', channel);
  if (error) throw error;
  return (data ?? []).map((r) => r.id);
}

async function fetchRoomTypeContext(userId, agencyId, weekStartDate, { lookbackWeeks = 1, channel = null } = {}) {
  if (useMock) return null;
  const profile = await assertAgencyAccess(agencyId);
  if (!profile) return null;
  const authId = await getSessionUserId();
  if (userId !== authId && !isManagerOrAdminRole(profile.role)) {
    throw new Error('Not authorized to view this agent');
  }

  const weekEnd = addDaysToDateString(weekStartDate, 6);
  const rangeStart = addDaysToDateString(weekStartDate, -7 * (lookbackWeeks - 1));
  const startIso = dateStringToLocalStartIso(rangeStart);
  const endIso = dateStringToLocalEndIso(weekEnd);

  let callsQuery = supabase
    .from('call_logs')
    .select('id, lead_id, room_type, outcome, duration_seconds, called_at, agent_id')
    .eq('agency_id', agencyId)
    .eq('agent_id', userId)
    .gte('called_at', startIso)
    .lte('called_at', endIso);

  if (channel) {
    const channelLeadIds = await fetchLeadIdsForChannel(agencyId, channel);
    if (!channelLeadIds.length) return { calls: [], leadMeta: new Map() };
    callsQuery = callsQuery.in('lead_id', channelLeadIds);
  }

  const { data: calls, error: callErr } = await callsQuery.order('called_at', { ascending: true });
  if (callErr) throw callErr;

  const leadIds = [...new Set((calls ?? []).map((c) => c.lead_id).filter(Boolean))];
  const leadMeta = new Map();
  if (leadIds.length) {
    const { data: leads, error: leadErr } = await supabase
      .from('leads')
      .select('id, status')
      .in('id', leadIds);
    if (leadErr) throw leadErr;
    for (const lead of leads ?? []) {
      leadMeta.set(lead.id, { statusNow: lead.status, statusBeforeWeek: lead.status });
    }
  }

  return { calls: calls ?? [], leadMeta };
}

async function applyRoomTypeToCallAndLead(callLogId, leadId, roomType) {
  if (!roomType) return;
  if (callLogId) {
    await supabase.from('call_logs').update({ room_type: roomType }).eq('id', callLogId);
  }
  if (leadId) {
    await supabase.from('leads').update({ room_type: roomType }).eq('id', leadId);
  }
}

const KPI_PICKUP_OUTCOMES = new Set(['answered', 'booked_meeting', 'closed_business']);
const KPI_TRANSIENT_OUTCOMES = new Set(['ringing', 'connecting']);

function addDaysToDateString(isoDate, days) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

function getWeekDayStrings(weekStartDate) {
  return Array.from({ length: 7 }, (_, i) => addDaysToDateString(weekStartDate, i));
}

function toLocalDateKey(isoOrDate) {
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return toLocalDateKey(new Date());
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateStringToLocalStartIso(dateStr) {
  const key = parseLocalDateKey(dateStr);
  if (!key) return safeToISO(new Date());
  const [y, m, d] = key.split('-').map(Number);
  return safeToISO(new Date(y, m - 1, d, 0, 0, 0, 0));
}

function dateStringToLocalEndIso(dateStr) {
  const key = parseLocalDateKey(dateStr);
  if (!key) return safeToISO(new Date());
  const [y, m, d] = key.split('-').map(Number);
  return safeToISO(new Date(y, m - 1, d, 23, 59, 59, 999));
}

function emptyKpiAutoDay() {
  return {
    total_called: 0,
    pickups: 0,
    thirty_sec_convos: 0,
    two_min_convos: 0,
    appts_set: 0,
    appts_on_calendar: 0,
    appts_showed: 0,
  };
}

function initKpiAutoStatsByDays(dayStrings) {
  const byDay = {};
  for (const d of dayStrings) byDay[d] = emptyKpiAutoDay();
  return byDay;
}

function appointmentAgentId(row) {
  return row.assigned_to || row.created_by;
}

function aggregateKpiCallLogs(rows, byDay) {
  for (const row of rows) {
    const day = toLocalDateKey(row.called_at);
    if (!byDay[day]) continue;
    const outcome = row.outcome;
    if (!outcome || KPI_TRANSIENT_OUTCOMES.has(outcome)) continue;
    byDay[day].total_called += 1;
    if (KPI_PICKUP_OUTCOMES.has(outcome)) {
      byDay[day].pickups += 1;
      const dur = Number(row.duration_seconds) || 0;
      if (dur >= 30) byDay[day].thirty_sec_convos += 1;
      if (dur >= 120) byDay[day].two_min_convos += 1;
    }
  }
}

function aggregateKpiAppointments(rows, byDay, userId) {
  for (const row of rows) {
    const agentId = appointmentAgentId(row);
    if (agentId !== userId) continue;

    if (row.created_by === userId) {
      const createdDay = toLocalDateKey(row.created_at);
      if (byDay[createdDay]) byDay[createdDay].appts_set += 1;
    }

    const schedDay = toLocalDateKey(row.scheduled_at);
    if (byDay[schedDay] && row.status !== 'cancelled') {
      byDay[schedDay].appts_on_calendar += 1;
    }
    if (byDay[schedDay] && row.status === 'completed') {
      byDay[schedDay].appts_showed += 1;
    }
  }
}

function aggregateKpiActivityLogs(rows, byDay) {
  for (const row of rows) {
    const day = row.activity_date;
    if (!byDay[day]) continue;
    switch (row.activity_type) {
      case 'presentation':
        byDay[day].presentations = (byDay[day].presentations || 0) + 1;
        break;
      case 'app_submitted':
        byDay[day].apps_submitted = (byDay[day].apps_submitted || 0) + 1;
        byDay[day].ap_submitted_dollars =
          (byDay[day].ap_submitted_dollars || 0) + (Number(row.amount_dollars) || 0);
        break;
      case 'referral':
        byDay[day].referrals_gathered = (byDay[day].referrals_gathered || 0) + 1;
        break;
      case 'objection':
        byDay[day].objections_received = (byDay[day].objections_received || 0) + 1;
        break;
      case 'manual_calls':
        byDay[day].manual_calls = (byDay[day].manual_calls || 0) + (Number(row.call_count) || 0);
        break;
      default:
        break;
    }
  }
}

async function fetchKpiManualWeekLogs(userId, agencyId, weekStartDate, { channel = null } = {}) {
  const weekEnd = addDaysToDateString(weekStartDate, 6);
  let q = supabase
    .from('daily_kpi_logs')
    .select('*')
    .eq('user_id', userId)
    .eq('agency_id', agencyId)
    .gte('date', weekStartDate)
    .lte('date', weekEnd);
  if (channel) q = q.eq('channel', channel);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

async function fetchKpiAutoStatsForUser(userId, agencyId, dateRange, { channel = null } = {}) {
  const dayKeys =
    dateRange.endDate === addDaysToDateString(dateRange.startDate, 6)
      ? getWeekDayStrings(dateRange.startDate)
      : (() => {
          const all = [];
          let cur = dateRange.startDate;
          while (cur <= dateRange.endDate) {
            all.push(cur);
            cur = addDaysToDateString(cur, 1);
          }
          return all;
        })();
  const byDay = initKpiAutoStatsByDays(dayKeys);
  const rangeStart = dateStringToLocalStartIso(dateRange.startDate);
  const rangeEnd = dateStringToLocalEndIso(dateRange.endDate);

  let channelLeadIds = null;
  if (channel) {
    channelLeadIds = await fetchLeadIdsForChannel(agencyId, channel);
    if (!channelLeadIds.length) return byDay;
  }

  const callLogsQuery = (from, to) => {
    let b = supabase
      .from('call_logs')
      .select('called_at, outcome, duration_seconds, lead_id')
      .eq('agency_id', agencyId)
      .eq('agent_id', userId)
      .gte('called_at', rangeStart)
      .lte('called_at', rangeEnd);
    if (channelLeadIds) b = b.in('lead_id', channelLeadIds);
    return b.range(from, to);
  };

  const apptCreatedQuery = (from, to) => {
    let b = supabase
      .from('appointments')
      .select('id, created_at, scheduled_at, status, created_by, assigned_to, lead_id')
      .eq('agency_id', agencyId)
      .eq('created_by', userId)
      .gte('created_at', rangeStart)
      .lte('created_at', rangeEnd);
    if (channelLeadIds) b = b.in('lead_id', channelLeadIds);
    return b.range(from, to);
  };

  const apptScheduledQuery = (from, to) => {
    let b = supabase
      .from('appointments')
      .select('id, created_at, scheduled_at, status, created_by, assigned_to, lead_id')
      .eq('agency_id', agencyId)
      .or(`created_by.eq.${userId},assigned_to.eq.${userId}`)
      .gte('scheduled_at', rangeStart)
      .lte('scheduled_at', rangeEnd);
    if (channelLeadIds) b = b.in('lead_id', channelLeadIds);
    return b.range(from, to);
  };

  const activityQuery = (from, to) => {
    let b = supabase
      .from('activity_logs')
      .select('activity_type, activity_date, amount_dollars, call_count, lead_id')
      .eq('agency_id', agencyId)
      .eq('agent_id', userId)
      .in('activity_type', [
        'presentation',
        'app_submitted',
        'referral',
        'objection',
        'manual_calls',
      ])
      .gte('activity_date', dateRange.startDate)
      .lte('activity_date', dateRange.endDate);
    if (channelLeadIds) b = b.in('lead_id', channelLeadIds);
    return b.range(from, to);
  };

  const [callRows, apptCreatedRows, apptScheduledRows, activityRows] = await Promise.all([
    paginateSelect(callLogsQuery),
    paginateSelect(apptCreatedQuery),
    paginateSelect(apptScheduledQuery),
    paginateSelect(activityQuery),
  ]);

  const apptById = new Map();
  for (const row of [...apptCreatedRows, ...apptScheduledRows]) {
    apptById.set(row.id, row);
  }
  aggregateKpiCallLogs(callRows, byDay);
  aggregateKpiAppointments([...apptById.values()], byDay, userId);
  aggregateKpiActivityLogs(activityRows, byDay);
  return byDay;
}

async function countLeadsCreatedInRange(agencyId, userId, dateRange, { channel, leadType } = {}) {
  let q = supabase
    .from('leads')
    .select('id', { count: 'exact', head: true })
    .eq('agency_id', agencyId)
    .eq('created_by', userId)
    .gte('created_at', dateStringToLocalStartIso(dateRange.startDate))
    .lte('created_at', dateStringToLocalEndIso(dateRange.endDate));
  if (channel) q = q.eq('channel', channel);
  if (leadType) q = q.eq('lead_type', leadType);
  const { count, error } = await q;
  if (error) throw error;
  return count ?? 0;
}

async function fetchChannelActivityForPeriod(agencyId, dateRange, { agentId = null } = {}) {
  const normalized = normalizeChannelActivityRange(dateRange);
  const channels = ['LD', 'IM', 'AM'];
  const rangeStart = dateStringToLocalStartIso(normalized.startDate);
  const rangeEnd = dateStringToLocalEndIso(normalized.endDate);
  const result = {};

  for (const channel of channels) {
    const leadIds = await fetchLeadIdsForChannel(agencyId, channel);
    const empty = {
      leadsAdded: 0,
      callsMade: 0,
      appointmentsSet: 0,
      appsSubmitted: 0,
    };

    let leadsQ = supabase
      .from('leads')
      .select('id', { count: 'exact', head: true })
      .eq('agency_id', agencyId)
      .eq('channel', channel);
    if (rangeStart && rangeEnd) {
      leadsQ = leadsQ.gte('created_at', rangeStart).lte('created_at', rangeEnd);
    }
    if (agentId) leadsQ = leadsQ.eq('created_by', agentId);

    let callsQ = supabase
      .from('call_logs')
      .select('id', { count: 'exact', head: true })
      .eq('agency_id', agencyId);
    if (rangeStart && rangeEnd) {
      callsQ = callsQ.gte('called_at', rangeStart).lte('called_at', rangeEnd);
    }
    if (agentId) callsQ = callsQ.eq('agent_id', agentId);
    if (leadIds.length) {
      callsQ = callsQ.in('lead_id', leadIds);
    } else {
      const { count: leadsCount, error: leadsErr } = await leadsQ;
      if (leadsErr) throw leadsErr;
      result[channel] = { ...empty, leadsAdded: leadsCount ?? 0 };
      continue;
    }

    let apptsQ = supabase
      .from('leads')
      .select('id', { count: 'exact', head: true })
      .eq('agency_id', agencyId)
      .eq('channel', channel)
      .or('status.eq.appointment_set,pipeline_stage.eq.Appointment Set');
    if (agentId) apptsQ = apptsQ.eq('created_by', agentId);

    let activityQ = supabase
      .from('activity_logs')
      .select('id', { count: 'exact', head: true })
      .eq('agency_id', agencyId)
      .eq('activity_type', 'app_submitted');
    if (rangeStart && rangeEnd) {
      activityQ = activityQ
        .gte('activity_date', normalized.startDate)
        .lte('activity_date', normalized.endDate);
    }
    if (agentId) activityQ = activityQ.eq('agent_id', agentId);
    if (leadIds.length) activityQ = activityQ.in('lead_id', leadIds);

    const [leadsRes, callsRes, apptsRes, appsRes] = await Promise.all([
      leadsQ,
      callsQ,
      apptsQ,
      activityQ,
    ]);
    for (const r of [leadsRes, callsRes, apptsRes, appsRes]) {
      if (r.error) throw r.error;
    }

    result[channel] = {
      leadsAdded: leadsRes.count ?? 0,
      callsMade: callsRes.count ?? 0,
      appointmentsSet: apptsRes.count ?? 0,
      appsSubmitted: appsRes.count ?? 0,
    };
  }

  return result;
}

async function assertManagerForAgencyKpi(agencyId) {
  const profile = await assertAgencyAccess(agencyId);
  if (!profile) throw new Error('Not authenticated');
  if (!isManagerOrAdminRole(profile.role)) {
    throw new Error('Manager or admin access required');
  }
  return profile;
}

async function fetchSequenceStep1DelayDays(sequenceId) {
  const { data, error } = await supabase
    .from('email_sequence_steps')
    .select('delay_days')
    .eq('sequence_id', sequenceId)
    .eq('step_number', 1)
    .maybeSingle();
  if (error) throw error;
  return data?.delay_days ?? 0;
}

function normalizeEnrollmentRow(row) {
  const seq = row.email_sequences;
  const steps = (seq?.email_sequence_steps ?? []).sort(
    (a, b) => (a.step_number ?? 0) - (b.step_number ?? 0),
  );
  const totalSteps = steps.length;
  const currentStepNum = row.current_step ?? 1;
  const currentStep = steps.find((s) => s.step_number === currentStepNum);
  const { email_sequences: _seq, enrolled_by_user: _user, ...rest } = row;
  return {
    ...rest,
    sequence_name: seq?.name ?? 'Unknown',
    total_steps: totalSteps,
    current_step_label:
      totalSteps > 0 ? `Step ${currentStepNum} of ${totalSteps}` : `Step ${currentStepNum}`,
    current_step_subject: currentStep?.subject ?? null,
    enrolled_by_name: row.enrolled_by_user?.full_name ?? null,
  };
}

/** Paginate `select()` until fewer than `PAGE` rows (caller supplies fresh query each time via factory). */
async function paginateSelect(factory) {
  const rows = [];
  let from = 0;
  while (true) {
    const { data, error } = await factory(from, from + PAGE - 1);
    if (error) throw error;
    if (!data?.length) break;
    rows.push(...data);
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return rows;
}

async function distinctLeadIdsFromCallLogs(agencyId, { startDate, endDate } = {}) {
  const hasRange = Boolean(startDate && endDate);
  const rows = await paginateSelect((from, to) => {
    let q = supabase.from('call_logs').select('lead_id').eq('agency_id', agencyId);
    if (hasRange) q = q.gte('called_at', startDate).lte('called_at', endDate);
    return q.range(from, to);
  });
  return new Set(rows.map((r) => r.lead_id).filter(Boolean)).size;
}

function countPipelineStages(rows, defaultStage) {
  const map = {};
  for (const row of rows || []) {
    const raw = row.pipeline_stage;
    const label = raw && String(raw).trim() ? String(raw).trim() : defaultStage;
    map[label] = (map[label] || 0) + 1;
  }
  return map;
}

function orderedStageChart(stageLabels, counts) {
  const labels = (stageLabels || []).map((s) => String(s));
  return labels.map((name) => ({ name, count: counts[name] || 0 }));
}

async function completePendingAgencySignup() {
  const raw = localStorage.getItem(PENDING_SIGNUP_KEY);
  if (!raw) return false;
  let pending;
  try {
    pending = JSON.parse(raw);
  } catch {
    localStorage.removeItem(PENDING_SIGNUP_KEY);
    return false;
  }
  const { agencyData, full_name } = pending;
  const { error: rpcError } = await supabase.rpc('complete_agency_signup', {
    p_agency_name: agencyData.name,
    p_slug: agencyData.slug,
    p_full_name: full_name,
  });
  if (rpcError) throw rpcError;
  localStorage.removeItem(PENDING_SIGNUP_KEY);
  return true;
}

function normalizeImportPhone(phone) {
  const digits = String(phone ?? '').replace(/\D/g, '');
  return digits || null;
}

/** Last 10 NANP digits — shared matching logic with edge `_shared/phone.ts`. */
function phoneLast10(phone) {
  const digits = String(phone ?? '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 10) return digits;
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  if (digits.length > 11) return digits.slice(-10);
  return null;
}

function phonesMatch(a, b) {
  const a10 = phoneLast10(a);
  const b10 = phoneLast10(b);
  return Boolean(a10 && b10 && a10 === b10);
}

/** Common US phone format variants for PostgREST `.eq` lookups. */
function phoneEqVariants(raw) {
  const last10 = phoneLast10(raw);
  if (!last10) return [];
  const a = last10.slice(0, 3);
  const b = last10.slice(3, 6);
  const c = last10.slice(6);
  const variants = [
    last10,
    `1${last10}`,
    `+1${last10}`,
    `${a}-${b}-${c}`,
    `(${a}) ${b}-${c}`,
    `(${a})${b}-${c}`,
    `(${a}) ${b} ${c}`,
    `${a}.${b}.${c}`,
    `${a} ${b} ${c}`,
    `1-${a}-${b}-${c}`,
    `+1-${a}-${b}-${c}`,
    `+1 (${a}) ${b}-${c}`,
    `+1 ${a} ${b} ${c}`,
    `1 ${a} ${b} ${c}`,
  ];
  const trimmed = String(raw ?? '').trim();
  if (trimmed) variants.push(trimmed);
  return [...new Set(variants.filter(Boolean))];
}

function postgrestQuote(value) {
  return `"${String(value).replace(/"/g, '""')}"`;
}

function phoneSmsThreadOrFilter(raw) {
  const variants = phoneEqVariants(raw);
  if (!variants.length) return null;
  return variants
    .flatMap((v) => {
      const q = postgrestQuote(v);
      return [`from_number.eq.${q}`, `to_number.eq.${q}`];
    })
    .join(',');
}

function phoneLeadOrFilter(raw) {
  const variants = phoneEqVariants(raw);
  if (!variants.length) return null;
  return variants
    .flatMap((v) => {
      const q = postgrestQuote(v);
      return [`phone.eq.${q}`, `phone_secondary.eq.${q}`];
    })
    .join(',');
}

function normalizeImportEmail(email) {
  const e = String(email ?? '').trim().toLowerCase();
  return e || null;
}

const TOP25_CATEGORY_NOTE_LABELS = {
  trust_respect: 'Trust & Respect',
  center_of_influence: 'Center of Influence',
  last_10_contacted: 'Last 10 Contacted',
  entrepreneurship: 'Entrepreneurship',
  money_conversations: 'Money Conversations',
};

function splitPersonName(fullName) {
  const trimmed = String(fullName || '').trim();
  if (!trimmed) return { first_name: '', last_name: '' };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
}

function formatTop25ConversionNotes(contact) {
  const category =
    TOP25_CATEGORY_NOTE_LABELS[contact?.category] || contact?.category || 'Unknown';
  const machoBits = [];
  if (contact?.macho_married) machoBits.push('Married');
  if (contact?.macho_age_25_55) machoBits.push('Age 25-55');
  if (contact?.macho_children) machoBits.push('Children');
  if (contact?.macho_homeowner) machoBits.push('Homeowner');
  const parts = [`Top 25 category: ${category}.`];
  if (machoBits.length) parts.push(`MACHO: ${machoBits.join(', ')}.`);
  const occupation = String(contact?.macho_occupation || '').trim();
  if (occupation) parts.push(`Occupation: ${occupation}.`);
  return parts.join(' ');
}

/**
 * Tiebreaker when email/phone match multiple agency leads:
 * 1. Inviter match — if agent-owned invite (inviterUserId set) and exactly one
 *    candidate has created_by = inviterUserId, use it.
 * 2. Otherwise last_called_at desc (nulls last).
 */
function pickLeadByTiebreaker(rows, inviterUserId) {
  if (!rows?.length) return null;

  if (inviterUserId) {
    const owned = rows.filter((r) => r.created_by === inviterUserId);
    if (owned.length === 1) return owned[0];
  }

  return [...rows].sort((a, b) => {
    const aNull = a.last_called_at == null;
    const bNull = b.last_called_at == null;
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    return (Date.parse(b.last_called_at) || 0) - (Date.parse(a.last_called_at) || 0);
  })[0];
}

/**
 * Agency-scoped lead match for Mentors door / public registration parity:
 * email first, then phone; inviter-owned (exact one) else last_called_at. Does not merge others.
 * inviterUserId: agent-owned invitation user_id only (null for student invites or no invite).
 */
async function findMatchingLeadForAgency(agencyId, email, phone, inviterUserId = null) {
  if (!agencyId || (!email && !phone)) return null;
  // leads.updated_at does not exist — do not select it.
  const selectCols = 'id, email, phone, phone_secondary, created_by, last_called_at, created_at';

  if (email) {
    const emailRows = await paginateSelect((from, to) =>
      supabase
        .from('leads')
        .select(selectCols)
        .eq('agency_id', agencyId)
        .not('email', 'is', null)
        .ilike('email', email)
        .range(from, to),
    );
    const emailMatches = (emailRows || []).filter(
      (row) => normalizeImportEmail(row.email) === email,
    );
    const bestEmail = pickLeadByTiebreaker(emailMatches, inviterUserId);
    if (bestEmail) return bestEmail.id;
  }

  if (phone) {
    const orFilter = phoneLeadOrFilter(phone);
    let phoneMatches = [];

    if (orFilter) {
      const { data: exactRows, error: exactErr } = await supabase
        .from('leads')
        .select(selectCols)
        .eq('agency_id', agencyId)
        .or(orFilter)
        .limit(50);
      if (exactErr) throw exactErr;
      phoneMatches = (exactRows || []).filter(
        (row) => phonesMatch(row.phone, phone) || phonesMatch(row.phone_secondary, phone),
      );
    }

    if (!phoneMatches.length) {
      const phoneRows = await paginateSelect((from, to) =>
        supabase
          .from('leads')
          .select(selectCols)
          .eq('agency_id', agencyId)
          .or('phone.not.is.null,phone_secondary.not.is.null')
          .range(from, to),
      );
      phoneMatches = (phoneRows || []).filter(
        (row) => phonesMatch(row.phone, phone) || phonesMatch(row.phone_secondary, phone),
      );
    }

    const bestPhone = pickLeadByTiebreaker(phoneMatches, inviterUserId);
    if (bestPhone) return bestPhone.id;
  }

  return null;
}

const PM_DEFAULT_TZ = 'America/New_York';

function formatInTimezone(iso, timeZone) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const tz = timeZone || PM_DEFAULT_TZ;
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: tz,
      timeZoneName: 'short',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: PM_DEFAULT_TZ,
      timeZoneName: 'short',
    }).format(date);
  }
}

function todayDateInTimezone(timeZone) {
  const tz = timeZone || PM_DEFAULT_TZ;
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: PM_DEFAULT_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }
}

/** Prefer selected session; else nearest upcoming, else most recent past. */
async function resolvePmSessionForNote(eventId, sessionId) {
  if (sessionId) {
    const { data, error } = await supabase
      .from('pm_event_sessions')
      .select('id, title, starts_at, timezone')
      .eq('id', sessionId)
      .eq('event_id', eventId)
      .maybeSingle();
    if (error) throw error;
    if (data) return data;
  }

  const { data: sessions, error } = await supabase
    .from('pm_event_sessions')
    .select('id, title, starts_at, timezone')
    .eq('event_id', eventId)
    .order('starts_at', { ascending: true });
  if (error) throw error;
  if (!sessions?.length) return null;

  const now = Date.now();
  const upcoming = sessions.filter((s) => new Date(s.starts_at).getTime() >= now);
  if (upcoming.length) return upcoming[0];
  return sessions[sessions.length - 1];
}

function isEmptyLeadFieldValue(key, val) {
  if (val === null || val === undefined) return true;
  if (typeof val === 'string' && val.trim() === '') return true;
  if (key === 'metadata' && typeof val === 'object' && Object.keys(val || {}).length === 0) return true;
  return false;
}

/** Shared column mapping for CSV bulk insert and merge updates. */
function buildBulkLeadDataCols(r) {
  return {
    first_name: r.first_name?.trim(),
    last_name: r.last_name?.trim(),
    phone: r.phone?.trim() || null,
    phone_secondary: r.phone_secondary?.trim() || null,
    email: r.email?.trim() || null,
    lead_type: r.lead_type,
    channel: r.channel ?? null,
    lead_source: r.lead_source?.trim() || 'manual',
    lead_vendor: r.lead_vendor?.trim() || null,
    lead_vendor_id: r.lead_vendor_id?.trim() || null,
    status: r.status || 'new',
    pipeline_stage: r.pipeline_stage?.trim() || null,
    address: r.address?.trim() || null,
    city: r.city?.trim() || null,
    state: r.state?.trim() || null,
    zip: r.zip?.trim() || null,
    area_code: r.area_code?.trim() || null,
    time_zone: r.time_zone?.trim() || null,
    dob: r.dob?.trim() || null,
    gender: r.gender?.trim() || null,
    marital_status: r.marital_status?.trim() || null,
    smoker: r.smoker ?? null,
    height_feet: r.height_feet ?? null,
    height_inches: r.height_inches ?? null,
    weight: r.weight ?? null,
    coverage_amount_requested: r.coverage_amount_requested?.trim() || null,
    policy_type_requested: r.policy_type_requested?.trim() || null,
    term_requested: r.term_requested?.trim() || null,
    notes: r.notes?.trim() || null,
    metadata: r.metadata && Object.keys(r.metadata).length > 0 ? r.metadata : {},
    import_batch_id: r.import_batch_id ?? null,
    import_label: r.import_label?.trim() || null,
    imported_at: r.imported_at ?? null,
    capturing_agent_name: r.capturing_agent_name?.trim() || null,
    capturing_agent_email: r.capturing_agent_email?.trim() || null,
    event_title: r.event_title?.trim() || null,
    payment_status: r.payment_status?.trim() || null,
    has_pm_account: r.has_pm_account ?? null,
    pm_form_responses: r.pm_form_responses ?? null,
    pm_quiz_responses: r.pm_quiz_responses ?? null,
    pm_survey_responses: r.pm_survey_responses ?? null,
    pm_activities: r.pm_activities ?? null,
  };
}

const CSV_IMPORT_FIELD_LABELS = {
  first_name: 'First name',
  last_name: 'Last name',
  phone: 'Phone',
  phone_secondary: 'Phone secondary',
  email: 'Email',
  lead_type: 'Lead type',
  channel: 'Channel',
  lead_source: 'Lead source',
  lead_vendor: 'Lead vendor',
  lead_vendor_id: 'Lead vendor ID',
  status: 'Status',
  pipeline_stage: 'Pipeline stage',
  address: 'Address',
  city: 'City',
  state: 'State',
  zip: 'Zip',
  area_code: 'Area code',
  time_zone: 'Time zone',
  dob: 'Date of birth',
  gender: 'Gender',
  marital_status: 'Marital status',
  smoker: 'Smoker',
  height_feet: 'Height (ft)',
  height_inches: 'Height (in)',
  weight: 'Weight',
  coverage_amount_requested: 'Coverage amount',
  policy_type_requested: 'Policy type',
  term_requested: 'Term',
  notes: 'Notes',
  capturing_agent_name: 'Capturing agent',
  capturing_agent_email: 'Capturing agent email',
  event_title: 'Event title',
  payment_status: 'Payment status',
  import_label: 'Import label',
};

function buildBulkLeadOverwriteUpdate(r) {
  const cols = buildBulkLeadDataCols(r);
  const data = {};
  Object.entries(cols).forEach(([k, v]) => {
    if (v !== null && v !== undefined) data[k] = v;
  });
  if (!r.status) delete data.status;
  if (data.metadata && Object.keys(data.metadata).length === 0) delete data.metadata;
  if (r.exclusivity_status) data.exclusivity_status = r.exclusivity_status;
  return data;
}

function buildBulkLeadFillEmptyUpdate(existing, csvRow) {
  const cols = buildBulkLeadDataCols(csvRow);
  const data = {};
  Object.entries(cols).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    if (k === 'metadata' && typeof v === 'object' && Object.keys(v).length === 0) return;
    if (!isEmptyLeadFieldValue(k, existing[k])) return;
    data[k] = v;
  });
  if (!csvRow.status) delete data.status;
  if (data.metadata && Object.keys(data.metadata).length === 0) delete data.metadata;
  if (csvRow.exclusivity_status && isEmptyLeadFieldValue('exclusivity_status', existing.exclusivity_status)) {
    data.exclusivity_status = csvRow.exclusivity_status;
  }
  return data;
}

function formatCsvImportFieldValue(key, val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val).trim();
}

function buildCsvImportMergeNote(csvRow, existingLead) {
  const cols = buildBulkLeadDataCols(csvRow);
  const skippedParts = [];
  Object.entries(cols).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    if (k === 'metadata' && typeof v === 'object' && Object.keys(v).length === 0) return;
    if (!isEmptyLeadFieldValue(k, existingLead[k])) {
      const label = CSV_IMPORT_FIELD_LABELS[k] || k;
      skippedParts.push(`${label}: ${formatCsvImportFieldValue(k, v)}`);
    }
  });
  const dateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const leadSource = csvRow.lead_source?.trim() || cols.lead_source || '—';
  const channel = csvRow.channel ?? cols.channel ?? '—';
  const eventTitle = csvRow.event_title?.trim();
  let note = `CSV Import [${dateStr}]: Additional registration data.`;
  if (skippedParts.length) note += ` ${skippedParts.join(', ')}.`;
  note += ` Lead source: ${leadSource}. Channel: ${channel}.`;
  if (eventTitle) note += ` Event: ${eventTitle}.`;
  return note;
}

async function executeBulkLeadInserts(rows, rowAgentIds, profile, agentAssignments) {
  const CHUNK = 200;
  let inserted = 0;
  let failed = 0;
  const assignmentRows = [];
  for (let i = 0; i < rows.length; i += CHUNK) {
    const chunk = rows.slice(i, i + CHUNK);
    const { data: insertedRows, error } = await supabase
      .from('leads')
      .insert(chunk)
      .select('id');
    if (error) {
      failed += chunk.length;
    } else {
      inserted += chunk.length;
      (insertedRows || []).forEach((row, j) => {
        const agentId = rowAgentIds[i + j];
        if (row?.id && agentId) {
          assignmentRows.push({
            lead_id: row.id,
            user_id: agentId,
            agency_id: profile.agency_id,
            assignment_role: 'setter',
          });
        }
      });
    }
  }
  if (Array.isArray(agentAssignments)) {
    for (const a of agentAssignments) {
      if (a?.lead_id && a?.user_id) {
        assignmentRows.push({
          lead_id: a.lead_id,
          user_id: a.user_id,
          agency_id: a.agency_id || profile.agency_id,
          assignment_role: 'setter',
        });
      }
    }
  }
  if (assignmentRows.length) {
    for (let i = 0; i < assignmentRows.length; i += CHUNK) {
      const chunk = assignmentRows.slice(i, i + CHUNK);
      const { error: assignErr } = await supabase.from('lead_assignments').insert(chunk);
      if (assignErr) {
        console.error('[api.leads.bulkCreate] failed to insert lead_assignments', assignErr);
      }
    }
  }
  return { inserted, failed, assigned: assignmentRows.length };
}

export const bootcampComments = {

  // Fetch all non-deleted comments for an agency, with user info and reactions
  // Returns top-level comments with their replies nested under comment.replies[]
  async getAll(agencyId, videoId = null) {
    let query = supabase
      .from('bootcamp_comments')
      .select(`
        *,
        user:users(id, full_name, role),
        reactions:bootcamp_reactions(id, user_id, emoji)
      `)
      .eq('agency_id', agencyId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true });

    if (videoId) query = query.eq('video_id', videoId);

    const { data, error } = await query;
    if (error) throw error;

    // Nest replies under their parent
    const topLevel = data.filter(c => !c.parent_id);
    const replies = data.filter(c => c.parent_id);
    return topLevel.map(c => ({
      ...c,
      replies: replies.filter(r => r.parent_id === c.id)
    }));
  },

  // Post a new top-level comment or reply
  async create(agencyId, userId, videoId, body, mentionedUserIds = [], parentId = null) {
    const { data, error } = await supabase
      .from('bootcamp_comments')
      .insert({
        agency_id: agencyId,
        user_id: userId,
        video_id: videoId,
        body,
        mentioned_user_ids: mentionedUserIds,
        parent_id: parentId || null
      })
      .select(`*, user:users(id, full_name, role), reactions:bootcamp_reactions(id, user_id, emoji)`)
      .single();
    if (error) throw error;
    return { ...data, replies: [] };
  },

  // Soft delete a comment (own comment or admin)
  async delete(commentId) {
    const { error } = await supabase
      .from('bootcamp_comments')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', commentId);
    if (error) throw error;
  },

  // Toggle a reaction — if user already reacted with this emoji, remove it; otherwise add it
  async toggleReaction(agencyId, commentId, userId, emoji) {
    // Check if reaction exists
    const { data: existing } = await supabase
      .from('bootcamp_reactions')
      .select('id')
      .eq('comment_id', commentId)
      .eq('user_id', userId)
      .eq('emoji', emoji)
      .maybeSingle();

    if (existing) {
      const { error } = await supabase
        .from('bootcamp_reactions')
        .delete()
        .eq('id', existing.id);
      if (error) throw error;
      return { action: 'removed' };
    } else {
      const { error } = await supabase
        .from('bootcamp_reactions')
        .insert({ agency_id: agencyId, comment_id: commentId, user_id: userId, emoji });
      if (error) throw error;
      return { action: 'added' };
    }
  }
};

// ─── APPLICATIONS ────────────────────────────────────────────────────────────
export const applications = {
  // Get all applications for the agency (respects RLS)
  getAll: async (filters = {}) => {
    if (useMock) return { data: [], error: null };
    let query = supabase
      .from('applications')
      .select(`
        *,
        agent:agent_id(id, full_name, email),
        split_agent:split_agent_id(id, full_name, email),
        lead:lead_id(id, first_name, last_name, phone, email),
        latest_comment:application_comments(body, author_name, created_at),
        requirements:application_requirements(id, title, completed),
        status_history:application_status_history(id, previous_status, new_status, changed_by_name, changed_at)
      `)
      .eq('archived', false)
      .order('created_at', { referencedTable: 'application_comments', ascending: false })
      .limit(1, { referencedTable: 'application_comments' })
      .order('created_at', { ascending: false });

    if (filters.status) query = query.eq('status', filters.status);
    if (filters.agent_id) query = query.eq('agent_id', filters.agent_id);
    if (filters.carrier) query = query.eq('carrier', filters.carrier);
    if (filters.search) query = query.ilike('client_name', `%${filters.search}%`);

    const { data, error } = await query;
    if (error) throw error;

    // Attach only the latest comment per application (embed is already
    // newest-first / limit 1; keep the same object-or-null shape).
    const results = (data || []).map((app) => {
      const raw = app.latest_comment;
      const latest = Array.isArray(raw)
        ? raw.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || null
        : raw || null;
      return { ...app, latest_comment: latest };
    });

    if (filters.sort_by) {
      const descending = String(filters.sort_by).endsWith('_desc');
      const field = descending
        ? String(filters.sort_by).slice(0, -5)
        : String(filters.sort_by).replace(/_asc$/, '');
      results.sort((a, b) => {
        let aVal = a[field];
        let bVal = b[field];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
          return descending ? -cmp : cmp;
        }
        if (aVal < bVal) return descending ? 1 : -1;
        if (aVal > bVal) return descending ? -1 : 1;
        return 0;
      });
    }

    return results;
  },

  getById: async (id) => {
    if (useMock) return null;
    const { data, error } = await supabase
      .from('applications')
      .select(`
        *,
        agent:agent_id(id, full_name, email),
        split_agent:split_agent_id(id, full_name, email),
        lead:lead_id(id, first_name, last_name, phone, email),
        comments:application_comments(*, created_by(id, full_name)),
        requirements:application_requirements(*),
        documents:application_documents(*),
        tasks:application_tasks(*, assigned_to(id, full_name)),
        chargebacks:application_chargebacks(*),
        status_history:application_status_history(*)
      `)
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },

  create: async (applicationData) => {
    if (useMock) return null;
    const { data, error } = await supabase
      .from('applications')
      .insert(applicationData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  update: async (id, updates) => {
    if (useMock) return null;
    const { data, error } = await supabase
      .from('applications')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  updateStatus: async (id, newStatus, notes = null) => {
    if (useMock) return null;
    const updates = { status: newStatus };
    if (newStatus === 'submitted') updates.submitted_at = new Date().toISOString();
    if (newStatus === 'approved') updates.approved_at = new Date().toISOString();
    if (newStatus === 'advanced_paid') updates.issued_at = new Date().toISOString();
    if (newStatus === 'cancelled') updates.cancelled_at = new Date().toISOString();
    const { data, error } = await supabase
      .from('applications')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  archive: async (id) => {
    if (useMock) return null;
    const { data, error } = await supabase
      .from('applications')
      .update({ archived: true, archived_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  delete: async (id) => {
    if (useMock) return null;
    const { error } = await supabase
      .from('applications')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return { success: true };
  },

  getReportData: async (filters = {}) => {
    if (useMock) return [];
    let query = supabase
      .from('applications')
      .select(`
        id,
        client_name,
        carrier,
        product,
        status,
        target_points,
        premium,
        agent_id,
        agent_name,
        agent:agent_id(id, full_name),
        submitted_at,
        approved_at,
        issued_at,
        created_at,
        updated_at,
        is_split,
        split_percent,
        policy_number,
        lead_id
      `)
      .eq('archived', false);

    if (filters.status) query = query.eq('status', filters.status);
    if (filters.carrier) query = query.eq('carrier', filters.carrier);
    if (filters.agent_id) query = query.eq('agent_id', filters.agent_id);
    if (filters.date_from) query = query.gte('submitted_at', filters.date_from);
    if (filters.date_to) query = query.lte('submitted_at', filters.date_to);

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  searchLeads: async (query, agencyId) => {
    if (!query || query.length < 2) return [];
    const { data, error } = await supabase
      .from('leads')
      .select('id, first_name, last_name, phone, email, lead_type')
      .eq('agency_id', agencyId)
      .eq('archived_at', null)
      .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,phone.ilike.%${query}%,email.ilike.%${query}%`)
      .limit(8);
    if (error) throw error;
    return data || [];
  },

  linkLead: async (applicationId, leadId) => {
    const { data, error } = await supabase
      .from('applications')
      .update({ lead_id: leadId })
      .eq('id', applicationId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  unlinkLead: async (applicationId) => {
    const { data, error } = await supabase
      .from('applications')
      .update({ lead_id: null })
      .eq('id', applicationId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // ── Comments ──
  comments: {
    getByApplication: async (applicationId) => {
      const { data, error } = await supabase
        .from('application_comments')
        .select('*, created_by(id, full_name)')
        .eq('application_id', applicationId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    create: async (comment) => {
      const { data, error } = await supabase
        .from('application_comments')
        .insert(comment)
        .select()
        .single();
      if (error) throw error;

      // Fire mention notification if there are mentioned users
      if (comment.mentioned_user_ids?.length > 0) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-application-mention`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify({
                comment_id: data.id,
                application_id: comment.application_id,
                author_name: comment.author_name,
                comment_body: comment.body,
                mentioned_user_ids: comment.mentioned_user_ids,
              }),
            }
          );
        } catch (e) {
          console.warn('Mention notification failed:', e);
        }
      }

      return data;
    },
    getUnreadMentionCount: async () => {
      try {
        const userId = await getSessionUserId();
        if (!userId) return 0;

        // Get all comments where user is mentioned
        const { data: mentioned, error: e1 } = await supabase
          .from('application_comments')
          .select('id')
          .contains('mentioned_user_ids', [userId]);

        if (e1 || !mentioned?.length) return 0;

        const mentionedIds = mentioned.map(c => c.id);

        // Get already-read comment ids
        const { data: reads, error: e2 } = await supabase
          .from('application_mention_reads')
          .select('comment_id')
          .eq('user_id', userId)
          .in('comment_id', mentionedIds);

        if (e2) return 0;

        const readIds = new Set(reads?.map(r => r.comment_id) || []);
        const unreadCount = mentionedIds.filter(
          id => !readIds.has(id)
        ).length;

        return unreadCount;
      } catch {
        return 0;
      }
    },
    markMentionsRead: async (applicationId) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get all unread mentions in this application
      const { data: comments } = await supabase
        .from('application_comments')
        .select('id')
        .eq('application_id', applicationId)
        .contains('mentioned_user_ids', [user.id]);

      if (!comments?.length) return;

      // Get already-read comment ids
      const { data: reads } = await supabase
        .from('application_mention_reads')
        .select('comment_id')
        .eq('user_id', user.id)
        .in('comment_id', comments.map(c => c.id));

      const readIds = new Set(reads?.map(r => r.comment_id) || []);
      const unread = comments.filter(c => !readIds.has(c.id));

      if (!unread.length) return;

      await supabase
        .from('application_mention_reads')
        .insert(
          unread.map(c => ({
            user_id: user.id,
            comment_id: c.id,
            application_id: applicationId,
          }))
        );
    },
    delete: async (id) => {
      const { error } = await supabase
        .from('application_comments')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  // ── Requirements ──
  requirements: {
    getByApplication: async (applicationId) => {
      const { data, error } = await supabase
        .from('application_requirements')
        .select('*')
        .eq('application_id', applicationId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    create: async (requirement) => {
      const { data, error } = await supabase
        .from('application_requirements')
        .insert(requirement)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    update: async (id, updates) => {
      const { data, error } = await supabase
        .from('application_requirements')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    delete: async (id) => {
      const { error } = await supabase
        .from('application_requirements')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  // ── Documents ──
  documents: {
    getByApplication: async (applicationId) => {
      const { data, error } = await supabase
        .from('application_documents')
        .select('*')
        .eq('application_id', applicationId)
        .order('uploaded_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    create: async (doc) => {
      const { data, error } = await supabase
        .from('application_documents')
        .insert(doc)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    delete: async (id) => {
      const { error } = await supabase
        .from('application_documents')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  // ── Tasks ──
  tasks: {
    getByApplication: async (applicationId) => {
      const { data, error } = await supabase
        .from('application_tasks')
        .select('*, assigned_to(id, full_name)')
        .eq('application_id', applicationId)
        .order('due_date', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    create: async (task) => {
      const { data, error } = await supabase
        .from('application_tasks')
        .insert(task)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    update: async (id, updates) => {
      const { data, error } = await supabase
        .from('application_tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    delete: async (id) => {
      const { error } = await supabase
        .from('application_tasks')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  // ── Chargebacks (admin only) ──
  chargebacks: {
    getByApplication: async (applicationId) => {
      const { data, error } = await supabase
        .from('application_chargebacks')
        .select('*, recorded_by(id, full_name)')
        .eq('application_id', applicationId)
        .order('occurred_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    create: async (chargeback) => {
      const { data, error } = await supabase
        .from('application_chargebacks')
        .insert(chargeback)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    update: async (id, updates) => {
      const { data, error } = await supabase
        .from('application_chargebacks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
  },
};

// ── Financial Needs Analysis (FNA) ──────────────────────────────────────────
const FNA_NESTED_SELECT = `
  *,
  lead:lead_id(id, first_name, last_name),
  creator:created_by(id, full_name, email),
  income_sources:fna_income_sources(*),
  expenses:fna_expenses(*),
  assets:fna_assets(*),
  liabilities:fna_liabilities(*),
  insurance_policies:fna_insurance_policies(*),
  financial_goals:fna_financial_goals(*),
  estate_checklist:fna_estate_checklist(*),
  dependents:fna_dependents(*)
`;

const FNA_ESTATE_ITEM_KEYS = [
  'will',
  'powerOfAttorney',
  'healthcareDirective',
  'trust',
  'beneficiaryDesignations',
  'guardianship',
  'emergencyFund',
  'taxPlanning',
];

const FNA_SEEDED_EXPENSES = [
  { category: 'Housing', description: 'Mortgage or Rent' },
  { category: 'Housing', description: 'Home Insurance' },
  { category: 'Housing', description: 'Electricity' },
  { category: 'Housing', description: 'Water Gas Sewer' },
  { category: 'Transportation', description: 'Auto Loan or Lease' },
  { category: 'Transportation', description: 'Car Insurance' },
  { category: 'Transportation', description: 'Gas Toll Parking' },
  { category: 'Living', description: 'Groceries' },
  { category: 'Living', description: 'Household' },
  { category: 'Living', description: 'Dining Out' },
  { category: 'Living', description: 'Entertainment' },
  { category: 'Healthcare', description: 'Health Insurance' },
  { category: 'Healthcare', description: 'Prescriptions' },
  { category: 'Family', description: 'Childcare' },
  { category: 'Family', description: 'Educational' },
  { category: 'Family', description: 'Alimony and Child Support' },
  { category: 'Debt', description: 'Credit Card' },
  { category: 'Debt', description: 'Personal Loans' },
  { category: 'Communication', description: 'Cable and Internet' },
  { category: 'Communication', description: 'Mobile' },
  { category: 'Communication', description: 'Subscriptions' },
  { category: 'Personal Care', description: 'Gym' },
  { category: 'Personal Care', description: 'Hair Salon' },
  { category: 'Personal Care', description: 'Personal Care' },
  { category: 'Discretionary', description: 'Travel and Vacation' },
  { category: 'Discretionary', description: 'Tithe and Charity' },
  { category: 'Discretionary', description: 'Others' },
];

async function resolveFnaAgencyId(financialAnalysisId, data = {}) {
  if (data.agency_id) return data.agency_id;
  const { data: parent, error } = await supabase
    .from('financial_analyses')
    .select('agency_id')
    .eq('id', financialAnalysisId)
    .single();
  if (error) throw error;
  return parent.agency_id;
}

function makeFnaChildModule(tableName) {
  return {
    getByAnalysis: async (financialAnalysisId) => {
      if (useMock) return [];
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('financial_analysis_id', financialAnalysisId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    create: async (financialAnalysisId, data = {}) => {
      if (useMock) return null;
      const agency_id = await resolveFnaAgencyId(financialAnalysisId, data);
      const { data: row, error } = await supabase
        .from(tableName)
        .insert({
          ...data,
          financial_analysis_id: financialAnalysisId,
          agency_id,
        })
        .select()
        .single();
      if (error) throw error;
      return row;
    },
    update: async (id, data = {}) => {
      if (useMock) return null;
      const { data: row, error } = await supabase
        .from(tableName)
        .update(data)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return row;
    },
    delete: async (id) => {
      if (useMock) return { success: true };
      const { error } = await supabase.from(tableName).delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  };
}

export const financialAnalysis = {
  getAll: async (filters = {}) => {
    if (useMock) return [];
    let query = supabase
      .from('financial_analyses')
      .select(`
        *,
        lead:lead_id(id, first_name, last_name),
        creator:created_by(id, full_name, email),
        assets:fna_assets(id, amount, category),
        liabilities:fna_liabilities(id, amount),
        estate_checklist:fna_estate_checklist(id, item_key, needs_attention)
      `)
      .order('updated_at', { ascending: false });

    if (filters.created_by) query = query.eq('created_by', filters.created_by);
    if (filters.agent_id) query = query.eq('created_by', filters.agent_id);

    const { data, error } = await query;
    if (error) throw error;

    let results = data || [];
    if (filters.search) {
      const q = String(filters.search).trim().toLowerCase();
      if (q) {
        results = results.filter((row) => {
          const name = [row.lead?.first_name, row.lead?.last_name]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return name.includes(q);
        });
      }
    }
    return results;
  },

  getByLeadId: async (leadId) => {
    if (useMock) return null;
    const { data, error } = await supabase
      .from('financial_analyses')
      .select(FNA_NESTED_SELECT)
      .eq('lead_id', leadId)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  getById: async (id) => {
    if (useMock) return null;
    const { data, error } = await supabase
      .from('financial_analyses')
      .select(FNA_NESTED_SELECT)
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  create: async ({ leadId, agencyId, userId }) => {
    if (useMock) return null;
    if (!leadId || !agencyId) throw new Error('leadId and agencyId are required');

    const { data: parent, error: parentError } = await supabase
      .from('financial_analyses')
      .insert({
        lead_id: leadId,
        agency_id: agencyId,
        created_by: userId || null,
      })
      .select()
      .single();
    if (parentError) throw parentError;

    const estateRows = FNA_ESTATE_ITEM_KEYS.map((item_key) => ({
      financial_analysis_id: parent.id,
      agency_id: agencyId,
      item_key,
      needs_attention: false,
    }));
    const { error: estateError } = await supabase
      .from('fna_estate_checklist')
      .insert(estateRows);
    if (estateError) throw estateError;

    const expenseRows = FNA_SEEDED_EXPENSES.map((row) => ({
      financial_analysis_id: parent.id,
      agency_id: agencyId,
      category: row.category,
      description: row.description,
      amount: 0,
      frequency: 'monthly',
    }));
    const { error: expenseError } = await supabase
      .from('fna_expenses')
      .insert(expenseRows);
    if (expenseError) throw expenseError;

    return financialAnalysis.getByLeadId(leadId);
  },

  update: async (id, updates = {}) => {
    if (useMock) return null;
    const payload = { updated_at: new Date().toISOString() };
    if (updates.years_to_replace !== undefined) payload.years_to_replace = updates.years_to_replace;
    if (updates.final_expenses_override !== undefined) {
      payload.final_expenses_override = updates.final_expenses_override;
    }
    if (updates.education_fund !== undefined) payload.education_fund = updates.education_fund;
    if (updates.legacy_wishes !== undefined) payload.legacy_wishes = updates.legacy_wishes;
    if (updates.date_of_birth !== undefined) payload.date_of_birth = updates.date_of_birth;
    if (updates.employment_status !== undefined) payload.employment_status = updates.employment_status;
    if (updates.occupation !== undefined) payload.occupation = updates.occupation;
    if (updates.marital_status !== undefined) payload.marital_status = updates.marital_status;
    if (updates.spouse_first_name !== undefined) payload.spouse_first_name = updates.spouse_first_name;
    if (updates.spouse_last_name !== undefined) payload.spouse_last_name = updates.spouse_last_name;
    if (updates.spouse_email !== undefined) payload.spouse_email = updates.spouse_email;
    if (updates.spouse_phone !== undefined) payload.spouse_phone = updates.spouse_phone;
    if (updates.spouse_date_of_birth !== undefined) {
      payload.spouse_date_of_birth = updates.spouse_date_of_birth;
    }
    if (updates.spouse_lead_id !== undefined) payload.spouse_lead_id = updates.spouse_lead_id;

    const { data, error } = await supabase
      .from('financial_analyses')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  delete: async (id) => {
    if (useMock) return { success: true };
    const { error } = await supabase.from('financial_analyses').delete().eq('id', id);
    if (error) throw error;
    return { success: true };
  },

  incomeSources: makeFnaChildModule('fna_income_sources'),
  expenses: makeFnaChildModule('fna_expenses'),
  assets: makeFnaChildModule('fna_assets'),
  liabilities: makeFnaChildModule('fna_liabilities'),
  insurancePolicies: makeFnaChildModule('fna_insurance_policies'),
  financialGoals: makeFnaChildModule('fna_financial_goals'),
  dependents: makeFnaChildModule('fna_dependents'),

  spouseLead: {
    link: async (financialAnalysisId, leadId) => {
      if (useMock) return null;
      return financialAnalysis.update(financialAnalysisId, { spouse_lead_id: leadId });
    },
    unlink: async (financialAnalysisId) => {
      if (useMock) return null;
      return financialAnalysis.update(financialAnalysisId, { spouse_lead_id: null });
    },
  },

  estateChecklist: {
    getByAnalysis: async (financialAnalysisId) => {
      if (useMock) return [];
      const { data, error } = await supabase
        .from('fna_estate_checklist')
        .select('*')
        .eq('financial_analysis_id', financialAnalysisId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    updateItem: async (id, { needs_attention, last_updated, notes } = {}) => {
      if (useMock) return null;
      const payload = {};
      if (needs_attention !== undefined) payload.needs_attention = needs_attention;
      if (last_updated !== undefined) payload.last_updated = last_updated;
      if (notes !== undefined) payload.notes = notes;
      const { data, error } = await supabase
        .from('fna_estate_checklist')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
  },
};

// Plaintext columns only — never select *_encrypted over the Data API.
const PREUNDERWRITING_LOG_SAFE_COLUMNS = [
  'id',
  'agency_id',
  'agent_id',
  'split_agent_id',
  'status',
  'created_at',
  'submitted_at',
  'last_saved_at',
  'reviewed_at',
  'reviewed_by',
  'consent_communication',
  'consent_truthful_information',
  'full_name',
  'birth_state_country',
  'birth_date',
  'gender',
  'address',
  'years_at_address',
  'cell_phone',
  'email',
  'citizenship',
  'id_type',
  'id_number',
  'id_state',
  'id_expiration',
  'employer_name',
  'employer_address',
  'occupation',
  'duties',
  'employer_phone',
  'years_at_employer',
  'annual_income',
  'household_income',
  'monthly_expenses',
  'net_worth',
  'insurance_type',
  'monthly_premium',
  'insurance_option',
  'indexed_strategy_1',
  'indexed_strategy_2',
  'existing_policies',
  'bank_name',
  'rate_class',
  'health_provider',
  'last_medical_visit',
  'height',
  'weight',
  'weight_change_10lbs',
  'is_smoker',
  'other_specialists_5yr',
  'other_specialists_detail',
  'international_travel',
  'dangerous_activities',
  'unpaid_tickets',
  'exercises_regularly',
  'medications_5yr',
  'father_details',
  'mother_details',
  'siblings_details',
  'health_conditions',
  'has_spouse',
  'spouse_application_type',
  'spouse_full_name',
  'spouse_birth_date',
  'spouse_gender',
  'spouse_citizenship',
  'spouse_id_type',
  'spouse_id_number',
  'spouse_id_state',
  'spouse_id_expiration',
  'spouse_coverage_amount',
  'spouse_coverage_type',
  'spouse_health_provider',
  'spouse_last_medical_visit',
  'spouse_height',
  'spouse_weight',
  'spouse_weight_change_10lbs',
  'spouse_is_smoker',
  'spouse_other_specialists_5yr',
  'spouse_other_specialists_detail',
  'spouse_international_travel',
  'spouse_dangerous_activities',
  'spouse_unpaid_tickets',
  'spouse_exercises_regularly',
  'spouse_medications_5yr',
  'spouse_health_conditions',
  'has_children',
  'agent_name_code',
  'split_agent_name_code',
  'agent_carrier',
  'agent_indexed_strategies',
  'agent_other_comments',
  'lead_id',
].join(', ');

const PREUNDERWRITING_LOG_EMBEDS = `
  agent:agent_id(id, full_name, email),
  split_agent:split_agent_id(id, full_name, email),
  lead:lead_id(id, first_name, last_name)
`;

const PREUNDERWRITING_LOG_SELECT = `${PREUNDERWRITING_LOG_SAFE_COLUMNS},${PREUNDERWRITING_LOG_EMBEDS}`;

const PREUNDERWRITING_REVEAL_FIELDS = new Set([
  'ssn',
  'beneficiaries',
  'routing_number',
  'account_number',
  'spouse_ssn',
  'children',
]);

const PREUNDERWRITING_LOG_STATUSES = new Set(['submitted', 'reviewed', 'archived']);

// ─── PRE-UNDERWRITING APPLICATION LOGS ───────────────────────────────────────
export const preUnderwritingLogs = {
  getAll: async ({ status, agentId, search } = {}) => {
    if (useMock) return [];
    let query = supabase
      .from('preunderwriting_application_logs')
      .select(PREUNDERWRITING_LOG_SELECT)
      .order('created_at', { ascending: false });

    if (status) query = query.eq('status', status);
    if (agentId) query = query.eq('agent_id', agentId);
    if (search) query = query.ilike('full_name', `%${search}%`);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  getById: async (id) => {
    if (useMock) return null;
    const { data, error } = await supabase
      .from('preunderwriting_application_logs')
      .select(PREUNDERWRITING_LOG_SELECT)
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  updateStatus: async (id, status, notes = null) => {
    if (useMock) return null;
    if (!PREUNDERWRITING_LOG_STATUSES.has(status)) {
      throw new Error("status must be one of: 'submitted', 'reviewed', 'archived'");
    }
    const userId = await getSessionUserId();
    if (!userId) throw new Error('Not authenticated');

    const updates = {
      status,
      reviewed_at: new Date().toISOString(),
      reviewed_by: userId,
    };

    const { data, error } = await supabase
      .from('preunderwriting_application_logs')
      .update(updates)
      .eq('id', id)
      .select(PREUNDERWRITING_LOG_SELECT)
      .single();
    if (error) throw error;
    return data;
  },

  linkLead: async (id, leadId) => {
    if (useMock) return null;
    const { data, error } = await supabase
      .from('preunderwriting_application_logs')
      .update({ lead_id: leadId })
      .eq('id', id)
      .select(PREUNDERWRITING_LOG_SELECT)
      .single();
    if (error) throw error;
    return data;
  },

  revealField: async (logId, field) => {
    if (useMock) throw new Error('Reveal requires Supabase.');
    if (!PREUNDERWRITING_REVEAL_FIELDS.has(field)) {
      throw new Error(
        "field must be one of: 'ssn', 'beneficiaries', 'routing_number', 'account_number', 'spouse_ssn', 'children'",
      );
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    const { data, error } = await supabase.functions.invoke('decrypt-preunderwriting-field', {
      body: { log_id: logId, field },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    if (error) {
      let msg = error.message || 'Failed to reveal field';
      if (error instanceof FunctionsHttpError && error.context?.json) {
        try {
          const errBody = await error.context.json();
          if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
        } catch {
          /* keep msg */
        }
      }
      throw new Error(msg);
    }

    if (data?.error) {
      throw new Error(typeof data.error === 'string' ? data.error : 'Failed to reveal field');
    }
    if (!data?.success) {
      throw new Error('Failed to reveal field');
    }
    return { success: true, value: data.value };
  },

  buildPublicLink: (agencySlug, agentId) =>
    `https://go.prosperityleaders.net/preapp/${agencySlug}/${agentId}`,
};

// Plaintext columns only — never select *_encrypted over the Data API.
const ESTATE_INTAKE_LOG_SAFE_COLUMNS = [
  'id',
  'agency_id',
  'agent_id',
  'split_agent_id',
  'status',
  'created_at',
  'reviewed_at',
  'reviewed_by',
  'lead_id',
  'consent_communication',
  'consent_truthful_information',
  'grantor_full_name',
  'grantor_dob',
  'marital_status',
  'other_parent_in_picture',
  'other_parent_custody_rights',
  'other_parent_notes',
  'same_person_trustee_and_guardian',
  'distribution_structure',
  'distribution_details',
  'llc_legal_name',
  'llc_formation_date',
  'llc_members',
  'new_registered_agent_address',
  'property_address',
  'property_titled_in',
  'property_purchase_price',
  'property_mortgage_notes',
  'other_assets_notes',
  'wants_incapacity_trigger',
  'incapacity_trigger_notes',
  'second_parent_consent_required',
  'second_parent_consent_notes',
  'poa_power_medical',
  'poa_power_educational',
  'poa_power_financial',
  'poa_power_travel',
  'poa_trigger_language',
  'poa_duration_type',
  'poa_duration_notes',
  'agent_name_code',
  'split_agent_name_code',
  'agent_other_comments',
  'retainer_amount',
  'billing_name',
  'billing_address',
  'payment_method_preference',
  'payment_link_clicked_at',
  'payment_status',
  'payment_confirmed_at',
  'payment_confirmed_by',
].join(', ');

const ESTATE_INTAKE_LOG_EMBEDS = `
  agent:agent_id(id, full_name, email),
  split_agent:split_agent_id(id, full_name, email),
  lead:lead_id(id, first_name, last_name)
`;

const ESTATE_INTAKE_LOG_SELECT = `${ESTATE_INTAKE_LOG_SAFE_COLUMNS},${ESTATE_INTAKE_LOG_EMBEDS}`;

const ESTATE_INTAKE_REVEAL_FIELDS = new Set([
  'grantor_ssn',
  'other_parent_contact',
  'trustees',
  'beneficiaries',
  'llc_ein',
  'bank_accounts',
  'life_insurance',
  'guardians',
]);

const ESTATE_INTAKE_JSON_REVEAL_FIELDS = new Set([
  'other_parent_contact',
  'trustees',
  'beneficiaries',
  'bank_accounts',
  'life_insurance',
  'guardians',
]);

const ESTATE_INTAKE_LOG_STATUSES = new Set(['submitted', 'reviewed', 'archived']);
const ESTATE_INTAKE_PAYMENT_STATUSES = new Set(['pending', 'confirmed']);

export const estateIntakeLogs = {
  getAll: async ({ status, agentId, search } = {}) => {
    if (useMock) return [];
    let query = supabase
      .from('estate_intake_logs')
      .select(ESTATE_INTAKE_LOG_SELECT)
      .order('created_at', { ascending: false });

    if (status) query = query.eq('status', status);
    if (agentId) query = query.eq('agent_id', agentId);
    if (search) {
      query = query.or(
        `grantor_full_name.ilike.%${search}%,billing_name.ilike.%${search}%`,
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  getById: async (id) => {
    if (useMock) return null;
    const { data, error } = await supabase
      .from('estate_intake_logs')
      .select(ESTATE_INTAKE_LOG_SELECT)
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  updateStatus: async (id, status, notes = null) => {
    if (useMock) return null;
    if (!ESTATE_INTAKE_LOG_STATUSES.has(status)) {
      throw new Error("status must be one of: 'submitted', 'reviewed', 'archived'");
    }
    const userId = await getSessionUserId();
    if (!userId) throw new Error('Not authenticated');

    const updates = {
      status,
      reviewed_at: new Date().toISOString(),
      reviewed_by: userId,
    };

    const { data, error } = await supabase
      .from('estate_intake_logs')
      .update(updates)
      .eq('id', id)
      .select(ESTATE_INTAKE_LOG_SELECT)
      .single();
    if (error) throw error;
    return data;
  },

  linkLead: async (id, leadId) => {
    if (useMock) return null;
    const { data, error } = await supabase
      .from('estate_intake_logs')
      .update({ lead_id: leadId })
      .eq('id', id)
      .select(ESTATE_INTAKE_LOG_SELECT)
      .single();
    if (error) throw error;
    return data;
  },

  updatePaymentStatus: async (id, paymentStatus) => {
    if (useMock) return null;
    if (!ESTATE_INTAKE_PAYMENT_STATUSES.has(paymentStatus)) {
      throw new Error("paymentStatus must be one of: 'pending', 'confirmed'");
    }
    const userId = await getSessionUserId();
    if (!userId) throw new Error('Not authenticated');

    const updates = { payment_status: paymentStatus };
    if (paymentStatus === 'confirmed') {
      updates.payment_confirmed_at = new Date().toISOString();
      updates.payment_confirmed_by = userId;
    }

    const { data, error } = await supabase
      .from('estate_intake_logs')
      .update(updates)
      .eq('id', id)
      .select(ESTATE_INTAKE_LOG_SELECT)
      .single();
    if (error) throw error;
    return data;
  },

  revealField: async (logId, field) => {
    if (useMock) throw new Error('Reveal requires Supabase.');
    if (!ESTATE_INTAKE_REVEAL_FIELDS.has(field)) {
      throw new Error(
        "field must be one of: 'grantor_ssn', 'other_parent_contact', 'trustees', 'beneficiaries', 'llc_ein', 'bank_accounts', 'life_insurance', 'guardians'",
      );
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    const { data, error } = await supabase.functions.invoke('decrypt-estateintake-field', {
      body: { log_id: logId, field },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    if (error) {
      let msg = error.message || 'Failed to reveal field';
      let status = error.context?.status;
      if (error instanceof FunctionsHttpError && error.context?.json) {
        try {
          const errBody = await error.context.json();
          if (errBody?.message && typeof errBody.message === 'string') msg = errBody.message;
          else if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
        } catch {
          /* keep msg */
        }
      }
      if (status === 403 || /forbidden/i.test(msg)) {
        const forbidden = new Error('Forbidden');
        forbidden.status = 403;
        throw forbidden;
      }
      throw new Error(msg);
    }

    if (data?.message && data?.success === false) {
      if (/forbidden/i.test(data.message)) {
        const forbidden = new Error('Forbidden');
        forbidden.status = 403;
        throw forbidden;
      }
      throw new Error(typeof data.message === 'string' ? data.message : 'Failed to reveal field');
    }
    if (data?.error) {
      if (/forbidden/i.test(String(data.error))) {
        const forbidden = new Error('Forbidden');
        forbidden.status = 403;
        throw forbidden;
      }
      throw new Error(typeof data.error === 'string' ? data.error : 'Failed to reveal field');
    }
    if (!data?.success) {
      throw new Error('Failed to reveal field');
    }

    let value = data.value;
    if (ESTATE_INTAKE_JSON_REVEAL_FIELDS.has(field) && typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch {
        /* leave as string */
      }
    }

    return { success: true, value };
  },

  buildPublicLink: (agencySlug, agentId) =>
    `https://go.prosperityleaders.net/estateapp/${agencySlug}/${agentId}`,
};

const EMAIL_LOG_HISTORY_COLUMNS = [
  'id',
  'agency_id',
  'lead_id',
  'agent_id',
  'campaign_id',
  'to_email',
  'subject',
  'body_html',
  'direction',
  'status',
  'send_method',
  'brevo_message_id',
  'created_at',
  'delivered_at',
  'first_opened_at',
  'last_opened_at',
  'open_count',
  'first_clicked_at',
  'last_clicked_at',
  'click_count',
  'bounced_at',
  'tracking_token',
].join(', ');

const SCHEDULED_EMAIL_LIST_COLUMNS = [
  'id',
  'to_email',
  'subject',
  'send_at',
  'status',
  'attempts',
  'last_error',
  'email_log_id',
  'created_at',
].join(', ');

const SCHEDULED_EMAIL_LOCKED_MESSAGE = 'This email was already sent or can no longer be changed.';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EMAIL_OPEN_PIXEL_URL =
  'https://ogxgxzvxbmnszfxbiuai.supabase.co/functions/v1/email-open-pixel';

/** Open-tracking pixel URL for a gmail_manual email_logs.tracking_token. */
export function buildEmailOpenPixelUrl(token) {
  const t = typeof token === 'string' ? token.trim() : '';
  if (!UUID_RE.test(t)) throw new Error('A valid tracking token (UUID) is required');
  return `${EMAIL_OPEN_PIXEL_URL}?t=${encodeURIComponent(t)}`;
}

function toSendAtIso(sendAt) {
  const d = sendAt instanceof Date ? sendAt : new Date(sendAt);
  if (!sendAt || Number.isNaN(d.getTime())) throw new Error('A valid send time is required');
  return d.toISOString();
}

/** Sender may be null (agent deleted, or hidden from the viewer by users RLS). */
function normalizeEmailLogRow(row) {
  return {
    ...row,
    status: row.status || 'sent',
    send_method: row.send_method || 'brevo',
    open_count: row.open_count ?? 0,
    click_count: row.click_count ?? 0,
    user: row.user ?? null,
  };
}

function isRlsInsertDenied(error) {
  return (
    error?.code === '42501' ||
    /row-level security/i.test(error?.message || '')
  );
}

/**
 * Updates status or send_at on a still-pending scheduled email. RLS hides rows
 * the caller may not change, so "0 rows" covers both already-sent and not-allowed.
 */
async function updatePendingScheduledEmail(id, patch) {
  if (useMock) throw new Error('Scheduled emails require Supabase.');
  if (!id) throw new Error('id is required');
  const { data, error } = await supabase
    .from('scheduled_emails')
    .update(patch)
    .eq('id', id)
    .eq('status', 'pending')
    .select(SCHEDULED_EMAIL_LIST_COLUMNS);
  if (error) {
    if (isRlsInsertDenied(error)) {
      const denied = new Error("You don't have permission to change this scheduled email.");
      denied.code = error.code;
      denied.cause = error;
      throw denied;
    }
    throw error;
  }
  if (!data || data.length === 0) throw new Error(SCHEDULED_EMAIL_LOCKED_MESSAGE);
  return data[0];
}

export const api = {
  auth: {
    signUpAgency: async (agencyData, userData, password) => {
      if (useMock) {
        const res = await localDB.createAgency(agencyData, userData);
        currentMockUserId = res.user.id;
        localStorage.setItem('mock_user_id', currentMockUserId);
        return res;
      }
      const email = userData.email?.trim();
      if (!email || !password) throw new Error('Email and password are required');

      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: userData.full_name },
        },
      });
      if (signUpError) throw signUpError;

      if (!signUpData.session) {
        localStorage.setItem(
          PENDING_SIGNUP_KEY,
          JSON.stringify({ agencyData, full_name: userData.full_name })
        );
        throw new Error(
          'We sent a confirmation link to your email. After you confirm, sign in on this device to finish creating your agency.'
        );
      }

      const { error: rpcError } = await supabase.rpc('complete_agency_signup', {
        p_agency_name: agencyData.name,
        p_slug: agencyData.slug,
        p_full_name: userData.full_name,
      });
      if (rpcError) throw rpcError;

      localStorage.removeItem(PENDING_SIGNUP_KEY);
      const profile = await fetchProfileRowForAuthUser(signUpData.user.id);
      if (!profile) throw new Error('Could not load profile after signup');
      return { user: profile, agency: null };
    },
    signIn: async (email, password) => {
      if (useMock) {
        const res = await localDB.login(email, password);
        currentMockUserId = res.user.id;
        localStorage.setItem('mock_user_id', currentMockUserId);
        return res;
      }
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      let profile = await fetchProfileRowForAuthUser(data.user.id);
      if (!profile) {
        await completePendingAgencySignup();
        profile = await fetchProfileRowForAuthUser(data.user.id);
      }
      if (!profile) {
        throw new Error(
          'No agency profile found for this account. Complete signup from the registration email on this browser, or contact support.'
        );
      }
      return { user: profile };
    },
    signOut: async () => {
      if (useMock) {
        currentMockUserId = null;
        localStorage.removeItem('mock_user_id');
        return;
      }
      await supabase.auth.signOut();
    },
    getCurrentUser: async () => {
      if (useMock) {
        if (!currentMockUserId) return null;
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        return db.users?.find((u) => u.id === currentMockUserId) || null;
      }
      const userId = await getSessionUserId();
      if (!userId) return null;
      const { data, error } = await supabase.from('users').select('*').eq('id', userId).maybeSingle();
      if (error) throw error;
      return data;
    },
    resetPasswordForEmail: async (email) => {
      if (useMock) {
        throw new Error('Password reset requires Supabase. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
      }
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: PASSWORD_RESET_REDIRECT_URL,
      });
      if (error) throw error;
    },
    updatePassword: async (password) => {
      if (useMock) {
        throw new Error('Password reset requires Supabase. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error(
          'This reset link is invalid or has expired. Request a new one and open it in the same browser you used to request it.',
        );
      }
      const { data, error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      if (!data?.user) {
        throw new Error('Could not update password. Request a new reset email and try again.');
      }
    },
  },
  dashboard: {
    /**
     * Snapshot metrics for the selected period (new leads, call activity, appts, goal target).
     * @param {string} agencyId
     * @param {{ startDate?: string, endDate?: string }} range — when both set, filters apply.
     */
    getSnapshot: async (agencyId, { startDate, endDate } = {}) => {
      if (useMock) return localDB.getDashboardSnapshot(currentMockUserId, { startDate, endDate });
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return { ...EMPTY_SNAPSHOT };

      const hasRange = dashboardRangeIsValid(startDate, endDate);
      const rangeStart = hasRange ? safeToISO(startDate) : null;
      const rangeEnd = hasRange ? safeToISO(endDate) : null;
      if (import.meta.env.DEV && hasRange) {
        console.log('[dashboard] getSnapshot date range', { agencyId, startDate: rangeStart, endDate: rangeEnd });
      }

      let newLeadsQ = supabase
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .eq('agency_id', agencyId);
      const apptsQ = supabase
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .eq('agency_id', agencyId)
        .or('status.eq.appointment_set,pipeline_stage.eq.Appointment Set');
      let attemptsQ = supabase
        .from('call_logs')
        .select('id', { count: 'exact', head: true })
        .eq('agency_id', agencyId);
      if (hasRange) {
        newLeadsQ = newLeadsQ.gte('created_at', rangeStart).lte('created_at', rangeEnd);
        attemptsQ = attemptsQ.gte('called_at', rangeStart).lte('called_at', rangeEnd);
      }

      const [newLeadsRes, apptsRes, attemptsRes, agencyRes] = await Promise.all([
        newLeadsQ,
        apptsQ,
        attemptsQ,
        supabase.from('agencies').select('daily_call_goal').eq('id', agencyId).maybeSingle(),
      ]);
      for (const r of [newLeadsRes, apptsRes, attemptsRes, agencyRes]) {
        if (r.error) throw r.error;
      }

      const leadsCalledDistinct = await distinctLeadIdsFromCallLogs(
        agencyId,
        hasRange ? { startDate: rangeStart, endDate: rangeEnd } : {},
      );

      const dailyCallGoal =
        typeof agencyRes.data?.daily_call_goal === 'number' && agencyRes.data.daily_call_goal > 0
          ? agencyRes.data.daily_call_goal
          : 100;

      return {
        newLeadsCount: newLeadsRes.count ?? 0,
        callAttempts: attemptsRes.count ?? 0,
        leadsCalledDistinct,
        appointmentsSet: apptsRes.count ?? 0,
        dailyCallGoal,
      };
    },

    /** Outcome breakdown from `call_logs` for the period (called_at). */
    getCallingActivity: async (agencyId, { startDate, endDate } = {}) => {
      if (useMock) return localDB.getDashboardCallingActivity(currentMockUserId, { startDate, endDate });
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return { ...EMPTY_CALL_OUTCOMES };

      const hasRange = dashboardRangeIsValid(startDate, endDate);
      const rangeStart = hasRange ? safeToISO(startDate) : null;
      const rangeEnd = hasRange ? safeToISO(endDate) : null;
      const rows = await paginateSelect((from, to) => {
        let b = supabase.from('call_logs').select('outcome').eq('agency_id', agencyId);
        if (hasRange) b = b.gte('called_at', rangeStart).lte('called_at', rangeEnd);
        return b.range(from, to);
      });
      const TRANSIENT = new Set(['ringing', 'connecting']);
      const callOutcomes = { ...EMPTY_CALL_OUTCOMES };
      for (const row of rows) {
        const o = row?.outcome;
        if (!o || TRANSIENT.has(o)) continue;
        if (o === 'answered') callOutcomes.answered += 1;
        else if (o === 'voicemail' || o === 'machine') callOutcomes.voicemail += 1;
        else if (o === 'no_answer') callOutcomes.no_answer += 1;
        else if (o === 'busy') callOutcomes.busy += 1;
        else if (o === 'booked_meeting') callOutcomes.booked_meeting += 1;
        else if (o === 'closed_business') callOutcomes.closed_business += 1;
      }
      return callOutcomes;
    },

    /** Sales + recruiting stage charts — all agency leads by current stage (ignores period filter). */
    getPipelineSummary: async (agencyId) => {
      if (useMock) return localDB.getDashboardPipelineSummary(currentMockUserId);
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return { salesPipeline: [], recruitingByStage: [] };

      const { data: pipelines, error: pipeErr } = await supabase
        .from('pipelines')
        .select('pipeline_type,stages,created_at')
        .eq('agency_id', agencyId)
        .order('created_at', { ascending: true });
      if (pipeErr) throw pipeErr;

      const pipelinesList = pipelines || [];
      const salesPipelines = pipelinesList.filter((p) => p.pipeline_type === 'sales');
      const salesStageLabels =
        salesPipelines.length && Array.isArray(salesPipelines[0].stages) && salesPipelines[0].stages.length
          ? salesPipelines[0].stages
          : DEFAULT_SALES_STAGES;

      const recruitingPipelines = pipelinesList.filter((p) => p.pipeline_type === 'recruiting');
      const recruitStageLabels =
        recruitingPipelines.length &&
        Array.isArray(recruitingPipelines[0].stages) &&
        recruitingPipelines[0].stages.length
          ? recruitingPipelines[0].stages
          : DEFAULT_RECRUITING_STAGES;

      const allLeads = await paginateSelect((from, to) =>
        supabase
          .from('leads')
          .select('pipeline_stage,lead_type')
          .eq('agency_id', agencyId)
          .range(from, to),
      );
      const normLeadType = (v) => String(v ?? '').trim().toLowerCase();
      const nonRecruitLeads = allLeads.filter((l) => normLeadType(l.lead_type) !== 'recruit');
      const recruitLeads = allLeads.filter((l) => normLeadType(l.lead_type) === 'recruit');

      const salesCounts = countPipelineStages(nonRecruitLeads, 'New Lead');
      const salesPipeline = orderedStageChart(salesStageLabels, salesCounts);
      const recruitCounts = countPipelineStages(recruitLeads, 'New Prospect');
      const recruitingByStage = orderedStageChart(recruitStageLabels, recruitCounts);

      return { salesPipeline, recruitingByStage };
    },

    /** Today's call attempt count (called_at) and agency daily_call_goal — ignores global period filter. */
    getDailyGoalProgress: async (agencyId) => {
      if (useMock) return localDB.getDashboardDailyGoal(currentMockUserId);
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return { dailyCallGoal: 100, callAttemptsToday: 0 };

      const { startDate, endDate } = getLocalTodayIsoRange();
      if (import.meta.env.DEV) {
        console.log('[dashboard] getDailyGoalProgress range', { agencyId, startDate, endDate });
      }
      const [attemptsRes, agencyRes] = await Promise.all([
        supabase
          .from('call_logs')
          .select('id', { count: 'exact', head: true })
          .eq('agency_id', agencyId)
          .gte('called_at', startDate)
          .lte('called_at', endDate),
        supabase.from('agencies').select('daily_call_goal').eq('id', agencyId).maybeSingle(),
      ]);
      if (attemptsRes.error) throw attemptsRes.error;
      if (agencyRes.error) throw agencyRes.error;

      const dailyCallGoal =
        typeof agencyRes.data?.daily_call_goal === 'number' && agencyRes.data.daily_call_goal > 0
          ? agencyRes.data.daily_call_goal
          : 100;

      return {
        dailyCallGoal,
        callAttemptsToday: attemptsRes.count ?? 0,
      };
    },

    /**
     * Per-channel activity for dashboard widget (respects date range).
     * Agents see only their own stats; managers/admins see agency-wide.
     */
    getChannelActivity: async (agencyId, { startDate, endDate } = {}) => {
      const empty = {
        LD: { leadsAdded: 0, callsMade: 0, appointmentsSet: 0, appsSubmitted: 0 },
        IM: { leadsAdded: 0, callsMade: 0, appointmentsSet: 0, appsSubmitted: 0 },
        AM: { leadsAdded: 0, callsMade: 0, appointmentsSet: 0, appsSubmitted: 0 },
      };
      if (useMock) return empty;
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return empty;
      const agentId = isManagerOrAdminRole(profile.role) ? null : profile.id;
      const range = normalizeChannelActivityRange({ startDate, endDate });
      return fetchChannelActivityForPeriod(agencyId, range, { agentId });
    },

    /**
     * Leadership Team Pipeline Overview metrics.
     * @param {string} agencyId
     * @param {{ startDate?: string, endDate?: string, associateIds?: string[]|null }} opts
     *   associateIds null/undefined = All (agency roster participants); [] = empty team.
     */
    getTeamPipelineOverview: async (agencyId, { startDate, endDate, associateIds = null } = {}) => {
      const empty = {
        inviteLinkClicks: 0,
        eventRegistrations: 0,
        callsMade: 0,
        launchesByStatus: { not_started: 0, scheduled: 0, completed: 0 },
        top25Total: 0,
        top25Converted: 0,
      };
      if (useMock) return empty;
      const profile = await assertAgencyAccess(agencyId);
      if (!profile || !isManagerOrAdminRole(profile.role)) return empty;

      let memberIds = associateIds;
      if (memberIds == null) {
        const { data: roster, error: rosterErr } = await supabase
          .from('users')
          .select('id')
          .eq('agency_id', agencyId)
          .is('archived_at', null);
        if (rosterErr) throw rosterErr;
        memberIds = (roster ?? []).map((u) => u.id);
      }
      if (!Array.isArray(memberIds) || memberIds.length === 0) return empty;

      const hasRange = dashboardRangeIsValid(startDate, endDate);
      const rangeStart = hasRange ? safeToISO(startDate) : null;
      const rangeEnd = hasRange ? safeToISO(endDate) : null;

      const [linksRes, launchesRes, top25Res, callsRes] = await Promise.all([
        supabase
          .from('pm_event_invitation_links')
          .select('id, click_count, user_id')
          .in('user_id', memberIds),
        supabase
          .from('business_launches')
          .select('id, status')
          .eq('agency_id', agencyId)
          .in('associate_id', memberIds),
        supabase
          .from('top25_contacts')
          .select('id, converted_lead_id')
          .eq('agency_id', agencyId)
          .in('associate_id', memberIds),
        (() => {
          let q = supabase
            .from('call_logs')
            .select('id', { count: 'exact', head: true })
            .eq('agency_id', agencyId)
            .in('agent_id', memberIds);
          if (hasRange) q = q.gte('called_at', rangeStart).lte('called_at', rangeEnd);
          return q;
        })(),
      ]);
      for (const r of [linksRes, launchesRes, top25Res, callsRes]) {
        if (r.error) throw r.error;
      }

      const links = linksRes.data ?? [];
      const inviteLinkClicks = links.reduce((sum, row) => sum + (Number(row.click_count) || 0), 0);
      const linkIds = links.map((row) => row.id).filter(Boolean);

      let eventRegistrations = 0;
      // Registrations via team invite links
      if (linkIds.length > 0) {
        const { count, error } = await supabase
          .from('pm_event_registrations')
          .select('id', { count: 'exact', head: true })
          .in('invitation_link_id', linkIds);
        if (error) throw error;
        eventRegistrations += count ?? 0;
      }
      // Plus registrations on events owned by team members (agent_id)
      const { data: teamEvents, error: eventsErr } = await supabase
        .from('pm_events')
        .select('id')
        .eq('agency_id', agencyId)
        .in('agent_id', memberIds);
      if (eventsErr) throw eventsErr;
      const teamEventIds = (teamEvents ?? []).map((e) => e.id);
      if (teamEventIds.length > 0) {
        let regQ = supabase
          .from('pm_event_registrations')
          .select('id, invitation_link_id')
          .in('event_id', teamEventIds);
        const { data: regs, error: regErr } = await regQ;
        if (regErr) throw regErr;
        const linkIdSet = new Set(linkIds);
        // Count regs on team events that weren't already counted via team invite links
        for (const row of regs ?? []) {
          if (row.invitation_link_id && linkIdSet.has(row.invitation_link_id)) continue;
          eventRegistrations += 1;
        }
      }

      const launchesByStatus = { not_started: 0, scheduled: 0, completed: 0 };
      for (const row of launchesRes.data ?? []) {
        if (Object.prototype.hasOwnProperty.call(launchesByStatus, row.status)) {
          launchesByStatus[row.status] += 1;
        }
      }

      const top25Rows = top25Res.data ?? [];
      const top25Total = top25Rows.length;
      const top25Converted = top25Rows.filter((r) => r.converted_lead_id).length;

      return {
        inviteLinkClicks,
        eventRegistrations,
        callsMade: callsRes.count ?? 0,
        launchesByStatus,
        top25Total,
        top25Converted,
      };
    },

    /** Thoughtly AI caller stats for dashboard widget (filtered by created_at). */
    getAiCallerPerformance: async (agencyId, { startDate, endDate } = {}) => {
      const empty = {
        totalCalls: 0,
        appointmentsBooked: 0,
        bookingRate: 0,
        outcomes: { qualified: 0, not_interested: 0, no_answer: 0, voicemail: 0 },
      };
      if (useMock) return empty;
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return empty;

      const hasRange = dashboardRangeIsValid(startDate, endDate);
      const rangeStart = hasRange ? safeToISO(startDate) : null;
      const rangeEnd = hasRange ? safeToISO(endDate) : null;

      const rows = await paginateSelect((from, to) => {
        let q = supabase
          .from('thoughtly_calls')
          .select('outcome, appointment_booked, created_at')
          .eq('agency_id', agencyId);
        if (hasRange) {
          q = q.gte('created_at', rangeStart).lte('created_at', rangeEnd);
        }
        return q.range(from, to);
      });

      const totalCalls = rows.length;
      const appointmentsBooked = rows.filter(
        (r) => r.appointment_booked === true || r.outcome === 'appointment_booked',
      ).length;
      const bookingRate = totalCalls > 0 ? Math.round((appointmentsBooked / totalCalls) * 1000) / 10 : 0;

      const outcomes = { qualified: 0, not_interested: 0, no_answer: 0, voicemail: 0 };
      for (const row of rows) {
        const o = row.outcome;
        if (o && Object.prototype.hasOwnProperty.call(outcomes, o)) {
          outcomes[o] += 1;
        }
      }

      return { totalCalls, appointmentsBooked, bookingRate, outcomes };
    },
  },

  leads: {
    getAll: async (filters) => {
      if (useMock) return localDB.getLeads(currentMockUserId, filters);
      const scope = await resolveLeadsScope(filters);
      if (leadsScopeIsEmpty(filters, scope)) return [];

      const query = applyLeadsScope(
        supabase.from('leads').select(LEADS_FULL_SELECT),
        filters,
        scope,
        { archived: false, sortField: 'created_at', sortDir: 'desc' },
      );
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    getArchived: async (filters) => {
      if (useMock) return [];
      const scope = await resolveLeadsScope(filters);
      if (leadsScopeIsEmpty(filters, scope)) return [];

      const query = applyLeadsScope(
        supabase.from('leads').select(LEADS_FULL_SELECT),
        filters,
        scope,
        { archived: true, sortField: 'archived_at', sortDir: 'desc' },
      );
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    /**
     * One page of leads for the list view.
     *
     * Filtering, searching, sorting and paging all happen server-side. The
     * matching id list is fetched alongside the page because the list view needs
     * it for two things the page alone cannot supply: the total row count, and
     * the prev/next sequence the lead detail view walks. Selecting only `id`
     * skips the people embeds, so it costs a fraction of a full row fetch.
     */
    getPage: async (filters, { page = 1, pageSize = 25, sortField, sortDir, search, agentId, archived = false } = {}) => {
      if (useMock) {
        const rows = await localDB.getLeads(currentMockUserId, filters);
        const list = archived ? [] : (rows || []);
        return { rows: list.slice((page - 1) * pageSize, page * pageSize), ids: list.map((l) => l.id), total: list.length };
      }

      const scope = await resolveLeadsScope(filters, { agentId });
      if (leadsScopeIsEmpty(filters, scope)) return { rows: [], ids: [], total: 0 };

      const shared = { archived, search, sortField, sortDir };
      const from = (page - 1) * pageSize;

      const [pageResult, ids] = await Promise.all([
        applyLeadsScope(
          supabase.from('leads').select(LEADS_LIST_SELECT),
          filters,
          scope,
          shared,
        ).range(from, from + pageSize - 1),
        paginateSelect((rangeFrom, rangeTo) =>
          applyLeadsScope(
            supabase.from('leads').select('id'),
            filters,
            scope,
            shared,
          ).range(rangeFrom, rangeTo)),
      ]);

      if (pageResult.error) throw pageResult.error;
      return {
        rows: pageResult.data || [],
        ids: ids.map((r) => r.id),
        total: ids.length,
      };
    },

    /**
     * Row count only, for the Active/Archived tab labels. `head: true` returns no
     * rows, so this stays cheap even though RLS still has to evaluate the set.
     */
    getCount: async (filters, { agentId, search, archived = false } = {}) => {
      if (useMock) return 0;
      const scope = await resolveLeadsScope(filters, { agentId });
      if (leadsScopeIsEmpty(filters, scope)) return 0;

      const query = applyLeadsScope(
        supabase.from('leads').select('id', { count: 'exact', head: true }),
        filters,
        scope,
        { archived, search },
      );
      const { count, error } = await query;
      if (error) throw error;
      return count ?? 0;
    },
    getById: async (id, { activityLimit = LEAD_ACTIVITY_LIMIT } = {}) => {
      if (useMock) return localDB.getLead(currentMockUserId, id);

      // These four are independent; running them concurrently turns what used to
      // be four sequential round trips into one.
      const [leadRes, callsRes, smsRes, logsRes] = await Promise.all([
        supabase
          .from('leads')
          .select(
            '*,assignments:lead_assignments(*,user:users(*)),exclusive_holder:users!leads_exclusive_to_fkey(id,full_name,email),owner:users!leads_created_by_fkey(id,full_name,email,trainer_id),co_worker:users!leads_co_worker_id_fkey(id,full_name,email)',
          )
          .eq('id', id)
          .single(),
        supabase
          .from('call_logs')
          .select('*, user:users!agent_id(id, full_name, email)')
          .eq('lead_id', id)
          .order('called_at', { ascending: false })
          .limit(activityLimit),
        supabase
          .from('sms_logs')
          .select('*, user:users!agent_id(id, full_name, email)')
          .eq('lead_id', id)
          .order('created_at', { ascending: false })
          .limit(activityLimit),
        supabase
          .from('activity_logs')
          .select('*, user:users!agent_id(id, full_name, email)')
          .eq('lead_id', id)
          .order('created_at', { ascending: false })
          .limit(activityLimit),
      ]);

      if (leadRes.error) throw leadRes.error;
      if (callsRes.error) throw callsRes.error;
      if (smsRes.error) throw smsRes.error;
      if (logsRes.error) throw logsRes.error;

      const data = leadRes.data;
      const activityRows = callsRes.data;
      const smsRows = smsRes.data;
      const logRows = logsRes.data;

      const callItems = (activityRows || []).map((c) => ({
        ...c,
        type: 'call',
        user: c.user,
        activity_at: c.called_at,
      }));
      const smsItems = (smsRows || []).map((s) => ({
        ...s,
        type: 'sms',
        user: s.user,
        activity_at: s.created_at,
      }));
      const activityLogItems = (logRows || []).map((row) => ({
        ...row,
        type: 'activity',
        user: row.user,
        activity_at: row.created_at,
      }));
      const activity = [...callItems, ...smsItems, ...activityLogItems].sort(
        (a, b) => new Date(b.activity_at).getTime() - new Date(a.activity_at).getTime(),
      );
      return { ...data, activity, callLogs: activityRows || [] };
    },
    /**
     * Newest activity rows for Call Workspace. Same item shapes as
     * RecentActivityPanel.fetchLeadActivity, without loading the full lead.
     */
    getRecentActivity: async (leadId, { limit = 8 } = {}) => {
      if (useMock) return [];
      if (!leadId) return [];
      const cap = Math.max(1, Number(limit) || 8);

      const [callsRes, smsLogsRes, inboundRes, apptsRes] = await Promise.all([
        supabase
          .from('call_logs')
          .select('*, user:users!agent_id(id, full_name, email)')
          .eq('lead_id', leadId)
          .order('called_at', { ascending: false })
          .limit(cap),
        supabase
          .from('sms_logs')
          .select('*, user:users!agent_id(id, full_name, email)')
          .eq('lead_id', leadId)
          .order('created_at', { ascending: false })
          .limit(cap),
        supabase
          .from('sms_messages')
          .select('id, lead_id, direction, body, from_number, created_at')
          .eq('lead_id', leadId)
          .eq('direction', 'inbound')
          .order('created_at', { ascending: false })
          .limit(cap),
        supabase
          .from('appointments')
          .select(
            '*, assignee:users!appointments_assigned_to_fkey(id, full_name, email, role)',
          )
          .eq('lead_id', leadId)
          .order('created_at', { ascending: false })
          .limit(cap),
      ]);
      if (callsRes.error) throw callsRes.error;
      if (smsLogsRes.error) throw smsLogsRes.error;
      if (inboundRes.error) throw inboundRes.error;
      if (apptsRes.error) throw apptsRes.error;

      const callItems = (callsRes.data || []).map((c) => ({
        ...c,
        type: 'call',
        user: c.user,
        activity_at: c.called_at,
      }));
      const smsItems = (smsLogsRes.data || []).map((s) => ({
        ...s,
        type: 'sms',
        direction: 'outbound',
        user: s.user,
        activity_at: s.created_at,
      }));
      const inboundItems = (inboundRes.data || []).map((s) => ({
        ...s,
        type: 'sms',
        direction: 'inbound',
        activity_at: s.created_at,
      }));
      const noteItems = callItems
        .filter((c) => c.notes?.trim())
        .map((c) => ({
          id: `${c.id}-note`,
          type: 'note',
          activity_at: c.called_at,
          notes: c.notes,
          user: c.user,
        }));
      const apptItems = (apptsRes.data || []).map((a) => ({
        ...a,
        type: 'appointment',
        activity_at: a.created_at,
      }));

      return [...callItems, ...smsItems, ...inboundItems, ...noteItems, ...apptItems]
        .sort((a, b) => new Date(b.activity_at).getTime() - new Date(a.activity_at).getTime())
        .slice(0, cap);
    },
    create: async (leadData) => {
      const userId = await getSessionUserId();
      if (!userId && !useMock) throw new Error('Not authenticated');
      const profile = useMock ? null : await fetchProfileRowForAuthUser(userId);
      if (!useMock && !profile?.agency_id) throw new Error('No agency profile');

      const creatorId = profile?.id ?? userId;
      const insertPayload = { ...leadData };
      if (!useMock) {
        insertPayload.agency_id = profile.agency_id;
      }
      if (insertPayload.created_by == null) {
        insertPayload.created_by = creatorId;
      }
      if (!insertPayload.exclusivity_status) {
        insertPayload.exclusivity_status = 'exclusive';
      }
      if (insertPayload.exclusivity_status === 'exclusive') {
        insertPayload.exclusive_to = leadData.exclusive_to || insertPayload.created_by;
      }

      if (useMock) return localDB.createLead(currentMockUserId, insertPayload);

      const { data, error } = await supabase
        .from('leads')
        .insert(insertPayload)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    findDuplicatesByPhoneOrEmail: async (phones, emails, agencyId) => {
      const normPhones = [
        ...new Set((phones || []).map((p) => normalizeImportPhone(p)).filter(Boolean)),
      ];
      const normEmails = [
        ...new Set((emails || []).map((e) => normalizeImportEmail(e)).filter(Boolean)),
      ];
      if (!normPhones.length && !normEmails.length) return [];

      if (useMock) return [];

      let resolvedAgencyId = agencyId;
      if (!resolvedAgencyId) {
        const authId = await getSessionUserId();
        if (!authId) return [];
        const profile = await fetchProfileRowForAuthUser(authId);
        if (!profile?.agency_id) return [];
        resolvedAgencyId = profile.agency_id;
      }

      const phoneSet = new Set(normPhones);
      const emailSet = new Set(normEmails);
      const byId = new Map();

      const mergeRows = (rows) => {
        (rows || []).forEach((row) => {
          if (!row?.id) return;
          byId.set(row.id, row);
        });
      };

      const selectCols =
        'id, first_name, last_name, phone, email, lead_source, created_by, channel';

      if (normEmails.length) {
        const emailRows = await paginateSelect((from, to) =>
          supabase
            .from('leads')
            .select(selectCols)
            .eq('agency_id', resolvedAgencyId)
            .not('email', 'is', null)
            .range(from, to),
        );
        (emailRows || []).forEach((row) => {
          const em = normalizeImportEmail(row.email);
          if (em && emailSet.has(em)) mergeRows([row]);
        });
      }

      if (normPhones.length) {
        const phoneRows = await paginateSelect((from, to) =>
          supabase
            .from('leads')
            .select(selectCols)
            .eq('agency_id', resolvedAgencyId)
            .not('phone', 'is', null)
            .range(from, to),
        );
        (phoneRows || []).forEach((row) => {
          const row10 = phoneLast10(row.phone);
          if (row10 && [...phoneSet].some((p) => phoneLast10(p) === row10)) mergeRows([row]);
        });
      }

      return [...byId.values()];
    },
    bulkCreate: async (
      leadsPayload,
      {
        duplicateMode = 'merge',
        skipDuplicatesByPhone = false,
        updateDuplicates = false,
        agentAssignments = null,
      } = {},
    ) => {
      if (!Array.isArray(leadsPayload) || leadsPayload.length === 0) {
        if (duplicateMode === 'merge') {
          return { newRows: [], duplicateRows: [], allRows: [] };
        }
        return { inserted: 0, updated: 0, skipped: 0, failed: 0 };
      }

      const userId = await getSessionUserId();
      if (!userId && !useMock) throw new Error('Not authenticated');
      const profile = useMock ? null : await fetchProfileRowForAuthUser(userId);
      if (!useMock && !profile?.agency_id) throw new Error('No agency profile');

      const agencyId = profile?.agency_id;

      if (duplicateMode === 'merge') {
        const phones = leadsPayload.map((r) => normalizeImportPhone(r.phone)).filter(Boolean);
        const emails = leadsPayload.map((r) => normalizeImportEmail(r.email)).filter(Boolean);
        const existing = await api.leads.findDuplicatesByPhoneOrEmail(phones, emails, agencyId);

        const phoneToLead = new Map();
        const emailToLead = new Map();
        (existing || []).forEach((lead) => {
          const ph = normalizeImportPhone(lead.phone);
          const em = normalizeImportEmail(lead.email);
          if (ph && !phoneToLead.has(ph)) phoneToLead.set(ph, lead);
          if (em && !emailToLead.has(em)) emailToLead.set(em, lead);
        });

        const newRows = [];
        const duplicateRows = [];
        const allRows = [];

        leadsPayload.forEach((csvRow, index) => {
          const ph = normalizeImportPhone(csvRow.phone);
          const em = normalizeImportEmail(csvRow.email);
          let match = null;
          if (em && emailToLead.has(em)) match = emailToLead.get(em);
          else if (ph && phoneToLead.has(ph)) match = phoneToLead.get(ph);

          if (!match) {
            const row = { status: 'new', csvRow, rowIndex: index };
            newRows.push(row);
            allRows.push(row);
          } else {
            const existingName = [match.first_name, match.last_name].filter(Boolean).join(' ');
            const row = {
              status: 'duplicate',
              csvRow,
              rowIndex: index,
              existingLeadId: match.id,
              existingLead: match,
              existingLeadName: existingName,
            };
            duplicateRows.push(row);
            allRows.push(row);
          }
        });

        return { newRows, duplicateRows, allRows };
      }

      if (useMock) {
        const res = await localDB.createLeadsBulk(currentMockUserId, leadsPayload);
        return { inserted: res?.inserted ?? 0, updated: 0, skipped: 0, failed: 0 };
      }

      const base = {
        agency_id: profile.agency_id,
        created_by: profile.id,
      };

      let existingPhones = new Set();
      if (skipDuplicatesByPhone) {
        const { data: existing } = await supabase
          .from('leads')
          .select('phone')
          .eq('agency_id', profile.agency_id)
          .not('phone', 'is', null);
        if (existing) {
          existingPhones = new Set(existing.map((r) => r.phone.replace(/\D/g, '')));
        }
      }

      const rows = [];
      const rowAgentIds = [];
      const updateOps = [];
      let skipped = 0;

      for (const r of leadsPayload) {
        if (updateDuplicates && r._existing_lead_id) {
          updateOps.push({ id: r._existing_lead_id, data: buildBulkLeadOverwriteUpdate(r) });
          continue;
        }

        const phone = r.phone?.trim() || null;
        if (skipDuplicatesByPhone && phone) {
          const phoneDigits = phone.replace(/\D/g, '');
          if (phoneDigits && existingPhones.has(phoneDigits)) {
            skipped++;
            continue;
          }
          existingPhones.add(phoneDigits);
        }

        rows.push({
          ...buildBulkLeadDataCols(r),
          ...(r.created_at ? { created_at: r.created_at } : {}),
          ...(r.exclusivity_status ? { exclusivity_status: r.exclusivity_status } : {}),
          ...base,
        });
        rowAgentIds.push(r._matched_agent_id || null);
      }

      const { inserted, failed, assigned } = await executeBulkLeadInserts(
        rows,
        rowAgentIds,
        profile,
        agentAssignments,
      );

      let updated = 0;
      let updateFailed = 0;
      for (const op of updateOps) {
        const { error: updErr } = await supabase
          .from('leads')
          .update(op.data)
          .eq('id', op.id)
          .eq('agency_id', profile.agency_id);
        if (updErr) {
          updateFailed += 1;
          console.error('[api.leads.bulkCreate] failed to update duplicate lead', updErr);
        } else {
          updated += 1;
        }
      }

      return {
        inserted,
        updated,
        skipped,
        failed: failed + updateFailed,
        assigned,
      };
    },
    bulkCreateConfirmed: async ({
      newRows = [],
      duplicateRows = [],
      agencyId,
      userId,
      agentAssignments = null,
    }) => {
      const newPayload = (newRows || []).map((r) => r.csvRow ?? r);
      const dupPayload = (duplicateRows || []).map((r) => ({
        csvRow: r.csvRow ?? r,
        existingLeadId: r.existingLeadId ?? r.existingLead?.id,
      }));

      if (!newPayload.length && !dupPayload.length) {
        return { created: 0, updated: 0, activityNotesAdded: 0 };
      }

      if (useMock) {
        const res = await localDB.createLeadsBulk(currentMockUserId, newPayload);
        return {
          created: res?.inserted ?? 0,
          updated: 0,
          activityNotesAdded: 0,
        };
      }

      const authId = userId || (await getSessionUserId());
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      const resolvedAgencyId = agencyId || profile?.agency_id;
      if (!resolvedAgencyId) throw new Error('No agency profile');

      const base = {
        agency_id: resolvedAgencyId,
        created_by: profile.id,
      };

      const insertRows = [];
      const rowAgentIds = [];
      for (const r of newPayload) {
        insertRows.push({
          ...buildBulkLeadDataCols(r),
          ...(r.created_at ? { created_at: r.created_at } : {}),
          ...(r.exclusivity_status ? { exclusivity_status: r.exclusivity_status } : {}),
          ...base,
        });
        rowAgentIds.push(r._matched_agent_id || null);
      }

      const { inserted: created } = await executeBulkLeadInserts(
        insertRows,
        rowAgentIds,
        { agency_id: resolvedAgencyId, id: profile.id },
        agentAssignments,
      );

      let updated = 0;
      let activityNotesAdded = 0;
      const activityDate = toLocalDateKey(new Date());

      for (const item of dupPayload) {
        const { csvRow, existingLeadId } = item;
        if (!existingLeadId || !csvRow) continue;

        const { data: existingLead, error: fetchErr } = await supabase
          .from('leads')
          .select('*')
          .eq('id', existingLeadId)
          .eq('agency_id', resolvedAgencyId)
          .maybeSingle();
        if (fetchErr || !existingLead) {
          console.error('[api.leads.bulkCreateConfirmed] failed to fetch lead', fetchErr);
          continue;
        }

        const updateData = buildBulkLeadFillEmptyUpdate(existingLead, csvRow);
        if (Object.keys(updateData).length > 0) {
          const { error: updErr } = await supabase
            .from('leads')
            .update(updateData)
            .eq('id', existingLeadId)
            .eq('agency_id', resolvedAgencyId);
          if (updErr) {
            console.error('[api.leads.bulkCreateConfirmed] failed to update lead', updErr);
          } else {
            updated += 1;
          }
        }

        const notes = buildCsvImportMergeNote(csvRow, existingLead);
        const { error: logErr } = await supabase.from('activity_logs').insert({
          lead_id: existingLeadId,
          agency_id: resolvedAgencyId,
          agent_id: profile.id,
          activity_type: 'csv_import_merge',
          activity_date: activityDate,
          notes,
        });
        if (logErr) {
          console.error('[api.leads.bulkCreateConfirmed] failed to insert activity log', logErr);
        } else {
          activityNotesAdded += 1;
        }
      }

      return { created, updated, activityNotesAdded };
    },
    /**
     * Return { id, email, phone } for every lead in the current user's agency,
     * used by the CSV importer to detect duplicates by email or phone.
     */
    findExistingContacts: async () => {
      if (useMock) return [];
      const authId = await getSessionUserId();
      if (!authId) return [];
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) return [];
      const { data, error } = await supabase
        .from('leads')
        .select('id, email, phone')
        .eq('agency_id', profile.agency_id);
      if (error) throw error;
      return data || [];
    },
    /**
     * Return distinct, non-null `import_label` and `event_title` values for the
     * current user's agency, used to populate the leads list filter dropdowns.
     */
    getImportFilterOptions: async () => {
      if (useMock) return { importLabels: [], eventTitles: [] };
      const authId = await getSessionUserId();
      if (!authId) return { importLabels: [], eventTitles: [] };
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) return { importLabels: [], eventTitles: [] };

      const [labelsRes, eventsRes] = await Promise.all([
        supabase
          .from('leads')
          .select('import_label')
          .eq('agency_id', profile.agency_id)
          .not('import_label', 'is', null),
        supabase
          .from('leads')
          .select('event_title')
          .eq('agency_id', profile.agency_id)
          .not('event_title', 'is', null),
      ]);
      if (labelsRes.error) throw labelsRes.error;
      if (eventsRes.error) throw eventsRes.error;

      const dedupe = (rows, key) => {
        const set = new Set();
        (rows || []).forEach((r) => {
          const v = (r[key] ?? '').trim();
          if (v) set.add(v);
        });
        return [...set].sort((a, b) => a.localeCompare(b));
      };

      return {
        importLabels: dedupe(labelsRes.data, 'import_label'),
        eventTitles: dedupe(eventsRes.data, 'event_title'),
      };
    },
    update: async (id, updates) => {
      if (useMock) return localDB.updateLeadStatus(currentMockUserId, id, updates);

      let priorRow = null;
      if (updates.status !== undefined) {
        const { data: prior, error: priorErr } = await supabase
          .from('leads')
          .select('status, agency_id')
          .eq('id', id)
          .maybeSingle();
        if (priorErr) throw priorErr;
        priorRow = prior;
      }

      if (updates.status === 'closed_won') {
        const authId = await getSessionUserId();
        if (authId) {
          const profile = await fetchProfileRowForAuthUser(authId);
          const { data: leadRow, error: leadErr } = await supabase
            .from('leads')
            .select('created_by, exclusivity_status')
            .eq('id', id)
            .maybeSingle();
          if (!leadErr && leadRow && profile) {
            const isNonOwnerCloser =
              leadRow.created_by &&
              leadRow.created_by !== profile.id &&
              leadRow.exclusivity_status === 'non_exclusive';
            if (isNonOwnerCloser) {
              let ownerName = 'Unknown';
              const { data: owner } = await supabase
                .from('users')
                .select('full_name')
                .eq('id', leadRow.created_by)
                .maybeSingle();
              if (owner?.full_name) ownerName = owner.full_name;
              const agentName = profile.full_name || 'Unknown agent';
              updates = {
                ...updates,
                split_applies: true,
                split_note: `Closed by ${agentName} — original owner: ${ownerName}. 50/50 split applies.`,
              };
            }
          }
        }
      }

      const { data, error } = await supabase.from('leads').update(updates).eq('id', id).select().single();
      if (error) throw error;

      if (
        updates.status !== undefined &&
        priorRow &&
        data?.status &&
        data.status !== priorRow.status &&
        data.agency_id
      ) {
        try {
          await api.enrollments.checkAndAutoEnroll(id, data.agency_id, data.status);
        } catch (autoErr) {
          console.error('[api.leads.update] checkAndAutoEnroll failed', autoErr);
        }
      }

      return data;
    },
    archive: async (id) => {
      if (useMock) return null;
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile) throw new Error('No profile');
      const { data, error } = await supabase
        .from('leads')
        .update({ archived_at: new Date().toISOString(), archived_by: profile.id })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    unarchive: async (id) => {
      if (useMock) return null;
      const { data, error } = await supabase
        .from('leads')
        .update({ archived_at: null, archived_by: null })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    delete: async (id) => {
      if (useMock) return null;
      const { error } = await supabase.from('leads').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
    /**
     * Replace setter/closer rows for a lead (manager, admin, or super_admin only — enforced in RLS).
     * Pass null/empty ids to clear a role.
     */
    setAssignments: async (leadId, { setterUserId, closerUserId }) => {
      if (useMock) {
        return localDB.setLeadAssignments(currentMockUserId, leadId, {
          setterUserId,
          closerUserId,
        });
      }
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) throw new Error('No agency profile');
      const privileged = ['manager', 'admin', 'super_admin'].includes(profile.role);
      if (!privileged) throw new Error('Only managers and admins can assign agents');

      const { data: lead, error: leadErr } = await supabase
        .from('leads')
        .select('id, agency_id')
        .eq('id', leadId)
        .single();
      if (leadErr) throw leadErr;
      if (!lead || lead.agency_id !== profile.agency_id) {
        throw new Error('Lead not found or not in your agency');
      }

      const norm = (v) => {
        const s = v != null ? String(v).trim() : '';
        return s ? s : null;
      };
      const setterId = norm(setterUserId);
      const closerId = norm(closerUserId);

      const pickIds = [setterId, closerId].filter(Boolean);
      if (pickIds.length) {
        const { data: validRows, error: vErr } = await supabase
          .from('users')
          .select('id')
          .eq('agency_id', lead.agency_id)
          .in('id', pickIds);
        if (vErr) throw vErr;
        const ok = new Set((validRows || []).map((r) => r.id));
        for (const pid of pickIds) {
          if (!ok.has(pid)) throw new Error('Each assigned agent must belong to your agency');
        }
      }

      const { error: delErr } = await supabase.from('lead_assignments').delete().eq('lead_id', leadId);
      if (delErr) throw delErr;

      const rows = [];
      if (setterId) {
        rows.push({
          lead_id: leadId,
          user_id: setterId,
          agency_id: lead.agency_id,
          assignment_role: 'setter',
        });
      }
      if (closerId) {
        rows.push({
          lead_id: leadId,
          user_id: closerId,
          agency_id: lead.agency_id,
          assignment_role: 'closer',
        });
      }
      if (rows.length) {
        const { error: insErr } = await supabase.from('lead_assignments').insert(rows);
        if (insErr) throw insErr;
      }

      return api.leads.getById(leadId);
    },
    /**
     * Set or clear the lead co-worker (lead owner or admin/super_admin only — enforced in RLS).
     */
    updateCoWorker: async (leadId, coWorkerId) => {
      if (useMock) {
        return localDB.updateLeadCoWorker(currentMockUserId, leadId, coWorkerId);
      }
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data: lead, error: leadErr } = await supabase
        .from('leads')
        .select('id, agency_id, created_by')
        .eq('id', leadId)
        .single();
      if (leadErr) throw leadErr;
      if (!lead || lead.agency_id !== profile.agency_id) {
        throw new Error('Lead not found or not in your agency');
      }

      const isOwner = lead.created_by === profile.id;
      const isAdmin = ['admin', 'super_admin'].includes(profile.role);
      if (!isOwner && !isAdmin) {
        throw new Error('Only the lead owner or an admin can change the co-worker');
      }

      const norm = (v) => {
        const s = v != null ? String(v).trim() : '';
        return s ? s : null;
      };
      const workerId = norm(coWorkerId);

      if (workerId) {
        const { data: validRow, error: vErr } = await supabase
          .from('users')
          .select('id')
          .eq('agency_id', lead.agency_id)
          .eq('id', workerId)
          .maybeSingle();
        if (vErr) throw vErr;
        if (!validRow) throw new Error('Co-worker must be an active user in your agency');
      }

      const { error } = await supabase
        .from('leads')
        .update({ co_worker_id: workerId })
        .eq('id', leadId);
      if (error) throw error;

      return api.leads.getById(leadId);
    },
    /**
     * Find active, non-merged leads in the same agency with matching phone or email.
     * Phone match uses last-10 digits (not raw string equality).
     */
    findDuplicates: async (leadId, agencyId) => {
      if (useMock) return [];
      const { data: lead, error: leadErr } = await supabase
        .from('leads')
        .select('id, phone, email, agency_id')
        .eq('id', leadId)
        .single();
      if (leadErr) throw leadErr;
      if (!lead || lead.agency_id !== agencyId) return [];

      const phone = lead.phone?.trim() || null;
      const email = lead.email?.trim() || null;
      if (!phone && !email) return [];

      const byId = new Map();
      const mergeRows = (rows) => {
        (rows || []).forEach((row) => {
          if (row?.id) byId.set(row.id, row);
        });
      };

      const selectCols = 'id, first_name, last_name, phone, phone_secondary, email, lead_type, status, created_at';

      if (phone) {
        const orFilter = phoneLeadOrFilter(phone);
        if (orFilter) {
          const { data, error } = await supabase
            .from('leads')
            .select(selectCols)
            .eq('agency_id', agencyId)
            .neq('id', leadId)
            .is('archived_at', null)
            .is('merged_into', null)
            .or(orFilter)
            .order('created_at', { ascending: false });
          if (error) throw error;
          mergeRows(
            (data || []).filter(
              (row) => phonesMatch(row.phone, phone) || phonesMatch(row.phone_secondary, phone),
            ),
          );
        }

        // Fallback: last-10 scan for oddball stored formats
        if (!byId.size) {
          const { data: candidates, error: scanErr } = await supabase
            .from('leads')
            .select(selectCols)
            .eq('agency_id', agencyId)
            .neq('id', leadId)
            .is('archived_at', null)
            .is('merged_into', null)
            .or('phone.not.is.null,phone_secondary.not.is.null');
          if (scanErr) throw scanErr;
          mergeRows(
            (candidates || []).filter(
              (row) => phonesMatch(row.phone, phone) || phonesMatch(row.phone_secondary, phone),
            ),
          );
        }
      }

      if (email) {
        const { data, error } = await supabase
          .from('leads')
          .select(selectCols)
          .eq('agency_id', agencyId)
          .neq('id', leadId)
          .is('archived_at', null)
          .is('merged_into', null)
          .eq('email', email)
          .order('created_at', { ascending: false });
        if (error) throw error;
        mergeRows(data);
      }

      return [...byId.values()].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );
    },
    /**
     * Merge absorbed lead into surviving lead: apply field choices, reassign history, archive absorbed.
     * fieldChoices: { field: 'A'|'B' } where A = surviving, B = absorbed.
     */
    merge: async ({ survivingId, absorbedId, fieldChoices, userId, agencyId }) => {
      if (useMock) throw new Error('Lead merge is not available in offline mode');

      const [survRes, absRes] = await Promise.all([
        supabase.from('leads').select('*').eq('id', survivingId).eq('agency_id', agencyId).single(),
        supabase.from('leads').select('*').eq('id', absorbedId).eq('agency_id', agencyId).single(),
      ]);
      if (survRes.error) throw survRes.error;
      if (absRes.error) throw absRes.error;
      const survivingLead = survRes.data;
      const absorbedLead = absRes.data;
      if (!survivingLead || !absorbedLead) {
        throw new Error('One or both leads were not found in your agency');
      }

      const mergedFields = {};
      for (const [key, choice] of Object.entries(fieldChoices || {})) {
        if (choice === 'A') mergedFields[key] = survivingLead[key];
        else if (choice === 'B') mergedFields[key] = absorbedLead[key];
      }

      if (Object.keys(mergedFields).length > 0) {
        const { error: updErr } = await supabase
          .from('leads')
          .update(mergedFields)
          .eq('id', survivingId)
          .eq('agency_id', agencyId);
        if (updErr) throw updErr;
      }

      const historyTables = [
        'call_logs',
        'sms_logs',
        'sms_messages',
        'appointments',
        'activity_logs',
        'email_logs',
        'workspace_sessions',
      ];
      for (const table of historyTables) {
        const { error: reassignErr } = await supabase
          .from(table)
          .update({ lead_id: survivingId })
          .eq('lead_id', absorbedId)
          .eq('agency_id', agencyId);
        if (reassignErr) throw reassignErr;
      }

      const absorbedName = [absorbedLead.first_name, absorbedLead.last_name].filter(Boolean).join(' ') || 'Unknown';
      const notes = `Merged with ${absorbedName} (phone: ${absorbedLead.phone ?? '—'}, email: ${absorbedLead.email ?? '—'})`;

      const { error: logErr } = await supabase.from('activity_logs').insert({
        lead_id: survivingId,
        agency_id: agencyId,
        agent_id: userId,
        activity_type: 'merge',
        activity_date: toLocalDateKey(new Date()),
        notes,
      });
      if (logErr) throw logErr;

      const now = new Date().toISOString();
      const { error: archiveErr } = await supabase
        .from('leads')
        .update({
          merged_into: survivingId,
          merged_at: now,
          merged_by: userId,
          archived_at: now,
          archived_by: userId,
        })
        .eq('id', absorbedId)
        .eq('agency_id', agencyId);
      if (archiveErr) throw archiveErr;

      return api.leads.getById(survivingId);
    },
  },
  pipelines: {
    getAll: async () => {
      if (useMock) return localDB.getPipelines(currentMockUserId);
      const { data, error } = await supabase.from('pipelines').select('*');
      if (error) throw error;
      return data;
    },
  },
  callLogs: {
    /** Aggregate dialing stats from `call_logs` for a lead (not denormalized `leads` columns). */
    getDialingStatsForLead: async (leadId) => {
      if (useMock) return localDB.getDialingStatsForLead(currentMockUserId, leadId);
      const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      const [countRes, recentRes, lastRes] = await Promise.all([
        supabase.from('call_logs').select('id', { count: 'exact', head: true }).eq('lead_id', leadId),
        supabase
          .from('call_logs')
          .select('id', { count: 'exact', head: true })
          .eq('lead_id', leadId)
          .gte('called_at', fiveMinAgo),
        supabase
          .from('call_logs')
          .select('called_at')
          .eq('lead_id', leadId)
          .order('called_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      if (countRes.error) throw countRes.error;
      if (recentRes.error) throw recentRes.error;
      if (lastRes.error) throw lastRes.error;
      return {
        totalCalls: countRes.count ?? 0,
        callsLast5Min: recentRes.count ?? 0,
        lastCalledAt: lastRes.data?.called_at ?? null,
      };
    },
  },
  calls: {
    log: async (leadId, callData) => {
      if (useMock) return localDB.logCall(currentMockUserId, leadId, callData);
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('call_logs')
        .insert({
          ...callData,
          called_at: normalizeCalledAt(callData.called_at),
          lead_id: leadId,
          agency_id: profile.agency_id,
          agent_id: profile.id,
        })
        .select()
        .single();
      if (error) throw error;
      if (callData.room_type) {
        await applyRoomTypeToCallAndLead(data.id, leadId, callData.room_type);
      }
      return data;
    },
    /**
     * Sends SMS via Edge Function (`twilio-sms` or `telnyx-sms`). The function logs to `sms_logs`.
     */
    sendSms: async ({ to, message, lead_id, agency_id, preferredDialer }) => {
      if (useMock) {
        return localDB.logSms(currentMockUserId, {
          to,
          message,
          lead_id,
          agency_id,
          provider: preferredDialer === 'telnyx' ? 'telnyx' : 'twilio',
        });
      }
      const fn = preferredDialer === 'telnyx' ? 'telnyx-sms' : 'twilio-sms';
      const { data, error } = await supabase.functions.invoke(fn, {
        body: { to, message, lead_id, agency_id },
      });
      if (error) {
        let msg = error.message || 'Could not send SMS';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }
      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Could not send SMS');
      }
      return data;
    },
    /**
     * Persist end-of-call row for Telnyx WebRTC; upserts when `telnyx_call_control_id` is present
     * so webhooks and the browser do not create duplicates.
     */
    logTelnyxCallEnd: async (leadId, callData) => {
      if (useMock) return localDB.logCall(currentMockUserId, leadId, callData);
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const base = {
        ...callData,
        called_at: normalizeCalledAt(callData.called_at),
        lead_id: leadId,
        agency_id: profile.agency_id,
        agent_id: profile.id,
      };

      if (callData.telnyx_call_control_id) {
        const { data, error } = await supabase
          .from('call_logs')
          .upsert(base, { onConflict: 'telnyx_call_control_id' })
          .select()
          .single();
        if (error) throw error;
        return data;
      }

      const { data, error } = await supabase.from('call_logs').insert(base).select().single();
      if (error) throw error;
      return data;
    },
    /** Patch an existing call log (e.g. post-call outcome and notes). */
    updateLog: async (callLogId, patch) => {
      if (useMock) return localDB.updateCallLog(currentMockUserId, callLogId, patch);
      const { data, error } = await supabase
        .from('call_logs')
        .update(patch)
        .eq('id', callLogId)
        .select('*, lead_id')
        .single();
      if (error) throw error;
      if (patch.room_type && data?.lead_id) {
        await applyRoomTypeToCallAndLead(null, data.lead_id, patch.room_type);
      }
      return data;
    },
  },
  telnyx: {
    getWebRtcToken: async () => {
      if (useMock) throw new Error('Telnyx softphone requires Supabase (not available in mock mode).');
      const { data, error } = await supabase.functions.invoke('telnyx-token', { body: {} });
      if (error) {
        let msg = error.message || 'Could not get Telnyx token';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }
      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Could not get Telnyx token');
      }
      return data;
    },
    /**
     * Mints a Twilio Voice Access Token via the `twilio-token` Edge Function.
     * Lives under `api.telnyx` for parity with `getWebRtcToken()` so the Softphone has a
     * single namespace for "browser dialer" credentials regardless of provider.
     */
    getTwilioToken: async () => {
      if (useMock) throw new Error('Twilio softphone requires Supabase (not available in mock mode).');
      const { data, error } = await supabase.functions.invoke('twilio-token', { body: {} });
      if (error) {
        let msg = error.message || 'Could not get Twilio token';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        console.error('[api.telnyx.getTwilioToken] Edge Function error', { message: msg, raw: error });
        throw new Error(msg);
      }
      if (data?.error) {
        console.error('[api.telnyx.getTwilioToken] Edge Function returned error body', data);
        throw new Error(typeof data.error === 'string' ? data.error : 'Could not get Twilio token');
      }
      // Surface the response shape (minus the JWT body) so a misconfigured signing mode or
      // missing TwiML app is obvious in the browser console without copying the token.
      console.log('[api.telnyx.getTwilioToken] response', {
        identity: data?.identity,
        caller_id_number: data?.caller_id_number,
        twiml_app_configured: data?.twiml_app_configured,
        signing_mode: data?.signing_mode,
        ttl: data?.ttl,
        token_length: data?.token?.length ?? 0,
        token_prefix: typeof data?.token === 'string' ? `${data.token.slice(0, 12)}…` : null,
      });
      return data;
    },
  },
  /**
   * Agency-wide display-only roster via SECURITY DEFINER RPC.
   * Returns { id, full_name, avatar_url, role, is_active } — no email/phone/assignments.
   * Use for name/avatar resolution and picker labels; keep users.listForAgency for admin fields.
   */
  usersDirectory: {
    list: async () => {
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        return (db.users || []).map((u) => ({
          id: u.id,
          full_name: u.full_name ?? null,
          avatar_url: u.headshot_url ?? u.avatar_url ?? null,
          role: u.role ?? null,
          is_active: u.is_active !== false,
        }));
      }
      const { data, error } = await supabase.rpc('get_agency_users_directory');
      if (error) throw error;
      return data ?? [];
    },
  },

  /**
   * Spouse / Assistant / Partner links (user_connections).
   * Insert/delete RLS: manager, admin, super_admin. Select: same agency.
   * Display names come from the directory resolver (no users! embed).
   */
  userConnections: {
    list: async () => {
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        return db.user_connections || [];
      }
      const { data, error } = await supabase
        .from('user_connections')
        .select('id, agency_id, user_id_a, user_id_b, connection_type, created_by, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      const { loadUsersDirectory, resolveDisplayName } = await import('./usersDirectory.js');
      await loadUsersDirectory();
      return (data || []).map((row) => ({
        ...row,
        user_a_name: resolveDisplayName(row.user_id_a) || null,
        user_b_name: resolveDisplayName(row.user_id_b) || null,
      }));
    },

    create: async ({ userIdA, userIdB, connectionType }) => {
      if (!userIdA || !userIdB) throw new Error('Both users are required');
      if (userIdA === userIdB) throw new Error('Cannot connect a user to themselves');
      const type = String(connectionType || '').toLowerCase();
      if (!['spouse', 'assistant', 'partner'].includes(type)) {
        throw new Error('Connection type must be spouse, assistant, or partner');
      }
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        const row = {
          id: crypto.randomUUID(),
          agency_id: null,
          user_id_a: userIdA,
          user_id_b: userIdB,
          connection_type: type,
          created_by: currentMockUserId,
          created_at: new Date().toISOString(),
        };
        db.user_connections = [...(db.user_connections || []), row];
        localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
        return row;
      }
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not signed in');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) throw new Error('No agency on profile');
      const { data, error } = await supabase
        .from('user_connections')
        .insert({
          agency_id: profile.agency_id,
          user_id_a: userIdA,
          user_id_b: userIdB,
          connection_type: type,
          created_by: authId,
        })
        .select('id, agency_id, user_id_a, user_id_b, connection_type, created_by, created_at')
        .single();
      if (error) throw error;
      const { loadUsersDirectory, resolveDisplayName } = await import('./usersDirectory.js');
      await loadUsersDirectory();
      return {
        ...data,
        user_a_name: resolveDisplayName(data.user_id_a) || null,
        user_b_name: resolveDisplayName(data.user_id_b) || null,
      };
    },

    remove: async (connectionId) => {
      if (!connectionId) throw new Error('Connection id is required');
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        db.user_connections = (db.user_connections || []).filter((c) => c.id !== connectionId);
        localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
        return true;
      }
      const { error } = await supabase
        .from('user_connections')
        .delete()
        .eq('id', connectionId);
      if (error) throw error;
      return true;
    },
  },

  users: {
    /** Same-agency roster; RLS restricts rows to the caller's agency. */
    listForAgency: async (agencyId) => {
      if (!agencyId) return [];
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        return (db.users || []).filter((u) => u.agency_id === agencyId);
      }
      const { data, error } = await supabase
        .from('users')
        .select('id, agency_id, email, full_name, role, is_active, is_licensed, created_at, headshot_url, title, trainer_id, marketing_director_id, is_marketing_director, archived_at, archived_by')
        .eq('agency_id', agencyId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },

    /**
     * Users who report to the signed-in user as trainer or marketing director.
     * Mirrors SQL get_my_team_user_ids() / is_team_member_of().
     */
    getMyTeam: async () => {
      if (useMock) return [];
      const authId = await getSessionUserId();
      if (!authId) return [];
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) return [];

      const { data, error } = await supabase
        .from('users')
        .select('id, agency_id, email, full_name, role, is_active, trainer_id, marketing_director_id, headshot_url, title')
        .eq('agency_id', profile.agency_id)
        .or(`trainer_id.eq.${authId},marketing_director_id.eq.${authId}`)
        .is('archived_at', null)
        .order('full_name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    /**
     * Build a lookup map of (lowercased) email → user_id for users in the current
     * user's agency whose email matches one of the supplied addresses.
     */
    findIdsByEmails: async (emails) => {
      const cleaned = [...new Set(
        (emails || [])
          .map((e) => String(e ?? '').trim().toLowerCase())
          .filter(Boolean),
      )];
      if (cleaned.length === 0) return {};
      if (useMock) return {};
      const authId = await getSessionUserId();
      if (!authId) return {};
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) return {};

      const wanted = new Set(cleaned);
      const { data, error } = await supabase
        .from('users')
        .select('id, email')
        .eq('agency_id', profile.agency_id);
      if (error) throw error;

      const map = {};
      (data || []).forEach((u) => {
        const key = String(u.email ?? '').trim().toLowerCase();
        if (key && wanted.has(key)) map[key] = u.id;
      });
      return map;
    },
    updateRole: async (userId, role) => {
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        const idx = (db.users || []).findIndex((u) => u.id === userId);
        if (idx > -1) {
          db.users[idx].role = role;
          localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
          return db.users[idx];
        }
        return null;
      }
      const { data, error } = await supabase.from('users').update({ role }).eq('id', userId).select().single();
      if (error) throw error;
      return data;
    },
    /**
     * Per-agent preferred browser dialer. RLS allows an agent to update only their own row
     * via `authenticated_users_update_own_profile`, so admins changing someone else's
     * preference would have to do so via service-role tooling.
     */
    /**
     * Update the signed-in user's profile fields (own row only via RLS).
     * Supports default_channel ('IM' | 'AM' | 'LD' | null) and other allowed columns.
     */
    updateProfile: async (userId, data) => {
      if (!userId) throw new Error('User id is required');
      const updates = { ...data };
      if ('default_channel' in updates) {
        const ch = updates.default_channel;
        if (ch !== null && ch !== undefined && ch !== '' && ch !== 'IM' && ch !== 'AM' && ch !== 'LD') {
          throw new Error(`Invalid default_channel: ${ch}`);
        }
        updates.default_channel = ch === '' || ch === undefined ? null : ch;
      }
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        const idx = (db.users || []).findIndex((u) => u.id === userId);
        if (idx > -1) {
          db.users[idx] = { ...db.users[idx], ...updates };
          localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
          return db.users[idx];
        }
        return null;
      }
      const { data: row, error } = await supabase.from('users').update(updates).eq('id', userId).select().single();
      if (error) throw error;
      return row;
    },
    updatePreferredDialer: async (userId, preferredDialer) => {
      if (preferredDialer !== 'telnyx' && preferredDialer !== 'twilio') {
        throw new Error(`Invalid preferred_dialer: ${preferredDialer}`);
      }
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        const idx = (db.users || []).findIndex((u) => u.id === userId);
        if (idx > -1) {
          db.users[idx].preferred_dialer = preferredDialer;
          localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
          return db.users[idx];
        }
        return null;
      }
      const { data, error } = await supabase
        .from('users')
        .update({ preferred_dialer: preferredDialer })
        .eq('id', userId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    setActive: async (userId, isActive) => {
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        const idx = (db.users || []).findIndex((u) => u.id === userId);
        if (idx > -1) {
          db.users[idx].is_active = isActive;
          localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
          return db.users[idx];
        }
        return null;
      }
      const { data, error } = await supabase.from('users').update({ is_active: isActive }).eq('id', userId).select().single();
      if (error) throw error;
      return data;
    },
    /**
     * Sends branded invite email via Brevo and inserts a placeholder `users` row.
     */
    invite: async ({ email, full_name, role, agency_id }) => {
      if (!agency_id) throw new Error('Agency is required');
      if (useMock) {
        const id = crypto.randomUUID();
        const newUser = {
          id,
          agency_id,
          email: email.trim(),
          full_name: full_name.trim(),
          role,
          is_active: false,
          created_at: new Date().toISOString(),
        };
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        db.users = [...(db.users || []), newUser];
        localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
        return newUser;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const inviteData = {
        email: email.trim(),
        full_name: full_name.trim(),
        role,
        agency_id,
      };

      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: inviteData,
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        let msg = error.message || 'Invitation failed';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }

      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Invitation failed');
      }
      if (!data?.user) throw new Error('Invitation failed');
      return data.user;
    },
    /**
     * Resends the invite email for a user who has not completed signup.
     */
    resendInvite: async (userId) => {
      if (!userId) throw new Error('User is required');
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        const idx = (db.users || []).findIndex((u) => u.id === userId);
        if (idx < 0) throw new Error('User not found');
        if (db.users[idx].is_active) throw new Error('User has already completed signup');
        db.users[idx].last_invited_at = new Date().toISOString();
        localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
        return db.users[idx];
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: { resend: true, user_id: userId },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        let msg = error.message || 'Resend invite failed';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }

      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Resend invite failed');
      }
      if (!data?.user) throw new Error('Resend invite failed');
      return data.user;
    },
    archive: async (userId, archivedBy) => {
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        const idx = (db.users || []).findIndex((u) => u.id === userId);
        if (idx > -1) {
          db.users[idx] = {
            ...db.users[idx],
            archived_at: new Date().toISOString(),
            archived_by: archivedBy,
            is_active: false,
          };
          localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
          return db.users[idx];
        }
        return null;
      }
      const { data, error } = await supabase
        .from('users')
        .update({ archived_at: new Date().toISOString(), archived_by: archivedBy, is_active: false })
        .eq('id', userId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    unarchive: async (userId) => {
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        const idx = (db.users || []).findIndex((u) => u.id === userId);
        if (idx > -1) {
          db.users[idx] = {
            ...db.users[idx],
            archived_at: null,
            archived_by: null,
            is_active: true,
          };
          localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
          return db.users[idx];
        }
        return null;
      }
      const { data, error } = await supabase
        .from('users')
        .update({ archived_at: null, archived_by: null, is_active: true })
        .eq('id', userId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    deleteWithBackup: async ({ targetUser, deletedByUser, agencyId }) => {
      if (!targetUser?.id) throw new Error('Target user is required');
      if (!deletedByUser?.id) throw new Error('Deleted-by user is required');
      if (!agencyId) throw new Error('Agency is required');

      const newOwnerId = targetUser.marketing_director_id || deletedByUser.id;

      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        const leads = (db.leads || []).filter(
          (l) => l.created_by === targetUser.id && !l.archived_at,
        );
        db.users = (db.users || []).filter((u) => u.id !== targetUser.id);
        localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
        return {
          success: true,
          leadsTransferred: leads.length,
          newOwnerName: deletedByUser.full_name || deletedByUser.email || 'Unknown',
        };
      }

      const { data: newOwner, error: ownerErr } = await supabase
        .from('users')
        .select('full_name, email')
        .eq('id', newOwnerId)
        .single();
      if (ownerErr) throw ownerErr;

      const newOwnerName = newOwner?.full_name?.trim() || newOwner?.email || 'Unknown';
      const newOwnerEmail = newOwner?.email || '';

      const countFor = async (table, column) => {
        const { count, error } = await supabase
          .from(table)
          .select('*', { count: 'exact', head: true })
          .eq(column, targetUser.id);
        if (error) throw error;
        return count ?? 0;
      };

      const [callLogCount, smsCount, activityLogCount, leadCountResult] = await Promise.all([
        countFor('call_logs', 'agent_id'),
        countFor('sms_logs', 'agent_id'),
        countFor('activity_logs', 'agent_id'),
        supabase
          .from('leads')
          .select('*', { count: 'exact', head: true })
          .eq('created_by', targetUser.id)
          .is('archived_at', null),
      ]);

      if (leadCountResult.error) throw leadCountResult.error;
      const leadCount = leadCountResult.count ?? 0;

      const { error: archiveInsertErr } = await supabase.from('deleted_users_archive').insert({
        agency_id: agencyId,
        deleted_by: deletedByUser.id,
        auth_email: targetUser.email,
        user_snapshot: targetUser,
        lead_count: leadCount,
        call_log_count: callLogCount,
        sms_count: smsCount,
        activity_log_count: activityLogCount,
        notes: `Deleted by ${deletedByUser.full_name} on ${new Date().toISOString()}`,
      });
      if (archiveInsertErr) throw archiveInsertErr;

      const { data: leads, error: leadsErr } = await supabase
        .from('leads')
        .select('id')
        .eq('created_by', targetUser.id)
        .is('archived_at', null);
      if (leadsErr) throw leadsErr;

      const transferredAt = new Date().toISOString();
      const transferPayload = {
        created_by: newOwnerId,
        previous_owner_id: targetUser.id,
        previous_owner_name: targetUser.full_name,
        previous_owner_email: targetUser.email,
        ownership_transferred_at: transferredAt,
      };

      if (leads?.length) {
        const { error: transferErr } = await supabase
          .from('leads')
          .update(transferPayload)
          .eq('created_by', targetUser.id)
          .is('archived_at', null);
        if (transferErr) throw transferErr;

        const activityDate = new Date().toISOString().split('T')[0];
        const activityNote =
          `Lead ownership transferred from ${targetUser.full_name} (${targetUser.email}) ` +
          `to ${newOwnerName} (${newOwnerEmail}) due to account deletion.`;

        const { error: activityErr } = await supabase.from('activity_logs').insert(
          leads.map((lead) => ({
            agency_id: agencyId,
            lead_id: lead.id,
            agent_id: deletedByUser.id,
            activity_type: 'manual_calls',
            activity_date: activityDate,
            notes: activityNote,
          })),
        );
        if (activityErr) throw activityErr;
      }

      const { data: { session } } = await supabase.auth.getSession();

      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { target_user_id: targetUser.id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || 'Failed to delete user');
      }

      return {
        success: true,
        leadsTransferred: leads?.length ?? 0,
        newOwnerName,
      };
    },
  },
  email: {
    /**
     * Sends transactional email via the brevo-email Edge Function.
     * `html_body` (or `bodyHtml`) is sent to Brevo as-is as the HTML content.
     */
    send: async ({ to_email, to_name, subject, html_body, bodyHtml, reply_to }) => {
      const html = typeof html_body === 'string' ? html_body : bodyHtml;
      if (typeof html !== 'string' || !html.trim()) {
        throw new Error('Email body is required');
      }

      if (useMock) {
        console.log('[mock] email.send', { to_email, subject });
        return { success: true, messageId: `mock-${crypto.randomUUID()}` };
      }

      const { data, error } = await supabase.functions.invoke('brevo-email', {
        body: { to_email, to_name, subject, html_body: html, reply_to },
      });

      if (error) {
        let msg = error.message || 'Could not send email';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }

      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Could not send email');
      }
      if (!data?.success) {
        throw new Error('Could not send email');
      }
      return data;
    },
  },
  emailLogs: {
    create: async (logData) => {
      if (useMock) {
        return localDB.logEmail(currentMockUserId, logData);
      }
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('email_logs')
        .insert({
          agency_id: profile.agency_id,
          agent_id: profile.id,
          lead_id: logData.lead_id,
          to_email: logData.to_email,
          subject: logData.subject,
          body_html: logData.body_html,
          direction: logData.direction || 'outbound',
          status: logData.status || 'sent',
          send_method: 'brevo',
          brevo_message_id: logData.brevo_message_id || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    /**
     * Records an email the agent sent manually from Gmail (not tracked by Brevo).
     * `trackingToken` (optional, client-generated UUID) is the open-pixel token;
     * build the pixel URL with buildEmailOpenPixelUrl.
     * Throws when RLS denies the insert (caller cannot view the lead).
     */
    logGmailManualEmail: async ({ leadId, subject, bodyHtml, toEmail, trackingToken }) => {
      if (!leadId) throw new Error('leadId is required');
      const to = typeof toEmail === 'string' ? toEmail.trim() : '';
      if (!to) throw new Error('toEmail is required');
      const subj = typeof subject === 'string' ? subject.trim() : '';
      if (!subj) throw new Error('subject is required');
      let token = null;
      if (trackingToken != null && trackingToken !== '') {
        token = typeof trackingToken === 'string' ? trackingToken.trim() : '';
        if (!UUID_RE.test(token)) throw new Error('trackingToken must be a UUID');
      }

      if (useMock) {
        const log = await localDB.logEmail(currentMockUserId, {
          lead_id: leadId,
          to_email: to,
          subject: subj,
          body_html: bodyHtml ?? '',
          status: 'sent_gmail',
        });
        return normalizeEmailLogRow({ ...log, send_method: 'gmail_manual', tracking_token: token });
      }

      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('email_logs')
        .insert({
          agency_id: profile.agency_id,
          agent_id: profile.id,
          lead_id: leadId,
          to_email: to,
          subject: subj,
          body_html: bodyHtml ?? '',
          direction: 'outbound',
          status: 'sent_gmail',
          send_method: 'gmail_manual',
          brevo_message_id: null,
          tracking_token: token,
        })
        .select(EMAIL_LOG_HISTORY_COLUMNS)
        .single();
      if (error) {
        if (isRlsInsertDenied(error)) {
          const denied = new Error(
            "You don't have access to this lead, so this Gmail email can't be logged on it.",
          );
          denied.code = error.code;
          denied.cause = error;
          throw denied;
        }
        if (error.code === '23505' && token) {
          const dup = new Error('This Gmail email was already logged.');
          dup.code = error.code;
          dup.cause = error;
          throw dup;
        }
        throw error;
      }
      return normalizeEmailLogRow(data);
    },
    getByLead: async (leadId) => {
      if (useMock) {
        const rows = await localDB.getEmailLogsByLead(currentMockUserId, leadId);
        return (rows ?? []).map(normalizeEmailLogRow);
      }
      const { data, error } = await supabase
        .from('email_logs')
        .select(`${EMAIL_LOG_HISTORY_COLUMNS}, user:users!agent_id(id, full_name, email)`)
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map(normalizeEmailLogRow);
    },
    /** One row per lead with email activity, sorted by most recent email. */
    getConversations: async () => {
      if (useMock) {
        return localDB.getEmailConversations(currentMockUserId);
      }
      const { data, error } = await supabase
        .from('email_logs')
        .select('*, user:users!agent_id(id, full_name, email), lead:leads(id, first_name, last_name, email)')
        .order('created_at', { ascending: false });
      if (error) throw error;

      const grouped = {};
      for (const log of data || []) {
        if (!log.lead_id || grouped[log.lead_id]) continue;
        grouped[log.lead_id] = {
          lead_id: log.lead_id,
          lead: log.lead,
          subject: log.subject,
          created_at: log.created_at,
          to_email: log.to_email,
          agent: log.user,
        };
      }
      return Object.values(grouped);
    },
  },
  /**
   * Emails queued for a future send_at; sent by the send-scheduled-emails worker.
   * Clients may only cancel or reschedule rows that are still pending.
   */
  scheduledEmails: {
    create: async ({ leadId, toEmail, subject, bodyHtml, sendAt }) => {
      if (useMock) throw new Error('Scheduled emails require Supabase.');
      if (!leadId) throw new Error('leadId is required');
      const to = typeof toEmail === 'string' ? toEmail.trim() : '';
      if (!to) throw new Error('toEmail is required');
      const subj = typeof subject === 'string' ? subject.trim() : '';
      if (!subj) throw new Error('subject is required');
      if (typeof bodyHtml !== 'string' || !bodyHtml.trim()) throw new Error('Email body is required');
      const sendAtIso = toSendAtIso(sendAt);

      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('scheduled_emails')
        .insert({
          agency_id: profile.agency_id,
          lead_id: leadId,
          created_by: profile.id,
          to_email: to,
          subject: subj,
          body_html: bodyHtml,
          send_at: sendAtIso,
          status: 'pending',
        })
        .select(SCHEDULED_EMAIL_LIST_COLUMNS)
        .single();
      if (error) {
        if (isRlsInsertDenied(error)) {
          const denied = new Error(
            "You don't have access to this lead, so an email can't be scheduled on it.",
          );
          denied.code = error.code;
          denied.cause = error;
          throw denied;
        }
        throw error;
      }
      return data;
    },
    listByLead: async (leadId) => {
      if (useMock) return [];
      if (!leadId) throw new Error('leadId is required');
      const { data, error } = await supabase
        .from('scheduled_emails')
        .select(SCHEDULED_EMAIL_LIST_COLUMNS)
        .eq('lead_id', leadId)
        .order('send_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    cancel: async (id) => updatePendingScheduledEmail(id, { status: 'cancelled' }),
    reschedule: async (id, sendAt) =>
      updatePendingScheduledEmail(id, { send_at: toSendAtIso(sendAt) }),
  },
  workspaceSessions: {
    create: async (sessionData) => {
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('workspace_sessions')
        .insert({
          ...sessionData,
          agency_id: profile.agency_id,
          agent_id: profile.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    update: async (id, updates) => {
      const { data, error } = await supabase
        .from('workspace_sessions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    getByLead: async (leadId) => {
      const { data, error } = await supabase
        .from('workspace_sessions')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    getById: async (id) => {
      const { data, error } = await supabase
        .from('workspace_sessions')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data;
    },
  },
  /**
   * Newest-first, row-capped reads for the lead detail activity feed. Each one
   * mirrors the table and select of the matching lead tab so RLS yields the
   * same rows; only ordering and the cap differ.
   */
  leadActivityFeed: {
    smsMessages: async (leadId, limit) => {
      if (useMock || !leadId) return [];
      const { data, error } = await supabase
        .from('sms_messages')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
    emailLogs: async (leadId, limit) => {
      if (useMock || !leadId) return [];
      const { data, error } = await supabase
        .from('email_logs')
        .select(`${EMAIL_LOG_HISTORY_COLUMNS}, user:users!agent_id(id, full_name, email)`)
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []).map(normalizeEmailLogRow);
    },
    /** Ordered by updated_at: every reminder event (set/completed/dismissed) happens at or before it. */
    reminders: async (leadId, limit) => {
      if (useMock || !leadId) return [];
      const { data, error } = await supabase
        .from('reminders')
        .select(api.reminders._select)
        .eq('lead_id', leadId)
        .order('updated_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
    enrollments: async (leadId, limit) => {
      if (useMock || !leadId) return [];
      const { data, error } = await supabase
        .from('email_sequence_enrollments')
        .select(
          `*,
          email_sequences (
            id,
            name,
            email_sequence_steps (
              step_number,
              subject,
              delay_days
            )
          ),
          enrolled_by_user:users!email_sequence_enrollments_enrolled_by_fkey (
            id,
            full_name
          )`,
        )
        .eq('lead_id', leadId)
        .order('enrolled_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []).map(normalizeEnrollmentRow);
    },
    workspaceSessions: async (leadId, limit) => {
      if (useMock || !leadId) return [];
      const { data, error } = await supabase
        .from('workspace_sessions')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  },
  appointments: {
    create: async (appointmentData) => {
      if (useMock) throw new Error('Appointments require Supabase (not available in mock mode).');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('appointments')
        .insert({
          ...appointmentData,
          guests: appointmentData.guests ?? [],
          agency_id: appointmentData.agency_id ?? profile.agency_id,
          created_by: appointmentData.created_by ?? profile.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    update: async (id, updates) => {
      if (useMock) throw new Error('Appointments require Supabase (not available in mock mode).');
      const { data, error } = await supabase.from('appointments').update(updates).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    delete: async (id) => {
      if (useMock) throw new Error('Appointments require Supabase (not available in mock mode).');
      const { error } = await supabase.from('appointments').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
    getById: async (id) => {
      if (useMock) throw new Error('Appointments require Supabase (not available in mock mode).');
      const { data, error } = await supabase
        .from('appointments')
        .select(
          '*, lead:leads(*), creator:users!appointments_created_by_fkey(*), assignee:users!appointments_assigned_to_fkey(*)',
        )
        .eq('id', id)
        .single();
      if (error) throw error;
      return data;
    },
    getByLead: async (leadId) => {
      if (useMock) throw new Error('Appointments require Supabase (not available in mock mode).');
      const { data, error } = await supabase
        .from('appointments')
        .select(
          '*, assignee:users!appointments_assigned_to_fkey(id, full_name, email, role)',
        )
        .eq('lead_id', leadId)
        .order('scheduled_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    getUpcoming: async ({ agencyId, assignedTo, startDate, endDate }) => {
      if (useMock) throw new Error('Appointments require Supabase (not available in mock mode).');
      if (!agencyId || !startDate || !endDate) {
        throw new Error('agencyId, startDate, and endDate are required');
      }
      let q = supabase
        .from('appointments')
        .select(
          '*, lead:leads(id, first_name, last_name, email, phone, agency_id), assignee:users!appointments_assigned_to_fkey(id, full_name, email, role)',
        )
        .eq('agency_id', agencyId)
        .gte('scheduled_at', startDate)
        .lte('scheduled_at', endDate)
        .order('scheduled_at', { ascending: true });
      if (assignedTo) q = q.eq('assigned_to', assignedTo);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
    getToday: async (agencyId) => {
      if (useMock) throw new Error('Appointments require Supabase (not available in mock mode).');
      if (!agencyId) throw new Error('Agency id is required');
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const { data, error } = await supabase
        .from('appointments')
        .select(
          '*, lead:leads(id, first_name, last_name), assignee:users!appointments_assigned_to_fkey(id, full_name, email)',
        )
        .eq('agency_id', agencyId)
        .gte('scheduled_at', start.toISOString())
        .lte('scheduled_at', end.toISOString())
        .order('scheduled_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    updateStatus: async (id, status) => {
      if (useMock) throw new Error('Appointments require Supabase (not available in mock mode).');
      const { data, error } = await supabase.from('appointments').update({ status }).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
  },
  marketingIntelligence: {
    getAll: async (agencyId, filters = {}) => {
      if (useMock) throw new Error('Marketing intelligence requires Supabase (not available in mock mode).');
      let q = supabase.from('marketing_resources').select('*').eq('agency_id', agencyId);
      if (filters.resource_type) q = q.eq('resource_type', filters.resource_type);
      if (filters.category) q = q.eq('category', filters.category);
      if (filters.status) q = q.eq('status', filters.status);
      if (filters.platform) q = q.eq('platform', filters.platform);
      if (filters.traffic_type) q = q.eq('traffic_type', filters.traffic_type);
      const { data, error } = await q.order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    getById: async (id) => {
      if (useMock) throw new Error('Marketing intelligence requires Supabase (not available in mock mode).');
      const { data, error } = await supabase.from('marketing_resources').select('*').eq('id', id).single();
      if (error) throw error;
      return data;
    },
    create: async (data) => {
      if (useMock) throw new Error('Marketing intelligence requires Supabase (not available in mock mode).');
      const { data: row, error } = await supabase.from('marketing_resources').insert(data).select().single();
      if (error) throw error;
      return row;
    },
    update: async (id, updates) => {
      if (useMock) throw new Error('Marketing intelligence requires Supabase (not available in mock mode).');
      const { data, error } = await supabase.from('marketing_resources').update(updates).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    archive: async (id) => {
      if (useMock) throw new Error('Marketing intelligence requires Supabase (not available in mock mode).');
      const { data, error } = await supabase
        .from('marketing_resources')
        .update({ status: 'archived' })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    delete: async (id) => {
      if (useMock) throw new Error('Marketing intelligence requires Supabase (not available in mock mode).');
      const { error } = await supabase.from('marketing_resources').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
    links: {
      getByResource: async (resourceId) => {
        if (useMock) throw new Error('Marketing intelligence requires Supabase (not available in mock mode).');
        const { data, error } = await supabase
          .from('marketing_resource_links')
          .select('*')
          .eq('resource_id', resourceId)
          .order('created_at', { ascending: true });
        if (error) throw error;
        return data ?? [];
      },
      add: async (data) => {
        if (useMock) throw new Error('Marketing intelligence requires Supabase (not available in mock mode).');
        const { data: row, error } = await supabase
          .from('marketing_resource_links')
          .insert(data)
          .select()
          .single();
        if (error) throw error;
        return row;
      },
      update: async (id, updates) => {
        if (useMock) throw new Error('Marketing intelligence requires Supabase (not available in mock mode).');
        const { data, error } = await supabase
          .from('marketing_resource_links')
          .update(updates)
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return data;
      },
      delete: async (id) => {
        if (useMock) throw new Error('Marketing intelligence requires Supabase (not available in mock mode).');
        const { error } = await supabase.from('marketing_resource_links').delete().eq('id', id);
        if (error) throw error;
        return { success: true };
      },
    },
  },
  agentCalendarSettings: {
    get: async () => {
      if (useMock) throw new Error('Calendar settings require Supabase (not available in mock mode).');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data: existing, error: selErr } = await supabase
        .from('agent_calendar_settings')
        .select('*')
        .eq('user_id', profile.id)
        .maybeSingle();
      if (selErr) throw selErr;
      if (existing) return existing;

      const { data, error } = await supabase
        .from('agent_calendar_settings')
        .insert({ user_id: profile.id, agency_id: profile.agency_id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    update: async (updates) => {
      if (useMock) throw new Error('Calendar settings require Supabase (not available in mock mode).');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('agent_calendar_settings')
        .update(updates)
        .eq('user_id', profile.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    getForUser: async (userId) => {
      if (useMock) throw new Error('Calendar settings require Supabase (not available in mock mode).');
      const { data, error } = await supabase.from('agent_calendar_settings').select('*').eq('user_id', userId).maybeSingle();
      if (error) throw error;
      return data;
    },
    /** Same-agency booking fields only (no OAuth tokens). Requires RPC migration. */
    getBookingSnapshot: async (userId) => {
      if (useMock) throw new Error('Calendar settings require Supabase (not available in mock mode).');
      const { data, error } = await supabase.rpc('get_agent_calendar_booking_snapshot', { p_user_id: userId });
      if (error) throw error;
      return data;
    },
    getAgencySettings: async () => {
      if (useMock) throw new Error('Calendar settings require Supabase (not available in mock mode).');
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('agency_calendar_settings')
        .select('*')
        .eq('agency_id', profile.agency_id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    updateAgencySettings: async (updates) => {
      if (useMock) throw new Error('Calendar settings require Supabase (not available in mock mode).');
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('agency_calendar_settings')
        .upsert(
          { ...updates, agency_id: profile.agency_id },
          { onConflict: 'agency_id' },
        )
        .select()
        .single();
      if (error) throw error;
      return data;
    },
  },
  googleCalendar: {
    /**
     * Returns the Edge Function URL that redirects to Google OAuth (browser GET, no JWT).
     * @param {'agent' | 'agency'} [mode]
     * @param {{ redirect?: boolean }} [opts] Pass `{ redirect: true }` to navigate immediately (legacy).
     */
    getAuthUrl: async (mode = 'agent', opts = {}) => {
      if (useMock) {
        throw new Error('Google Calendar requires Supabase (not available in mock mode).');
      }
      const profile = await api.auth.getCurrentUser();
      if (!profile?.id || !profile?.agency_id) throw new Error('Not authenticated');

      const m = mode === 'agency' ? 'agency' : 'agent';
      const redirect = opts.redirect === true;

      const params = new URLSearchParams({
        user_id: profile.id,
        agency_id: profile.agency_id,
        type: m === 'agency' ? 'agency' : 'agent',
      });
      const url = `${supabaseUrl}/functions/v1/google-calendar-auth?${params.toString()}`;
      if (redirect && typeof window !== 'undefined') {
        window.location.assign(url);
      }
      return url;
    },

    getAvailability: async (userId, startDate, endDate) => {
      if (useMock) {
        throw new Error('Google Calendar requires Supabase (not available in mock mode).');
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Invalid session — please log in again');
      const { data, error } = await supabase.functions.invoke('google-calendar-events', {
        body: {
          action: 'get_availability',
          payload: { user_id: userId, start_date: startDate, end_date: endDate },
        },
      });
      if (error) {
        let msg = error.message || 'Could not load availability';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }
      return Array.isArray(data) ? data : [];
    },

    createEvent: async (payload) => {
      if (useMock) {
        throw new Error('Google Calendar requires Supabase (not available in mock mode).');
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Invalid session — please log in again');
      const { data, error } = await supabase.functions.invoke('google-calendar-events', {
        body: { action: 'create_event', payload },
      });
      if (error) {
        let msg = error.message || 'Could not create calendar event';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }
      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Could not create calendar event');
      }
      return data?.event_id ?? null;
    },

    deleteEvent: async (userId, type, eventId) => {
      if (useMock) {
        throw new Error('Google Calendar requires Supabase (not available in mock mode).');
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Invalid session — please log in again');
      const { data, error } = await supabase.functions.invoke('google-calendar-events', {
        body: {
          action: 'delete_event',
          payload: { user_id: userId, type, event_id: eventId },
        },
      });
      if (error) {
        let msg = error.message || 'Could not delete calendar event';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }
      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Could not delete calendar event');
      }
      return data?.success === true;
    },

    disconnect: async (mode = 'agent') => {
      if (useMock) {
        throw new Error('Google Calendar requires Supabase (not available in mock mode).');
      }
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const cleared = {
        google_connected: false,
        google_calendar_id: null,
        google_token_expiry: null,
      };

      if (mode === 'agency') {
        const { data, error } = await supabase
          .from('agency_calendar_settings')
          .update(cleared)
          .eq('agency_id', profile.agency_id)
          .select()
          .maybeSingle();
        if (error) throw error;
        return data;
      }

      const { data, error } = await supabase
        .from('agent_calendar_settings')
        .update(cleared)
        .eq('user_id', profile.id)
        .select()
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  },
  zoom: {
    getAuthUrl(userId, agencyId) {
      const base = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/zoom-oauth`;
      return `${base}?user_id=${userId}&agency_id=${agencyId}`;
    },

    async createMeeting({ appointmentId, userId, topic, startTime, durationMinutes }) {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/zoom-create-meeting`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            appointment_id: appointmentId,
            user_id: userId,
            topic,
            start_time: startTime,
            duration_minutes: durationMinutes,
          }),
        },
      );
      let json = {};
      try {
        json = await res.json();
      } catch {
        json = {};
      }
      if (!res.ok) {
        return {
          error: json?.error || `Zoom create meeting failed (HTTP ${res.status})`,
          details: json?.details,
          reconnect_required: Boolean(json?.reconnect_required),
          status: res.status,
        };
      }
      return json;
    },
  },
  agencies: {
    /** Single row from `agencies` by id (RLS must allow SELECT for the caller's agency). */
    getById: async (agencyId) => {
      if (!agencyId) throw new Error('Agency id is required');
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        const profile = db.users?.find((u) => u.id === currentMockUserId);
        if (!profile) throw new Error('Not authenticated');
        if (profile.agency_id !== agencyId) throw new Error('Not authorized');
        const row = db.agencies?.find((a) => a.id === agencyId);
        if (!row) throw new Error('Agency not found');
        return row;
      }
      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .eq('id', agencyId)
        .single();
      if (error) throw error;
      return data;
    },
    /** Updates `agencies` filtered by `id`. Pass the signed-in user's `agency_id`. */
    update: async (agencyId, updates) => {
      if (!agencyId) throw new Error('Agency is required');
      if (useMock) {
        const db = JSON.parse(localStorage.getItem('prosperity_leads_db') || '{}');
        const profile = db.users?.find((u) => u.id === currentMockUserId);
        if (!profile) throw new Error('Not authenticated');
        if (profile.agency_id !== agencyId) throw new Error('Not authorized');
        const idx = db.agencies?.findIndex((a) => a.id === agencyId);
        if (idx < 0) throw new Error('Agency not found');
        db.agencies[idx] = { ...db.agencies[idx], ...updates };
        localStorage.setItem('prosperity_leads_db', JSON.stringify(db));
        return db.agencies[idx];
      }
      const { data, error } = await supabase
        .from('agencies')
        .update(updates)
        .eq('id', agencyId)
        .select()
        .single();
      if (error) {
        console.error('[api.agencies.update] Supabase error:', {
          agencyId,
          updates,
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        });
        throw error;
      }
      if (data == null) {
        console.error('[api.agencies.update] No row returned after update (often RLS blocks update or wrong agency id)', {
          agencyId,
          updates,
        });
        throw new Error('Agency update returned no row. Check RLS policies and agency id.');
      }
      return data;
    },
  },
  smsMessages: {
    getConversations: async () => {
      const { data, error } = await supabase.rpc('get_sms_conversations');
      if (error) {
        // No agency_id filter — RLS (sms_messages_select_visibility) scopes rows
        // to leads the caller can access; grouping only sees allowed messages.
        const { data: raw, error: rawErr } = await supabase
          .from('sms_messages')
          .select('*, lead:leads(id, first_name, last_name, phone)')
          .order('created_at', { ascending: false });
        if (rawErr) throw rawErr;

        const grouped = {};
        for (const msg of raw || []) {
          const key = msg.lead_id || msg.from_number;
          if (!grouped[key]) {
            const unreadCount = (raw || []).filter(
              (m) =>
                (m.lead_id || m.from_number) === key &&
                m.direction === 'inbound' &&
                !m.read_at,
            ).length;
            grouped[key] = { ...msg, unread_count: unreadCount };
          }
        }
        return Object.values(grouped);
      }
      return data;
    },

    getThread: async (leadId) => {
      const { data, error } = await supabase
        .from('sms_messages')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data;
    },

    getThreadByPhone: async (phoneNumber) => {
      const orFilter = phoneSmsThreadOrFilter(phoneNumber);
      if (!orFilter) return [];

      const { data, error } = await supabase
        .from('sms_messages')
        .select('*')
        .or(orFilter)
        .order('created_at', { ascending: true });
      if (error) throw error;

      // Harden against PostgREST variant over-match: keep rows whose from/to last-10 matches
      return (data || []).filter(
        (msg) => phonesMatch(msg.from_number, phoneNumber) || phonesMatch(msg.to_number, phoneNumber),
      );
    },

    saveOutbound: async (messageData) => {
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('sms_messages')
        .insert({
          agency_id: profile.agency_id,
          agent_id: profile.id,
          direction: 'outbound',
          status: 'sent',
          lead_id: messageData.lead_id ?? null,
          to_number: messageData.to_number,
          from_number: messageData.from_number,
          body: messageData.body,
          twilio_message_sid: messageData.twilio_message_sid ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    markThreadRead: async (leadId) => {
      const { data, error } = await supabase
        .from('sms_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('lead_id', leadId)
        .eq('direction', 'inbound')
        .is('read_at', null)
        .select();
      if (error) throw error;
      return data;
    },

    markThreadReadByPhone: async (phoneNumber) => {
      const orFilter = phoneSmsThreadOrFilter(phoneNumber);
      if (!orFilter) return [];

      const { data, error } = await supabase
        .from('sms_messages')
        .update({ read_at: new Date().toISOString() })
        .is('lead_id', null)
        .or(orFilter)
        .eq('direction', 'inbound')
        .is('read_at', null)
        .select();
      if (error) throw error;

      return (data || []).filter(
        (msg) => phonesMatch(msg.from_number, phoneNumber) || phonesMatch(msg.to_number, phoneNumber),
      );
    },

    getUnreadCount: async () => {
      const { count, error } = await supabase
        .from('sms_messages')
        .select('*', { count: 'exact', head: true })
        .eq('direction', 'inbound')
        .is('read_at', null);
      if (error) throw error;
      return count ?? 0;
    },
  },
  sequences: {
    getAll: async (agencyId) => {
      if (useMock) return [];
      if (!agencyId) return [];
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return [];

      const { data, error } = await supabase
        .from('email_sequences')
        .select('*, email_sequence_steps(count)')
        .eq('agency_id', agencyId)
        .order('created_at', { ascending: false });
      if (error) throw error;

      return (data ?? []).map((row) => {
        const { email_sequence_steps: stepAgg, ...seq } = row;
        const stepCount = Array.isArray(stepAgg) && stepAgg[0]?.count != null ? stepAgg[0].count : 0;
        return { ...seq, step_count: stepCount };
      });
    },

    getById: async (id) => {
      if (useMock) return null;
      if (!id) throw new Error('Sequence id is required');

      const { data, error } = await supabase
        .from('email_sequences')
        .select('*, email_sequence_steps(*)')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;

      const steps = (data.email_sequence_steps ?? []).sort(
        (a, b) => (a.step_number ?? 0) - (b.step_number ?? 0),
      );
      const { email_sequence_steps: _steps, ...sequence } = data;
      return { ...sequence, steps };
    },

    create: async (data) => {
      if (useMock) throw new Error('Email sequences require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const agencyId = data.agency_id || profile.agency_id;
      if (agencyId !== profile.agency_id) throw new Error('Not authorized');

      const { data: row, error } = await supabase
        .from('email_sequences')
        .insert({
          agency_id: agencyId,
          name: data.name,
          description: data.description || null,
          trigger_status: data.trigger_status ?? null,
          is_active: data.is_active ?? true,
          created_by: profile.id,
        })
        .select()
        .single();
      if (error) throw error;
      return row;
    },

    update: async (id, data) => {
      if (useMock) throw new Error('Email sequences require Supabase.');
      if (!id) throw new Error('Sequence id is required');

      const payload = {};
      if (data.name !== undefined) payload.name = data.name;
      if (data.description !== undefined) payload.description = data.description;
      if (data.trigger_status !== undefined) payload.trigger_status = data.trigger_status;
      if (data.is_active !== undefined) payload.is_active = data.is_active;

      const { data: row, error } = await supabase
        .from('email_sequences')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return row;
    },

    toggleActive: async (id, isActive) => {
      return api.sequences.update(id, { is_active: isActive });
    },

    delete: async (id) => {
      if (useMock) throw new Error('Email sequences require Supabase.');
      if (!id) throw new Error('Sequence id is required');

      const { error } = await supabase.from('email_sequences').delete().eq('id', id);
      if (error) throw error;
    },

    duplicate: async (id) => {
      if (useMock) throw new Error('Email sequences require Supabase.');
      if (!id) throw new Error('Sequence id is required');

      const source = await api.sequences.getById(id);
      if (!source) throw new Error('Sequence not found');

      const row = await api.sequences.create({
        agency_id: source.agency_id,
        name: `Copy of ${source.name}`,
        description: source.description,
        trigger_status: source.trigger_status,
        is_active: false,
      });

      for (const step of source.steps ?? []) {
        await api.sequences.createStep({
          sequence_id: row.id,
          agency_id: source.agency_id,
          delay_days: step.delay_days ?? 0,
          subject: step.subject,
          body_html: step.body_html,
        });
      }

      return api.sequences.getById(row.id);
    },

    createStep: async (stepData) => {
      if (useMock) throw new Error('Email sequences require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      let stepNumber = stepData.step_number;
      if (stepNumber == null) {
        const { data: existing, error: listErr } = await supabase
          .from('email_sequence_steps')
          .select('step_number')
          .eq('sequence_id', stepData.sequence_id)
          .order('step_number', { ascending: false })
          .limit(1);
        if (listErr) throw listErr;
        stepNumber = existing?.length ? existing[0].step_number + 1 : 1;
      }

      const { data: row, error } = await supabase
        .from('email_sequence_steps')
        .insert({
          sequence_id: stepData.sequence_id,
          agency_id: stepData.agency_id || profile.agency_id,
          step_number: stepNumber,
          delay_days: stepData.delay_days ?? 0,
          subject: stepData.subject,
          body_html: stepData.body_html,
        })
        .select()
        .single();
      if (error) throw error;
      return row;
    },

    updateStep: async (id, data) => {
      if (useMock) throw new Error('Email sequences require Supabase.');
      if (!id) throw new Error('Step id is required');

      const payload = {};
      if (data.step_number !== undefined) payload.step_number = data.step_number;
      if (data.delay_days !== undefined) payload.delay_days = data.delay_days;
      if (data.subject !== undefined) payload.subject = data.subject;
      if (data.body_html !== undefined) payload.body_html = data.body_html;

      const { data: row, error } = await supabase
        .from('email_sequence_steps')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return row;
    },

    deleteStep: async (id) => {
      if (useMock) throw new Error('Email sequences require Supabase.');
      if (!id) throw new Error('Step id is required');

      const { error } = await supabase.from('email_sequence_steps').delete().eq('id', id);
      if (error) throw error;
    },

    reorderSteps: async (sequenceId, stepIds) => {
      if (useMock) throw new Error('Email sequences require Supabase.');
      if (!sequenceId || !stepIds?.length) return;

      for (let i = 0; i < stepIds.length; i++) {
        const { error } = await supabase
          .from('email_sequence_steps')
          .update({ step_number: -(i + 1) })
          .eq('id', stepIds[i])
          .eq('sequence_id', sequenceId);
        if (error) throw error;
      }
      for (let i = 0; i < stepIds.length; i++) {
        const { error } = await supabase
          .from('email_sequence_steps')
          .update({ step_number: i + 1 })
          .eq('id', stepIds[i])
          .eq('sequence_id', sequenceId);
        if (error) throw error;
      }
    },
  },
  enrollments: {
    getByLead: async (leadId) => {
      if (useMock) return [];
      if (!leadId) return [];

      const { data, error } = await supabase
        .from('email_sequence_enrollments')
        .select(
          `*,
          email_sequences (
            id,
            name,
            email_sequence_steps (
              step_number,
              subject,
              delay_days
            )
          ),
          enrolled_by_user:users!email_sequence_enrollments_enrolled_by_fkey (
            id,
            full_name
          )`,
        )
        .eq('lead_id', leadId)
        .order('enrolled_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map(normalizeEnrollmentRow);
    },

    enroll: async (sequenceId, leadId, agencyId, enrolledBy) => {
      if (useMock) throw new Error('Email enrollments require Supabase.');
      if (!sequenceId || !leadId || !agencyId) {
        throw new Error('Sequence, lead, and agency are required');
      }

      const { data: existing, error: existErr } = await supabase
        .from('email_sequence_enrollments')
        .select('id, status')
        .eq('sequence_id', sequenceId)
        .eq('lead_id', leadId)
        .maybeSingle();
      if (existErr) throw existErr;

      if (existing?.status === 'active') {
        const { data: current, error: fetchErr } = await supabase
          .from('email_sequence_enrollments')
          .select(
            `*,
            email_sequences (
              id,
              name,
              email_sequence_steps ( step_number, subject, delay_days )
            ),
            enrolled_by_user:users!email_sequence_enrollments_enrolled_by_fkey ( id, full_name )`,
          )
          .eq('id', existing.id)
          .single();
        if (fetchErr) throw fetchErr;
        return normalizeEnrollmentRow(current);
      }

      const delayDays = await fetchSequenceStep1DelayDays(sequenceId);
      const next_send_at = computeNextSendAtFromDelayDays(delayDays);

      if (existing) {
        const { data, error } = await supabase
          .from('email_sequence_enrollments')
          .update({
            status: 'active',
            current_step: 1,
            next_send_at,
            enrolled_by: enrolledBy ?? null,
            enrolled_at: new Date().toISOString(),
          })
          .eq('id', existing.id)
          .select(
            `*,
            email_sequences (
              id,
              name,
              email_sequence_steps ( step_number, subject, delay_days )
            ),
            enrolled_by_user:users!email_sequence_enrollments_enrolled_by_fkey ( id, full_name )`,
          )
          .single();
        if (error) throw error;
        return normalizeEnrollmentRow(data);
      }

      const { data, error } = await supabase
        .from('email_sequence_enrollments')
        .insert({
          sequence_id: sequenceId,
          lead_id: leadId,
          agency_id: agencyId,
          enrolled_by: enrolledBy ?? null,
          status: 'active',
          current_step: 1,
          next_send_at,
        })
        .select(
          `*,
          email_sequences (
            id,
            name,
            email_sequence_steps ( step_number, subject, delay_days )
          ),
          enrolled_by_user:users!email_sequence_enrollments_enrolled_by_fkey ( id, full_name )`,
        )
        .single();
      if (error) throw error;
      return normalizeEnrollmentRow(data);
    },

    cancel: async (enrollmentId) => {
      if (useMock) throw new Error('Email enrollments require Supabase.');
      if (!enrollmentId) throw new Error('Enrollment id is required');

      const { data, error } = await supabase
        .from('email_sequence_enrollments')
        .update({ status: 'cancelled', next_send_at: null })
        .eq('id', enrollmentId)
        .select(
          `*,
          email_sequences (
            id,
            name,
            email_sequence_steps ( step_number, subject, delay_days )
          ),
          enrolled_by_user:users!email_sequence_enrollments_enrolled_by_fkey ( id, full_name )`,
        )
        .single();
      if (error) throw error;
      return normalizeEnrollmentRow(data);
    },

    checkAndAutoEnroll: async (leadId, agencyId, newStatus) => {
      if (useMock) return;
      if (!leadId || !agencyId || !newStatus) return;

      const { data: sequences, error } = await supabase
        .from('email_sequences')
        .select('id')
        .eq('agency_id', agencyId)
        .eq('is_active', true)
        .eq('trigger_status', newStatus);
      if (error) throw error;
      if (!sequences?.length) return;

      const { data: activeEnrollments, error: enrErr } = await supabase
        .from('email_sequence_enrollments')
        .select('sequence_id')
        .eq('lead_id', leadId)
        .eq('status', 'active');
      if (enrErr) throw enrErr;

      const activeSet = new Set((activeEnrollments ?? []).map((e) => e.sequence_id));

      for (const seq of sequences) {
        if (activeSet.has(seq.id)) continue;
        try {
          await api.enrollments.enroll(seq.id, leadId, agencyId, null);
        } catch (enrollErr) {
          console.error('[api.enrollments.checkAndAutoEnroll] enroll failed', {
            sequenceId: seq.id,
            leadId,
            message: enrollErr?.message,
          });
        }
      }
    },
  },
  campaigns: {
    getAll: async (agencyId) => {
      if (useMock) return [];
      if (!agencyId) return [];

      const { data, error } = await supabase
        .from('email_campaigns')
        .select('*')
        .eq('agency_id', agencyId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },

    getById: async (id) => {
      if (useMock) return null;
      if (!id) throw new Error('Campaign id is required');

      const { data, error } = await supabase
        .from('email_campaigns')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },

    create: async (data) => {
      if (useMock) throw new Error('Email campaigns require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const agencyId = data.agency_id || profile.agency_id;
      if (agencyId !== profile.agency_id) throw new Error('Not authorized');

      const { data: row, error } = await supabase
        .from('email_campaigns')
        .insert({
          agency_id: agencyId,
          name: data.name,
          subject: data.subject,
          body_html: data.body_html,
          audience_filters: data.audience_filters ?? {},
          status: 'draft',
          created_by: profile.id,
        })
        .select()
        .single();
      if (error) throw error;
      return row;
    },

    update: async (id, data) => {
      if (useMock) throw new Error('Email campaigns require Supabase.');
      if (!id) throw new Error('Campaign id is required');

      const existing = await api.campaigns.getById(id);
      if (!existing) throw new Error('Campaign not found');
      if (existing.status !== 'draft') {
        throw new Error('Only draft campaigns can be edited');
      }

      const payload = {};
      if (data.name !== undefined) payload.name = data.name;
      if (data.subject !== undefined) payload.subject = data.subject;
      if (data.body_html !== undefined) payload.body_html = data.body_html;
      if (data.audience_filters !== undefined) payload.audience_filters = data.audience_filters;

      const { data: row, error } = await supabase
        .from('email_campaigns')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return row;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Email campaigns require Supabase.');
      if (!id) throw new Error('Campaign id is required');

      const existing = await api.campaigns.getById(id);
      if (!existing) throw new Error('Campaign not found');
      if (existing.status !== 'draft') {
        throw new Error('Only draft campaigns can be deleted');
      }

      const { error } = await supabase.from('email_campaigns').delete().eq('id', id);
      if (error) throw error;
    },

    duplicate: async (id) => {
      if (useMock) throw new Error('Email campaigns require Supabase.');
      if (!id) throw new Error('Campaign id is required');

      const source = await api.campaigns.getById(id);
      if (!source) throw new Error('Campaign not found');

      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');
      if (source.agency_id !== profile.agency_id) throw new Error('Not authorized');

      const { data: row, error } = await supabase
        .from('email_campaigns')
        .insert({
          agency_id: source.agency_id,
          name: `Copy of ${source.name}`,
          subject: source.subject,
          body_html: source.body_html,
          audience_filters: source.audience_filters ?? {},
          status: 'draft',
          total_recipients: 0,
          sent_count: 0,
          open_count: 0,
          click_count: 0,
          brevo_campaign_id: null,
          sent_at: null,
          created_by: profile.id,
        })
        .select()
        .single();
      if (error) throw error;
      return row;
    },

    send: async (campaignId) => {
      if (useMock) throw new Error('Email campaigns require Supabase.');
      if (!campaignId) throw new Error('Campaign id is required');

      const { data, error } = await supabase.functions.invoke('brevo-campaign', {
        body: { action: 'create_and_send', campaign_id: campaignId },
      });

      if (error) {
        let msg = error.message || 'Could not send campaign';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }
      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Could not send campaign');
      }
      return data;
    },

    syncStats: async (campaignId) => {
      if (useMock) throw new Error('Email campaigns require Supabase.');
      if (!campaignId) throw new Error('Campaign id is required');

      const { data, error } = await supabase.functions.invoke('brevo-campaign', {
        body: { action: 'sync_stats', campaign_id: campaignId },
      });

      if (error) {
        let msg = error.message || 'Could not sync stats';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }
      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Could not sync stats');
      }
      return data?.campaign ?? data;
    },

    getRecipients: async (campaignId) => {
      if (useMock) return [];
      if (!campaignId) return [];

      const { data, error } = await supabase
        .from('email_logs')
        .select('id, to_email, created_at, lead:leads(id, first_name, last_name)')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      return (data ?? []).map((row) => {
        const lead = row.lead;
        const name = lead
          ? [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim()
          : '';
        return {
          id: row.id,
          email: row.to_email,
          sent_at: row.created_at,
          lead_name: name || row.to_email,
        };
      });
    },

    previewAudience: async (agencyId, filters = {}) => {
      if (useMock) return { count: 0, sample: [] };
      if (!agencyId) return { count: 0, sample: [] };

      let q = supabase
        .from('leads')
        .select('id, first_name, last_name, email', { count: 'exact' })
        .eq('agency_id', agencyId)
        .is('archived_at', null);

      const leadTypes = filters.lead_type?.filter(Boolean);
      const statuses = filters.status?.filter(Boolean);
      const sources = filters.lead_source?.filter(Boolean);

      if (leadTypes?.length) q = q.in('lead_type', leadTypes);
      if (statuses?.length) q = q.in('status', statuses);
      if (sources?.length) q = q.in('lead_source', sources);

      const { data, count, error } = await q.limit(200);
      if (error) throw error;

      const seen = new Set();
      const withEmail = [];
      for (const row of data ?? []) {
        const email = row.email?.trim();
        if (!email) continue;
        const key = email.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        withEmail.push(row);
      }

      return {
        count: withEmail.length,
        sample: withEmail.slice(0, 5).map((l) => {
          const email = l.email?.trim() || '';
          return {
            id: l.id,
            name: [l.first_name, l.last_name].filter(Boolean).join(' ') || email,
            email,
          };
        }),
      };
    },
  },

  emailTemplates: {
    async getAll(agencyId) {
      if (useMock) return [];
      if (!agencyId) return [];

      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .or(`agency_id.eq.${agencyId},is_system.eq.true`)
        .order('category')
        .order('name');
      if (error) throw error;
      return data;
    },

    async getById(id) {
      if (useMock) return null;
      if (!id) throw new Error('Template id is required');

      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data;
    },

    async create(template) {
      if (useMock) throw new Error('Email templates require Supabase.');

      const { data, error } = await supabase
        .from('email_templates')
        .insert(template)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async update(id, updates) {
      if (useMock) throw new Error('Email templates require Supabase.');
      if (!id) throw new Error('Template id is required');

      const { data, error } = await supabase
        .from('email_templates')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Save failed — template not found or permission denied');
      return data;
    },

    async duplicate(id, agencyId, userId) {
      if (useMock) throw new Error('Email templates require Supabase.');

      const original = await api.emailTemplates.getById(id);
      if (!original) throw new Error('Template not found');

      return api.emailTemplates.create({
        agency_id: agencyId,
        created_by: userId,
        name: `${original.name} (Copy)`,
        category: original.category,
        subject: original.subject,
        description: original.description,
        html_body: original.html_body,
        is_system: false,
      });
    },

    async delete(id) {
      if (useMock) throw new Error('Email templates require Supabase.');
      if (!id) throw new Error('Template id is required');

      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', id)
        .eq('is_system', false);
      if (error) throw error;
      return { success: true };
    },
  },

  exclusivity: {
    setStatus: async (leadId, status) => {
      if (useMock) throw new Error('Exclusivity requires Supabase.');
      if (!['exclusive', 'non_exclusive'].includes(status)) {
        throw new Error('status must be exclusive or non_exclusive');
      }
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!isManagerOrAdminRole(profile?.role)) {
        throw new Error('Only managers and admins can change exclusivity status');
      }
      const { data, error } = await supabase
        .from('leads')
        .update({ exclusivity_status: status })
        .eq('id', leadId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    grantExclusivity: async (leadId, userId, timeframeDays) => {
      if (useMock) throw new Error('Exclusivity requires Supabase.');
      const expiresAt = computeExclusivityExpiresAt(timeframeDays);
      const { data, error } = await supabase
        .from('leads')
        .update({
          exclusive_to: userId,
          exclusivity_expires_at: expiresAt,
          exclusivity_status: 'exclusive',
        })
        .eq('id', leadId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    revokeExclusivity: async (leadId) => {
      if (useMock) throw new Error('Exclusivity requires Supabase.');
      const { data, error } = await supabase
        .from('leads')
        .update({
          exclusive_to: null,
          exclusivity_expires_at: null,
          exclusivity_status: 'non_exclusive',
        })
        .eq('id', leadId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    requestExclusivity: async (leadId, reason) => {
      if (useMock) throw new Error('Exclusivity requires Supabase.');
      const trimmed = reason?.trim();
      if (!trimmed) throw new Error('A reason is required');
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('exclusivity_requests')
        .insert({
          lead_id: leadId,
          requested_by: profile.id,
          agency_id: profile.agency_id,
          reason: trimmed,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    getPendingRequests: async (agencyId) => {
      if (useMock) return [];
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return [];

      const { data, error } = await supabase
        .from('exclusivity_requests')
        .select(
          `
          *,
          lead:leads(first_name, last_name),
          requester:users!exclusivity_requests_requested_by_fkey(full_name)
        `,
        )
        .eq('agency_id', agencyId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (error) throw error;

      return (data ?? []).map((row) => {
        const lead = row.lead;
        const leadName = lead
          ? [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim()
          : '';
        return {
          ...row,
          lead_name: leadName || 'Unknown lead',
          requester_name: row.requester?.full_name ?? 'Unknown',
        };
      });
    },

    approveRequest: async (requestId, timeframeDays) => {
      if (useMock) throw new Error('Exclusivity requires Supabase.');
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!isManagerOrAdminRole(profile?.role)) {
        throw new Error('Only managers and admins can approve exclusivity requests');
      }

      const { data: request, error: fetchErr } = await supabase
        .from('exclusivity_requests')
        .select('id, lead_id, requested_by, agency_id, status')
        .eq('id', requestId)
        .single();
      if (fetchErr) throw fetchErr;
      if (!request || request.status !== 'pending') {
        throw new Error('Request not found or already resolved');
      }
      if (request.agency_id !== profile.agency_id && profile.role !== 'super_admin') {
        throw new Error('Request is not in your agency');
      }

      const expiresAt = computeExclusivityExpiresAt(timeframeDays);
      const { error: updErr } = await supabase
        .from('exclusivity_requests')
        .update({
          status: 'approved',
          approved_by: profile.id,
          timeframe_days: timeframeDays ?? null,
          expires_at: expiresAt,
        })
        .eq('id', requestId);
      if (updErr) throw updErr;

      return api.exclusivity.grantExclusivity(request.lead_id, request.requested_by, timeframeDays);
    },

    denyRequest: async (requestId) => {
      if (useMock) throw new Error('Exclusivity requires Supabase.');
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!isManagerOrAdminRole(profile?.role)) {
        throw new Error('Only managers and admins can deny exclusivity requests');
      }

      const { data, error } = await supabase
        .from('exclusivity_requests')
        .update({ status: 'denied', approved_by: profile.id })
        .eq('id', requestId)
        .eq('status', 'pending')
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    checkAndExpireExclusivity: async (leadId) => {
      if (useMock) return null;
      const { data: lead, error } = await supabase
        .from('leads')
        .select('id, exclusivity_expires_at, exclusivity_status')
        .eq('id', leadId)
        .maybeSingle();
      if (error) throw error;
      if (!lead?.exclusivity_expires_at) return lead;
      if (new Date(lead.exclusivity_expires_at) <= new Date()) {
        return api.exclusivity.revokeExclusivity(leadId);
      }
      return lead;
    },

    getPendingRequestCount: async (agencyId) => {
      if (useMock) return 0;
      const profile = await assertAgencyAccess(agencyId);
      if (!profile || !isManagerOrAdminRole(profile.role)) return 0;
      const { count, error } = await supabase
        .from('exclusivity_requests')
        .select('id', { count: 'exact', head: true })
        .eq('agency_id', agencyId)
        .eq('status', 'pending');
      if (error) throw error;
      return count ?? 0;
    },

    getResolvedRequests: async (agencyId) => {
      if (useMock) return [];
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return [];

      const { data, error } = await supabase
        .from('exclusivity_requests')
        .select(
          `
          *,
          lead:leads(first_name, last_name),
          requester:users!exclusivity_requests_requested_by_fkey(full_name),
          approver:users!exclusivity_requests_approved_by_fkey(full_name)
        `,
        )
        .eq('agency_id', agencyId)
        .in('status', ['approved', 'denied'])
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;

      return (data ?? []).map((row) => {
        const lead = row.lead;
        const leadName = lead
          ? [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim()
          : '';
        return {
          ...row,
          lead_name: leadName || 'Unknown lead',
          requester_name: row.requester?.full_name ?? 'Unknown',
          approver_name: row.approver?.full_name ?? '—',
        };
      });
    },

    countForBulkNonExclusive: async (agencyId, { leadTypes = [], leadSources = [] } = {}) => {
      if (useMock) return 0;
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile || !['admin', 'super_admin'].includes(profile.role)) {
        throw new Error('Only admins can bulk set non-exclusive leads');
      }
      if (profile.agency_id !== agencyId && profile.role !== 'super_admin') {
        throw new Error('Agency mismatch');
      }

      let query = supabase
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .eq('agency_id', agencyId)
        .is('archived_at', null);
      if (leadTypes.length) query = query.in('lead_type', leadTypes);
      if (leadSources.length) query = query.in('lead_source', leadSources);
      const { count, error } = await query;
      if (error) throw error;
      return count ?? 0;
    },

    bulkSetNonExclusive: async (agencyId, { leadTypes = [], leadSources = [] } = {}) => {
      if (useMock) throw new Error('Exclusivity requires Supabase.');
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile || !['admin', 'super_admin'].includes(profile.role)) {
        throw new Error('Only admins can bulk set non-exclusive leads');
      }
      if (profile.agency_id !== agencyId && profile.role !== 'super_admin') {
        throw new Error('Agency mismatch');
      }

      let query = supabase
        .from('leads')
        .update({
          exclusivity_status: 'non_exclusive',
          exclusive_to: null,
          exclusivity_expires_at: null,
        })
        .eq('agency_id', agencyId)
        .is('archived_at', null);
      if (leadTypes.length) query = query.in('lead_type', leadTypes);
      if (leadSources.length) query = query.in('lead_source', leadSources);
      const { data, error } = await query.select('id');
      if (error) throw error;
      return { updated: data?.length ?? 0 };
    },
  },

  ownershipTransfers: {
    getForAgency: async (agencyId) => {
      if (useMock) return [];
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return [];

      const { data, error } = await supabase
        .from('lead_ownership_transfer_requests')
        .select(OWNERSHIP_TRANSFER_SELECT)
        .eq('agency_id', agencyId)
        .order('created_at', { ascending: false });
      if (error) throw error;

      return (data ?? []).map(mapOwnershipTransferRow);
    },

    getPendingForLead: async (leadId) => {
      if (useMock) return null;
      if (!leadId) return null;

      const { data, error } = await supabase
        .from('lead_ownership_transfer_requests')
        .select(OWNERSHIP_TRANSFER_SELECT)
        .eq('lead_id', leadId)
        .eq('status', 'pending')
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return mapOwnershipTransferRow(data);
    },

    request: async ({ leadId, agencyId, currentOwnerId, notes }) => {
      if (useMock) throw new Error('Ownership transfers require Supabase.');
      const authId = await getSessionUserId();
      if (!authId) throw new Error('Not authenticated');

      const { data: existing, error: existingErr } = await supabase
        .from('lead_ownership_transfer_requests')
        .select('id')
        .eq('lead_id', leadId)
        .eq('status', 'pending')
        .maybeSingle();
      if (existingErr) throw existingErr;
      if (existing) {
        throw new Error('A pending transfer request already exists for this lead');
      }

      const { data, error } = await supabase
        .from('lead_ownership_transfer_requests')
        .insert({
          lead_id: leadId,
          agency_id: agencyId,
          requested_by: authId,
          current_owner_id: currentOwnerId,
          status: 'pending',
          notes: notes?.trim() || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    resolve: async (requestId, { approved, resolvedBy }) => {
      if (useMock) throw new Error('Ownership transfers require Supabase.');
      if (!requestId) throw new Error('Request id is required');
      if (!resolvedBy) throw new Error('resolvedBy is required');

      const { data: request, error: fetchErr } = await supabase
        .from('lead_ownership_transfer_requests')
        .select('id, lead_id, requested_by, status')
        .eq('id', requestId)
        .single();
      if (fetchErr) throw fetchErr;
      if (!request || request.status !== 'pending') {
        throw new Error('Request not found or already resolved');
      }

      const status = approved ? 'approved' : 'denied';
      const resolvedAt = new Date().toISOString();

      const { data, error } = await supabase
        .from('lead_ownership_transfer_requests')
        .update({
          status,
          resolved_by: resolvedBy,
          resolved_at: resolvedAt,
        })
        .eq('id', requestId)
        .eq('status', 'pending')
        .select()
        .single();
      if (error) throw error;

      if (approved) {
        const { error: leadErr } = await supabase
          .from('leads')
          .update({ created_by: request.requested_by })
          .eq('id', request.lead_id);
        if (leadErr) throw leadErr;
      }

      return data;
    },

    getPendingActionCount: async (agencyId) => {
      if (useMock) return 0;
      const authId = await getSessionUserId();
      if (!authId) return 0;
      const profile = await fetchProfileRowForAuthUser(authId);
      if (!profile?.agency_id) return 0;
      const effectiveAgencyId = agencyId || profile.agency_id;

      let query = supabase
        .from('lead_ownership_transfer_requests')
        .select('id', { count: 'exact', head: true })
        .eq('agency_id', effectiveAgencyId)
        .eq('status', 'pending');

      if (!['admin', 'super_admin'].includes(profile.role)) {
        query = query.eq('current_owner_id', authId);
      }

      const { count, error } = await query;
      if (error) throw error;
      return count ?? 0;
    },
  },

  activityLogs: {
    create: async (leadId, payload) => {
      if (useMock) {
        return localDB.createActivityLog(currentMockUserId, leadId, payload);
      }
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const row = {
        lead_id: leadId,
        agency_id: profile.agency_id,
        agent_id: userId,
        activity_type: payload.activity_type,
        activity_date: payload.activity_date || toLocalDateKey(new Date()),
        amount_dollars: payload.amount_dollars ?? null,
        policy_type: payload.policy_type ?? null,
        referral_name: payload.referral_name ?? null,
        referral_phone: payload.referral_phone ?? null,
        objection_text: payload.objection_text ?? null,
        how_handled: payload.how_handled ?? null,
        call_count: payload.call_count ?? null,
        call_outcome: payload.call_outcome ?? null,
        notes: payload.notes ?? null,
      };

      const { data, error } = await supabase
        .from('activity_logs')
        .insert(row)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
  },
  kpi: {
    getWeekData: async (userId, agencyId, weekStartDate, { channel = null } = {}) => {
      if (useMock) return [];
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return [];
      const authId = await getSessionUserId();
      if (userId !== authId && !isManagerOrAdminRole(profile.role)) {
        throw new Error('Not authorized to view this agent');
      }
      return fetchKpiManualWeekLogs(userId, agencyId, weekStartDate, { channel });
    },

    upsertDay: async (userId, agencyId, date, payload) => {
      if (useMock) {
        return {
          user_id: userId,
          agency_id: agencyId,
          date,
          presentations: Number(payload.presentations) || 0,
          apps_submitted: Number(payload.apps_submitted) || 0,
          ap_submitted_dollars: Number(payload.ap_submitted_dollars) || 0,
          referrals_gathered: Number(payload.referrals_gathered) || 0,
          objections_received: Number(payload.objections_received) || 0,
        };
      }
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) throw new Error('Not authenticated');
      const authId = await getSessionUserId();
      if (userId !== authId) throw new Error('Can only edit your own KPI entries');

      const channel =
        payload.channel && ['IM', 'AM', 'LD'].includes(payload.channel) ? payload.channel : 'LD';

      const row = {
        user_id: userId,
        agency_id: agencyId,
        date,
        channel,
        presentations: Math.max(0, Number(payload.presentations) || 0),
        apps_submitted: Math.max(0, Number(payload.apps_submitted) || 0),
        ap_submitted_dollars: Math.max(0, Number(payload.ap_submitted_dollars) || 0),
        referrals_gathered: Math.max(0, Number(payload.referrals_gathered) || 0),
        objections_received: Math.max(0, Number(payload.objections_received) || 0),
      };
      const { data, error } = await supabase
        .from('daily_kpi_logs')
        .upsert(row, { onConflict: 'user_id,date,channel' })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    getAutoStats: async (userId, agencyId, dateRange, { channel = null } = {}) => {
      const emptyForRange = () => {
        if (dateRange.endDate === addDaysToDateString(dateRange.startDate, 6)) {
          return initKpiAutoStatsByDays(getWeekDayStrings(dateRange.startDate));
        }
        const all = [];
        let cur = dateRange.startDate;
        while (cur <= dateRange.endDate) {
          all.push(cur);
          cur = addDaysToDateString(cur, 1);
        }
        return initKpiAutoStatsByDays(all);
      };

      if (useMock) return emptyForRange();

      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return emptyForRange();

      return fetchKpiAutoStatsForUser(userId, agencyId, dateRange, { channel });
    },

    /** Week-at-a-glance stats for LD / IM / AM (tracker summary row). */
    getChannelWeekSummary: async (userId, agencyId, weekStartDate) => {
      if (useMock) {
        return {
          LD: { calls: 0, apps: 0, apDollars: 0 },
          IM: { presentations: 0, recruits: 0, events: 0 },
          AM: { meetings: 0, referrals: 0, partnerships: 0 },
        };
      }
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) {
        return {
          LD: { calls: 0, apps: 0, apDollars: 0 },
          IM: { presentations: 0, recruits: 0, events: 0 },
          AM: { meetings: 0, referrals: 0, partnerships: 0 },
        };
      }
      const authId = await getSessionUserId();
      if (userId !== authId && !isManagerOrAdminRole(profile.role)) {
        throw new Error('Not authorized to view this agent');
      }
      const weekEnd = addDaysToDateString(weekStartDate, 6);
      const dateRange = { startDate: weekStartDate, endDate: weekEnd };
      const channels = ['LD', 'IM', 'AM'];
      const summary = {};

      for (const ch of channels) {
        const [manualLogs, autoByDay] = await Promise.all([
          fetchKpiManualWeekLogs(userId, agencyId, weekStartDate, { channel: ch }),
          fetchKpiAutoStatsForUser(userId, agencyId, dateRange, { channel: ch }),
        ]);
        const manualByDay = {};
        for (const row of manualLogs) {
          manualByDay[row.date] = row;
        }
        let calls = 0;
        let appts = 0;
        for (const day of Object.values(autoByDay)) {
          calls += day.total_called || 0;
          appts += day.appts_set || 0;
        }
        let apps = 0;
        let apDollars = 0;
        let presentations = 0;
        let referrals = 0;
        for (const row of manualLogs) {
          apps += Number(row.apps_submitted) || 0;
          apDollars += Number(row.ap_submitted_dollars) || 0;
          presentations += Number(row.presentations) || 0;
          referrals += Number(row.referrals_gathered) || 0;
        }
        const recruits = await countLeadsCreatedInRange(agencyId, userId, dateRange, {
          channel: ch,
          leadType: 'recruit',
        });
        const events = await countLeadsCreatedInRange(agencyId, userId, dateRange, {
          channel: ch,
          leadType: 'event',
        });

        summary[ch] = {
          calls,
          apps,
          apDollars,
          presentations,
          recruits,
          events,
          meetings: appts,
          referrals,
          partnerships: apps,
        };
      }

      return summary;
    },

    getWeekForAgent: async (agentId, agencyId, weekStartDate, { channel = null } = {}) => {
      await assertManagerForAgencyKpi(agencyId);
      return fetchKpiManualWeekLogs(agentId, agencyId, weekStartDate, { channel });
    },

    getAgencyWeek: async (agencyId, weekStartDate) => {
      await assertManagerForAgencyKpi(agencyId);
      const { data: roster, error: rosterErr } = await supabase
        .from('users')
        .select('id, full_name, email, is_active')
        .eq('agency_id', agencyId)
        .order('full_name', { ascending: true });
      if (rosterErr) throw rosterErr;
      const activeAgents = (roster ?? []).filter((u) => u.is_active !== false);
      const weekEnd = addDaysToDateString(weekStartDate, 6);
      const dateRange = { startDate: weekStartDate, endDate: weekEnd };

      const agents = await Promise.all(
        activeAgents.map(async (agent) => {
          const [manualLogs, autoByDay] = await Promise.all([
            fetchKpiManualWeekLogs(agent.id, agencyId, weekStartDate),
            fetchKpiAutoStatsForUser(agent.id, agencyId, dateRange),
          ]);
          return {
            userId: agent.id,
            fullName: agent.full_name || agent.email || 'Agent',
            manualLogs,
            autoByDay,
          };
        }),
      );

      return { weekStartDate, weekEndDate: weekEnd, agents };
    },
  },
  roomType: {
    getWeekStats: async (userId, agencyId, weekStartDate, { channel = null } = {}) => {
      const ctx = await fetchRoomTypeContext(userId, agencyId, weekStartDate, { channel });
      if (!ctx) return { byDay: {}, weekly: {}, sparklines: {} };
      const stats = computeWeekStats(ctx.calls, ctx.leadMeta, weekStartDate);
      const sparklines = computeMetricSparklines(
        ctx.calls,
        groupCallsByLeadForApi(ctx.calls),
        ctx.leadMeta,
        weekStartDate,
      );
      return { ...stats, sparklines };
    },

    getFourWeekTrend: async (userId, agencyId, weekStartDate, { channel = null } = {}) => {
      const anchor = weekStartDate || getMondayOfWeekForRoomType();
      const ctx = await fetchRoomTypeContext(userId, agencyId, anchor, { lookbackWeeks: 4, channel });
      if (!ctx) return [];
      return computeFourWeekTrend(ctx.calls, anchor);
    },

    getTypeDistribution: async (userId, agencyId, weekStartDate, { channel = null } = {}) => {
      const ctx = await fetchRoomTypeContext(userId, agencyId, weekStartDate, { channel });
      if (!ctx) return { counts: { RB: 0, EX: 0, SK: 0 }, total: 0, pct: { RB: 0, EX: 0, SK: 0 } };
      return computeTypeDistribution(ctx.calls, weekStartDate);
    },

    getBestCallTimes: async (userId, agencyId, weekStartDate, { channel = null } = {}) => {
      const anchor = weekStartDate || getMondayOfWeekForRoomType();
      const ctx = await fetchRoomTypeContext(userId, agencyId, anchor, { lookbackWeeks: 8, channel });
      if (!ctx) return { rows: [], bestHourByType: {}, hours: [] };
      const weekEnd = addDaysToDateString(anchor, 6);
      const startIso = dateStringToLocalStartIso(anchor);
      const endIso = dateStringToLocalEndIso(weekEnd);
      const weekCalls = (ctx.calls ?? []).filter((c) => {
        const t = new Date(c.called_at).getTime();
        return t >= new Date(startIso).getTime() && t <= new Date(endIso).getTime();
      });
      return computeBestCallTimes(weekCalls);
    },

    getGoals: async (userId, agencyId) => {
      if (useMock) return { ...DEFAULT_GOALS, user_id: userId, agency_id: agencyId };
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return { ...DEFAULT_GOALS };

      const [userRes, agencyRes] = await Promise.all([
        supabase
          .from('room_type_goals')
          .select('*')
          .eq('agency_id', agencyId)
          .eq('user_id', userId)
          .maybeSingle(),
        supabase
          .from('room_type_goals')
          .select('*')
          .eq('agency_id', agencyId)
          .is('user_id', null)
          .maybeSingle(),
      ]);
      if (userRes.error) throw userRes.error;
      if (agencyRes.error) throw agencyRes.error;
      return mergeGoals(userRes.data, agencyRes.data);
    },

    setGoals: async (userId, agencyId, goals, { agencyDefault = false } = {}) => {
      if (useMock) return { ...DEFAULT_GOALS, ...goals };
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) throw new Error('Not authenticated');
      const authId = await getSessionUserId();

      if (agencyDefault) {
        if (!isManagerOrAdminRole(profile.role)) {
          throw new Error('Only managers and admins can set agency-wide goals');
        }
      } else if (userId !== authId && !isManagerOrAdminRole(profile.role)) {
        throw new Error('Not authorized to set goals for this user');
      }

      const row = {
        agency_id: agencyId,
        user_id: agencyDefault ? null : userId,
        talk_time_target_seconds: Number(goals.talk_time_target_seconds) || 120,
        first_call_close_target_pct: Number(goals.first_call_close_target_pct) || 60,
        callback_show_target_pct: Number(goals.callback_show_target_pct) || 40,
        skeptic_save_target_pct: Number(goals.skeptic_save_target_pct) || 35,
        rb_conversion_target_pct: Number(goals.rb_conversion_target_pct) || 60,
        ex_reengage_target_pct: Number(goals.ex_reengage_target_pct) || 40,
      };

      if (agencyDefault) {
        const { data: existing } = await supabase
          .from('room_type_goals')
          .select('id')
          .eq('agency_id', agencyId)
          .is('user_id', null)
          .maybeSingle();
        if (existing?.id) {
          const { data, error } = await supabase
            .from('room_type_goals')
            .update(row)
            .eq('id', existing.id)
            .select()
            .single();
          if (error) throw error;
          return data;
        }
        const { data, error } = await supabase.from('room_type_goals').insert(row).select().single();
        if (error) throw error;
        return data;
      }

      const { data, error } = await supabase
        .from('room_type_goals')
        .upsert(row, { onConflict: 'agency_id,user_id' })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    getCoachingFlags: async (userId, agencyId, weekStartDate, { channel = null } = {}) => {
      const ctx = await fetchRoomTypeContext(userId, agencyId, weekStartDate, { lookbackWeeks: 12, channel });
      if (!ctx) return [];
      const stats = computeWeekStats(ctx.calls, ctx.leadMeta, weekStartDate);
      return computeCoachingFlags(
        stats.weekly,
        ctx.calls,
        groupCallsByLeadForApi(ctx.calls),
        weekStartDate,
      );
    },

    updateLeadRoomType: async (leadId, roomType) => {
      if (useMock) return { id: leadId, room_type: roomType };
      const patch = { room_type: roomType || null };
      const { data, error } = await supabase.from('leads').update(patch).eq('id', leadId).select().single();
      if (error) throw error;
      return data;
    },
  },
  thoughtly: {
    getAgents: async (agencyId) => {
      if (useMock) return [];
      const { data, error } = await supabase
        .from('thoughtly_agents')
        .select('*')
        .eq('agency_id', agencyId)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
    listAgents: async (agencyId) => {
      if (useMock) return [];
      const { data, error } = await supabase
        .from('thoughtly_agents')
        .select('*')
        .eq('agency_id', agencyId)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
    getWebhookConfig: async () => {
      if (useMock) {
        return {
          webhook_base_url: 'https://ogxgxzvxbmnszfxbiuai.supabase.co/functions/v1/thoughtly-webhook',
          secret_suffix: null,
        };
      }
      const { data, error } = await supabase.functions.invoke('thoughtly-config', { body: {} });
      if (error) {
        let msg = error.message || 'Could not load webhook config';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }
      return data;
    },
    createAgent: async (agentData) => {
      if (useMock) throw new Error('Thoughtly agents require Supabase (not available in mock mode).');
      const { data, error } = await supabase
        .from('thoughtly_agents')
        .insert(agentData)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    updateAgent: async (id, updates) => {
      if (useMock) throw new Error('Thoughtly agents require Supabase (not available in mock mode).');
      const { data, error } = await supabase
        .from('thoughtly_agents')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    deleteAgent: async (id) => {
      if (useMock) throw new Error('Thoughtly agents require Supabase (not available in mock mode).');
      const { error } = await supabase.from('thoughtly_agents').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
    triggerCall: async (leadId, thoughtlyAgentTableId) => {
      if (useMock) {
        throw new Error('Thoughtly AI Caller requires Supabase (not available in mock mode).');
      }
      const { data, error } = await supabase.functions.invoke('thoughtly-trigger', {
        body: { lead_id: leadId, thoughtly_agent_table_id: thoughtlyAgentTableId },
      });
      if (error) {
        let msg = error.message || 'Could not trigger Thoughtly call';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }
      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Could not trigger Thoughtly call');
      }
      return data;
    },
    getCalls: async (leadId) => {
      if (useMock) return [];
      const { data, error } = await supabase
        .from('thoughtly_calls')
        .select(
          '*, triggered_by_user:users!thoughtly_calls_triggered_by_fkey(id, full_name, email)',
        )
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    getRecentCalls: async (agencyId, limit = 25) => {
      if (useMock) return [];
      const { data, error } = await supabase
        .from('thoughtly_calls')
        .select('*, lead:leads(id, first_name, last_name, phone), triggered_by_user:users!thoughtly_calls_triggered_by_fkey(id, full_name)')
        .eq('agency_id', agencyId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  },

  funnels: {
    getAll: async (agencyId) => {
      if (useMock) return [];
      if (!agencyId) return [];
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return [];

      const { data, error } = await supabase
        .from('funnels')
        .select('*, funnel_steps(count), funnel_submissions(count)')
        .eq('agency_id', agencyId)
        .order('created_at', { ascending: false });
      if (error) throw error;

      return (data ?? []).map((row) => {
        const { funnel_steps: stepAgg, funnel_submissions: subAgg, ...funnel } = row;
        const stepCount = Array.isArray(stepAgg) && stepAgg[0]?.count != null ? stepAgg[0].count : 0;
        const submissionCount = Array.isArray(subAgg) && subAgg[0]?.count != null ? subAgg[0].count : 0;
        return { ...funnel, step_count: stepCount, submission_count: submissionCount };
      });
    },

    getById: async (id) => {
      if (useMock) return null;
      if (!id) throw new Error('Funnel id is required');

      const { data, error } = await supabase
        .from('funnels')
        .select('*, funnel_steps(*, funnel_blocks(*))')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;

      const steps = (data.funnel_steps ?? [])
        .map((step) => {
          const blocks = (step.funnel_blocks ?? []).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
          const { funnel_blocks: _blocks, ...rest } = step;
          return { ...rest, blocks };
        })
        .sort((a, b) => (a.step_number ?? 0) - (b.step_number ?? 0));

      const { funnel_steps: _steps, ...funnel } = data;
      return { ...funnel, steps };
    },

    slugExists: async (slug, excludeId = null) => {
      if (useMock) return false;
      if (!slug) return false;
      let q = supabase.from('funnels').select('id').eq('slug', slug);
      if (excludeId) q = q.neq('id', excludeId);
      const { data, error } = await q.maybeSingle();
      if (error) throw error;
      return Boolean(data);
    },

    create: async (data) => {
      if (useMock) throw new Error('Funnels require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const agencyId = data.agency_id || profile.agency_id;
      if (agencyId !== profile.agency_id) throw new Error('Not authorized');

      const { data: funnel, error: funnelErr } = await supabase
        .from('funnels')
        .insert({
          agency_id: agencyId,
          created_by: profile.id,
          name: data.name,
          slug: data.slug,
          status: data.status ?? 'draft',
          template_id: data.template_id ?? null,
          sequence_id: data.sequence_id ?? null,
          sequence_trigger: data.sequence_trigger ?? 'optin',
          sequence_channels: data.sequence_channels ?? { email: true, sms: true, ai_caller: false },
          default_entry_step: data.default_entry_step ?? 1,
          settings: data.settings ?? {},
        })
        .select()
        .single();
      if (funnelErr) throw funnelErr;

      const steps = data.steps ?? [];
      for (const step of steps) {
        const { data: stepRow, error: stepErr } = await supabase
          .from('funnel_steps')
          .insert({
            funnel_id: funnel.id,
            agency_id: agencyId,
            step_number: step.step_number,
            step_type: step.step_type,
            title: step.title,
            slug: step.slug,
            is_entry_point: step.is_entry_point ?? false,
          })
          .select()
          .single();
        if (stepErr) throw stepErr;

        const blocks = step.blocks ?? [];
        if (blocks.length) {
          const { error: blockErr } = await supabase.from('funnel_blocks').insert(
            blocks.map((b, i) => ({
              step_id: stepRow.id,
              agency_id: agencyId,
              block_type: b.block_type,
              position: b.position ?? i,
              content: b.content ?? {},
              is_visible: b.is_visible ?? true,
            })),
          );
          if (blockErr) throw blockErr;
        }
      }

      return api.funnels.getById(funnel.id);
    },

    update: async (id, updates) => {
      if (useMock) throw new Error('Funnels require Supabase.');
      if (!id) throw new Error('Funnel id is required');

      const payload = {};
      const fields = [
        'name', 'slug', 'status', 'template_id', 'sequence_id',
        'sequence_trigger', 'sequence_channels', 'default_entry_step', 'settings',
      ];
      for (const key of fields) {
        if (updates[key] !== undefined) payload[key] = updates[key];
      }

      const { data, error } = await supabase
        .from('funnels')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Funnels require Supabase.');
      if (!id) throw new Error('Funnel id is required');
      const { error } = await supabase.from('funnels').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },

    duplicateFunnel: async (id) => {
      if (useMock) throw new Error('Funnels require Supabase.');
      const source = await api.funnels.getById(id);
      if (!source) throw new Error('Funnel not found');

      let baseSlug = `${source.slug}-copy`;
      let slug = baseSlug;
      let n = 1;
      while (await api.funnels.slugExists(slug)) {
        n += 1;
        slug = `${baseSlug}-${n}`;
      }

      const steps = (source.steps ?? []).map((step) => ({
        step_number: step.step_number,
        step_type: step.step_type,
        title: step.title,
        slug: step.slug,
        is_entry_point: step.is_entry_point,
        blocks: (step.blocks ?? []).map((b) => ({
          block_type: b.block_type,
          position: b.position,
          content: b.content,
          is_visible: b.is_visible,
        })),
      }));

      return api.funnels.create({
        agency_id: source.agency_id,
        name: `Copy of ${source.name}`,
        slug,
        status: 'draft',
        template_id: source.template_id,
        sequence_id: source.sequence_id,
        sequence_trigger: source.sequence_trigger,
        sequence_channels: source.sequence_channels,
        default_entry_step: source.default_entry_step,
        settings: source.settings,
        steps,
      });
    },

    exportJson: async (id) => {
      const funnel = await api.funnels.getById(id);
      if (!funnel) throw new Error('Funnel not found');
      const { steps, ...meta } = funnel;
      return {
        version: 1,
        exported_at: new Date().toISOString(),
        funnel: meta,
        steps: (steps ?? []).map(({ blocks, ...step }) => ({
          ...step,
          blocks: (blocks ?? []).map(({ content, block_type, position, is_visible }) => ({
            block_type,
            position,
            content,
            is_visible,
          })),
        })),
      };
    },

    importJson: async (agencyId, json) => {
      if (useMock) throw new Error('Funnels require Supabase.');
      const payload = typeof json === 'string' ? JSON.parse(json) : json;
      const meta = payload.funnel || payload;
      const steps = payload.steps || [];

      let slug = meta.slug || slugify(meta.name);
      if (await api.funnels.slugExists(slug)) {
        slug = `${slug}-${Date.now()}`;
      }

      return api.funnels.create({
        agency_id: agencyId,
        name: meta.name || 'Imported Funnel',
        slug,
        status: meta.status ?? 'draft',
        sequence_id: meta.sequence_id ?? null,
        sequence_trigger: meta.sequence_trigger ?? 'optin',
        sequence_channels: meta.sequence_channels ?? { email: true, sms: true, ai_caller: false },
        default_entry_step: meta.default_entry_step ?? 1,
        settings: meta.settings ?? {},
        steps: steps.map((s) => ({
          step_number: s.step_number,
          step_type: s.step_type,
          title: s.title,
          slug: s.slug,
          is_entry_point: s.is_entry_point,
          blocks: s.blocks ?? [],
        })),
      });
    },

    replaceFromJson: async (id, json) => {
      if (useMock) throw new Error('Funnels require Supabase.');
      const payload = typeof json === 'string' ? JSON.parse(json) : json;
      const meta = payload.funnel || payload;
      const steps = payload.steps || [];

      const existing = await api.funnels.getById(id);
      if (!existing) throw new Error('Funnel not found');

      await api.funnels.update(id, {
        name: meta.name ?? existing.name,
        status: meta.status ?? existing.status,
        sequence_id: meta.sequence_id ?? existing.sequence_id,
        sequence_trigger: meta.sequence_trigger ?? existing.sequence_trigger,
        sequence_channels: meta.sequence_channels ?? existing.sequence_channels,
        default_entry_step: meta.default_entry_step ?? existing.default_entry_step,
        settings: meta.settings ?? existing.settings,
      });

      for (const step of existing.steps ?? []) {
        await api.funnelSteps.delete(step.id);
      }

      for (const step of steps) {
        const { data: stepRow, error: stepErr } = await supabase
          .from('funnel_steps')
          .insert({
            funnel_id: id,
            agency_id: existing.agency_id,
            step_number: step.step_number,
            step_type: step.step_type,
            title: step.title,
            slug: step.slug,
            is_entry_point: step.is_entry_point ?? false,
          })
          .select()
          .single();
        if (stepErr) throw stepErr;

        const blocks = step.blocks ?? [];
        if (blocks.length) {
          const { error: blockErr } = await supabase.from('funnel_blocks').insert(
            blocks.map((b, i) => ({
              step_id: stepRow.id,
              agency_id: existing.agency_id,
              block_type: b.block_type,
              position: b.position ?? i,
              content: b.content ?? {},
              is_visible: b.is_visible ?? true,
            })),
          );
          if (blockErr) throw blockErr;
        }
      }

      return api.funnels.getById(id);
    },
  },

  funnelSteps: {
    create: async (stepData) => {
      if (useMock) throw new Error('Funnel steps require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      let stepNumber = stepData.step_number;
      if (stepNumber == null) {
        const { data: existing, error: listErr } = await supabase
          .from('funnel_steps')
          .select('step_number')
          .eq('funnel_id', stepData.funnel_id)
          .order('step_number', { ascending: false })
          .limit(1);
        if (listErr) throw listErr;
        stepNumber = (existing?.[0]?.step_number ?? 0) + 1;
      }

      const { data, error } = await supabase
        .from('funnel_steps')
        .insert({
          funnel_id: stepData.funnel_id,
          agency_id: stepData.agency_id || profile.agency_id,
          step_number: stepNumber,
          step_type: stepData.step_type,
          title: stepData.title,
          slug: stepData.slug,
          is_entry_point: stepData.is_entry_point ?? false,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    update: async (id, updates) => {
      if (useMock) throw new Error('Funnel steps require Supabase.');
      const payload = {};
      const fields = ['step_number', 'step_type', 'title', 'slug', 'is_entry_point'];
      for (const key of fields) {
        if (updates[key] !== undefined) payload[key] = updates[key];
      }
      const { data, error } = await supabase
        .from('funnel_steps')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Funnel steps require Supabase.');
      const { error } = await supabase.from('funnel_steps').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },

    reorder: async (funnelId, orderedStepIds) => {
      if (useMock) throw new Error('Funnel steps require Supabase.');
      for (let i = 0; i < orderedStepIds.length; i += 1) {
        const { error } = await supabase
          .from('funnel_steps')
          .update({ step_number: i + 1 })
          .eq('id', orderedStepIds[i])
          .eq('funnel_id', funnelId);
        if (error) throw error;
      }
      return { success: true };
    },
  },

  funnelBlocks: {
    create: async (blockData) => {
      if (useMock) throw new Error('Funnel blocks require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      let position = blockData.position;
      if (position == null) {
        const { data: existing, error: listErr } = await supabase
          .from('funnel_blocks')
          .select('position')
          .eq('step_id', blockData.step_id)
          .order('position', { ascending: false })
          .limit(1);
        if (listErr) throw listErr;
        position = (existing?.[0]?.position ?? -1) + 1;
      }

      const { data, error } = await supabase
        .from('funnel_blocks')
        .insert({
          step_id: blockData.step_id,
          agency_id: blockData.agency_id || profile.agency_id,
          block_type: blockData.block_type,
          position,
          content: blockData.content ?? {},
          is_visible: blockData.is_visible ?? true,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    update: async (id, updates) => {
      if (useMock) throw new Error('Funnel blocks require Supabase.');
      const payload = {};
      if (updates.content !== undefined) payload.content = updates.content;
      if (updates.is_visible !== undefined) payload.is_visible = updates.is_visible;
      if (updates.position !== undefined) payload.position = updates.position;
      if (updates.block_type !== undefined) payload.block_type = updates.block_type;

      const { data, error } = await supabase
        .from('funnel_blocks')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Funnel blocks require Supabase.');
      const { error } = await supabase.from('funnel_blocks').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },

    reorder: async (stepId, orderedBlockIds) => {
      if (useMock) throw new Error('Funnel blocks require Supabase.');
      for (let i = 0; i < orderedBlockIds.length; i += 1) {
        const { error } = await supabase
          .from('funnel_blocks')
          .update({ position: i })
          .eq('id', orderedBlockIds[i])
          .eq('step_id', stepId);
        if (error) throw error;
      }
      return { success: true };
    },
  },

  funnelSubmissions: {
    getByFunnel: async (funnelId, filters = {}) => {
      if (useMock) return [];
      if (!funnelId) return [];

      const page = filters.page ?? 1;
      const pageSize = filters.pageSize ?? 25;
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let q = supabase
        .from('funnel_submissions')
        .select('*', { count: 'exact' })
        .eq('funnel_id', funnelId)
        .order('created_at', { ascending: false });

      const { data, error, count } = await q.range(from, to);
      if (error) throw error;
      return { data: data ?? [], count: count ?? 0, page, pageSize };
    },
  },

  funnelTemplates: {
    getAll: async () => {
      if (useMock) return [];
      const { data, error } = await supabase
        .from('funnel_templates')
        .select('*')
        .order('is_system', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((t) => ({
        ...t,
        step_count: Array.isArray(t.funnel_json?.steps) ? t.funnel_json.steps.length : 0,
      }));
    },

    saveAsTemplate: async (funnelId, name, description) => {
      if (useMock) throw new Error('Funnel templates require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const funnel = await api.funnels.exportJson(funnelId);
      const { data, error } = await supabase
        .from('funnel_templates')
        .insert({
          name,
          description: description || null,
          funnel_json: funnel,
          is_system: false,
          created_by: profile.id,
          agency_id: profile.agency_id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Funnel templates require Supabase.');
      const { error } = await supabase.from('funnel_templates').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  monitoredProjects: {
    async getAll() {
      if (useMock) return [];

      const { data: projects, error } = await supabase
        .from('monitored_projects')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      if (!projects?.length) return [];

      const projectIds = projects.map((p) => p.id);
      const { data: logs, error: logsError } = await supabase
        .from('project_ping_logs')
        .select('*')
        .in('monitored_project_id', projectIds)
        .order('pinged_at', { ascending: false });
      if (logsError) throw logsError;

      const latestByProject = {};
      for (const log of logs ?? []) {
        if (!latestByProject[log.monitored_project_id]) {
          latestByProject[log.monitored_project_id] = log;
        }
      }

      return projects.map((p) => ({
        ...p,
        last_ping: latestByProject[p.id] ?? null,
      }));
    },

    async getById(id) {
      if (useMock) return null;
      if (!id) throw new Error('Project id is required');

      const { data, error } = await supabase
        .from('monitored_projects')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data;
    },

    async create(data) {
      if (useMock) throw new Error('Monitored projects require Supabase.');

      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');

      const { data: row, error } = await supabase
        .from('monitored_projects')
        .insert({ ...data, created_by: userId })
        .select()
        .single();
      if (error) throw error;
      return row;
    },

    async update(id, updates) {
      if (useMock) throw new Error('Monitored projects require Supabase.');
      if (!id) throw new Error('Project id is required');

      const { data, error } = await supabase
        .from('monitored_projects')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Save failed — project not found or permission denied');
      return data;
    },

    async delete(id) {
      if (useMock) throw new Error('Monitored projects require Supabase.');
      if (!id) throw new Error('Project id is required');

      const { error } = await supabase
        .from('monitored_projects')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    },

    async getPingHistory(monitoredProjectId, limit = 20) {
      if (useMock) return [];
      if (!monitoredProjectId) throw new Error('Project id is required');

      const { data, error } = await supabase
        .from('project_ping_logs')
        .select('*')
        .eq('monitored_project_id', monitoredProjectId)
        .order('pinged_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },

    async testPing(id) {
      if (useMock) throw new Error('Monitored projects require Supabase.');
      if (!id) throw new Error('Project id is required');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const { data, error } = await supabase.functions.invoke('keepalive-ping', {
        body: { monitored_project_id: id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        let msg = error.message || 'Ping failed';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try {
            const errBody = await error.context.json();
            if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
          } catch {
            /* keep msg */
          }
        }
        throw new Error(msg);
      }

      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Ping failed');
      }

      const row = Array.isArray(data?.results)
        ? data.results.find((r) => r.monitored_project_id === id) ?? data.results[0]
        : null;

      if (!row) {
        return { success: false, message: 'No ping result returned', http_status: null };
      }

      const ok = row.status === 'success';
      return {
        success: ok,
        message: ok
          ? `Ping successful (HTTP ${row.http_status ?? 200})`
          : (row.error_message || 'Ping failed'),
        http_status: row.http_status ?? null,
      };
    },
  },

  agentPublicPages: {
    /** Matches agent_public_pages.slug CHECK: ^[a-z0-9]([a-z0-9-]{0,48}[a-z0-9])?$ */
    isValidSlug(slug) {
      if (typeof slug !== 'string') return false;
      return /^[a-z0-9]([a-z0-9-]{0,48}[a-z0-9])?$/.test(slug);
    },

    translateError(error) {
      const msg = String(error?.message || error || '');
      const code = error?.code;
      if (code === '23505' || /duplicate key|unique constraint/i.test(msg)) {
        return 'That slug is already taken. Please choose another.';
      }
      if (/slug_not_reserved|is_reserved_slug|reserved/i.test(msg)) {
        return 'That slug is reserved for the site. Please choose another.';
      }
      if (/slug_format|check constraint.*slug/i.test(msg)) {
        return 'Slug must be 1–50 characters: lowercase letters, numbers, and hyphens only (no leading/trailing hyphen).';
      }
      return msg || 'Something went wrong. Please try again.';
    },

    getMine: async () => {
      if (useMock) return null;
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('agent_public_pages')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },

    getByUserId: async (targetUserId) => {
      if (useMock) return null;
      if (!targetUserId) return null;
      const { data, error } = await supabase
        .from('agent_public_pages')
        .select('id, user_id, slug, is_public')
        .eq('user_id', targetUserId)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },

    checkSlugAvailable: async (slug) => {
      if (useMock) return true;
      const normalized = String(slug ?? '').trim().toLowerCase();
      if (!api.agentPublicPages.isValidSlug(normalized)) return false;

      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');

      const { count, error } = await supabase
        .from('agent_public_pages')
        .select('id', { count: 'exact', head: true })
        .ilike('slug', normalized)
        .neq('user_id', userId);
      if (error) throw error;
      return (count ?? 0) === 0;
    },

    create: async (data) => {
      if (useMock) throw new Error('Public profile requires Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');

      const slug = String(data?.slug ?? '').trim().toLowerCase();
      if (!api.agentPublicPages.isValidSlug(slug)) {
        throw new Error(
          'Slug must be 1–50 characters: lowercase letters, numbers, and hyphens only (no leading/trailing hyphen).',
        );
      }

      const { data: row, error } = await supabase
        .from('agent_public_pages')
        .insert({
          user_id: userId,
          slug,
          resources: [],
          is_public: false,
        })
        .select()
        .single();
      if (error) {
        const err = new Error(api.agentPublicPages.translateError(error));
        err.cause = error;
        throw err;
      }
      return row;
    },

    update: async (id, updates) => {
      if (useMock) throw new Error('Public profile requires Supabase.');
      if (!id) throw new Error('Public page id is required');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');

      const payload = {};
      if (updates.slug !== undefined) {
        const slug = String(updates.slug ?? '').trim().toLowerCase();
        if (!api.agentPublicPages.isValidSlug(slug)) {
          throw new Error(
            'Slug must be 1–50 characters: lowercase letters, numbers, and hyphens only (no leading/trailing hyphen).',
          );
        }
        payload.slug = slug;
      }
      if (updates.resources !== undefined) {
        if (!Array.isArray(updates.resources)) {
          throw new Error('Resources must be an array.');
        }
        if (updates.resources.length > 3) {
          throw new Error('You can have at most 3 resources.');
        }
        payload.resources = updates.resources;
      }
      if (updates.is_public !== undefined) {
        payload.is_public = Boolean(updates.is_public);
      }

      const { data: row, error } = await supabase
        .from('agent_public_pages')
        .update(payload)
        .eq('id', id)
        .eq('user_id', userId)
        .select()
        .single();
      if (error) {
        const err = new Error(api.agentPublicPages.translateError(error));
        err.cause = error;
        throw err;
      }
      return row;
    },
  },

  agentTestimonials: {
    getForMyPage: async () => {
      if (useMock) return [];
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');

      const { data: page, error: pageError } = await supabase
        .from('agent_public_pages')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      if (pageError) throw pageError;
      if (!page?.id) return [];

      const { data, error } = await supabase
        .from('agent_testimonials')
        .select('*')
        .eq('agent_public_page_id', page.id)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const rows = data ?? [];
      const statusRank = { pending: 0, approved: 1, rejected: 2 };
      return [...rows].sort((a, b) => {
        const rankA = statusRank[a.status] ?? 9;
        const rankB = statusRank[b.status] ?? 9;
        if (rankA !== rankB) return rankA - rankB;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    },

    updateStatus: async (id, status) => {
      if (useMock) throw new Error('Testimonials require Supabase.');
      if (!id) throw new Error('Testimonial id is required');
      if (status !== 'approved' && status !== 'rejected') {
        throw new Error('Status must be approved or rejected');
      }
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');

      const { data: row, error } = await supabase
        .from('agent_testimonials')
        .update({
          status,
          reviewed_at: new Date().toISOString(),
          reviewed_by: userId,
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return row;
    },
  },

  licensingResources: {
    getAll: async (agencyId) => {
      if (useMock) return [];
      if (!agencyId) return [];
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return [];

      const { data, error } = await supabase
        .from('licensing_resources')
        .select('*')
        .eq('agency_id', agencyId)
        .order('category', { ascending: true })
        .order('step_order', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },

    create: async (data) => {
      if (useMock) throw new Error('Licensing resources require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const agencyId = data.agency_id || profile.agency_id;
      if (agencyId !== profile.agency_id) throw new Error('Not authorized');

      const { data: row, error } = await supabase
        .from('licensing_resources')
        .insert({
          agency_id: agencyId,
          category: data.category,
          step_order: data.step_order ?? 0,
          phase: data.phase ?? null,
          title: data.title,
          subtitle: data.subtitle ?? null,
          instructions: data.instructions ?? null,
          url: data.url ?? null,
          links: Array.isArray(data.links) ? data.links : [],
          applies_to_states: data.applies_to_states ?? ['ALL'],
          is_active: data.is_active ?? true,
          created_by: profile.id,
        })
        .select()
        .single();
      if (error) throw error;
      return row;
    },

    update: async (id, updates) => {
      if (useMock) throw new Error('Licensing resources require Supabase.');
      if (!id) throw new Error('Resource id is required');

      const payload = {};
      if (updates.category !== undefined) payload.category = updates.category;
      if (updates.step_order !== undefined) payload.step_order = updates.step_order;
      if (updates.phase !== undefined) payload.phase = updates.phase;
      if (updates.title !== undefined) payload.title = updates.title;
      if (updates.subtitle !== undefined) payload.subtitle = updates.subtitle;
      if (updates.instructions !== undefined) payload.instructions = updates.instructions;
      if (updates.url !== undefined) payload.url = updates.url;
      if (updates.links !== undefined) payload.links = Array.isArray(updates.links) ? updates.links : [];
      if (updates.applies_to_states !== undefined) payload.applies_to_states = updates.applies_to_states;
      if (updates.is_active !== undefined) payload.is_active = updates.is_active;

      const { data: row, error } = await supabase
        .from('licensing_resources')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return row;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Licensing resources require Supabase.');
      if (!id) throw new Error('Resource id is required');

      const { error } = await supabase.from('licensing_resources').delete().eq('id', id);
      if (error) throw error;
    },

    // Caller scopes `items` to one phase (licensing_step) or the full
    // general_link/provider list; renumbers step_order only within that set.
    reorder: async (items) => {
      if (useMock) throw new Error('Licensing resources require Supabase.');
      if (!items?.length) return;

      for (const item of items) {
        if (!item?.id) continue;
        const { error } = await supabase
          .from('licensing_resources')
          .update({ step_order: item.step_order })
          .eq('id', item.id);
        if (error) throw error;
      }
    },
  },

  applications,
  financialAnalysis,
  preUnderwritingLogs,
  estateIntakeLogs,
  bootcampComments,

  pmEvents: {
    getAll: async (agencyId) => {
      if (useMock) return [];
      if (!agencyId) return [];
      const profile = await assertAgencyAccess(agencyId);
      if (!profile) return [];

      const { data, error } = await supabase
        .from('pm_events')
        .select(`
          *,
          agent:agent_id(id, full_name, email),
          marketing_director:marketing_director_id(id, full_name, email),
          pm_event_sessions(*),
          pm_event_registrations(count)
        `)
        .eq('agency_id', agencyId)
        .order('created_at', { ascending: false });
      if (error) throw error;

      return (data ?? []).map((row) => {
        const { pm_event_sessions: sessRows, pm_event_registrations: regAgg, ...event } = row;
        const sessions = (Array.isArray(sessRows) ? sessRows : []).slice().sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.starts_at).localeCompare(String(b.starts_at)),
        );
        const registrationCount = Array.isArray(regAgg) && regAgg[0]?.count != null ? regAgg[0].count : 0;
        return {
          ...event,
          sessions,
          session_count: sessions.length,
          registration_count: registrationCount,
        };
      });
    },

    getById: async (id) => {
      if (useMock) return null;
      if (!id) throw new Error('Event id is required');

      const { data, error } = await supabase
        .from('pm_events')
        .select(`
          *,
          agent:agent_id(id, full_name, email, title, headshot_url),
          marketing_director:marketing_director_id(id, full_name, email),
          pm_event_sessions(*),
          pm_event_speakers(*, user:user_id(id, full_name, title, headshot_url), partner:partner_id(id, name, logo_url)),
          pm_event_partners(*),
          pm_event_invitation_links(*, user:user_id(id, full_name, email))
        `)
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;

      const sessions = (data.pm_event_sessions ?? []).sort(
        (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.starts_at).localeCompare(String(b.starts_at)),
      );
      const speakers = (data.pm_event_speakers ?? []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      const partners = (data.pm_event_partners ?? []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      const invitation_links = (data.pm_event_invitation_links ?? []).sort((a, b) =>
        String(b.created_at).localeCompare(String(a.created_at)),
      );

      const {
        pm_event_sessions: _s,
        pm_event_speakers: _sp,
        pm_event_partners: _p,
        pm_event_invitation_links: _i,
        ...event
      } = data;
      return { ...event, sessions, speakers, partners, invitation_links };
    },

    slugExists: async (agencyId, slug, excludeId = null) => {
      if (useMock) return false;
      if (!agencyId || !slug) return false;
      let q = supabase.from('pm_events').select('id').eq('agency_id', agencyId).eq('slug', slug);
      if (excludeId) q = q.neq('id', excludeId);
      const { data, error } = await q.maybeSingle();
      if (error) throw error;
      return Boolean(data);
    },

    create: async (payload) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const agencyId = payload.agency_id || profile.agency_id;
      if (agencyId !== profile.agency_id && profile.role !== 'super_admin') {
        throw new Error('Not authorized');
      }

      const { data, error } = await supabase
        .from('pm_events')
        .insert({
          agency_id: agencyId,
          agent_id: Object.prototype.hasOwnProperty.call(payload, 'agent_id')
            ? (payload.agent_id || null)
            : profile.id,
          marketing_director_id: Object.prototype.hasOwnProperty.call(payload, 'marketing_director_id')
            ? (payload.marketing_director_id || null)
            : null,
          title: payload.title,
          short_description: payload.short_description || null,
          slug: payload.slug,
          custom_domain: payload.custom_domain || null,
          language: payload.language ?? 'en',
          format: payload.format ?? 'in_person',
          requires_payment: payload.requires_payment ?? false,
          price: payload.price === '' ? null : (payload.price ?? null),
          early_bird_price: payload.early_bird_price === '' ? null : (payload.early_bird_price ?? null),
          early_bird_deadline: payload.early_bird_deadline === '' ? null : (payload.early_bird_deadline ?? null),
          late_price: payload.late_price === '' ? null : (payload.late_price ?? null),
          is_main_event: payload.is_main_event ?? false,
          landing_template_id: payload.landing_template_id ?? 'classic',
          landing_headline: payload.landing_headline?.trim() || null,
          landing_badge: payload.landing_badge?.trim() || null,
          registration_scope: payload.registration_scope === 'all_sessions' ? 'all_sessions' : 'single_session',
          landing_sections: Array.isArray(payload.landing_sections) ? payload.landing_sections : [],
          theme: payload.theme ?? 'default',
          density: payload.density ?? 'comfortable',
          hero_style: payload.hero_style ?? 'standard',
          hero_image_url: payload.hero_image_url || null,
          allow_self_serve_guests: payload.allow_self_serve_guests ?? false,
          status: payload.status ?? 'draft',
          pipeline_event_type: payload.pipeline_event_type || null,
          business_launch_assignee_name: payload.business_launch_assignee_name || null,
          business_launch_assignee_phone: payload.business_launch_assignee_phone || null,
          business_launch_assignee_email: payload.business_launch_assignee_email || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    update: async (id, updates) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      if (!id) throw new Error('Event id is required');
      const allowed = [
        'title', 'short_description', 'slug', 'custom_domain', 'language', 'format', 'requires_payment', 'price',
        'early_bird_price', 'early_bird_deadline', 'late_price',
        'is_main_event', 'landing_template_id', 'landing_headline', 'landing_badge',
        'registration_scope', 'landing_sections', 'theme', 'density', 'hero_style',
        'hero_image_url', 'allow_self_serve_guests', 'status', 'agent_id',
        'marketing_director_id',
        'pipeline_event_type',
        'business_launch_assignee_name', 'business_launch_assignee_phone', 'business_launch_assignee_email',
      ];
      const patch = {};
      for (const key of allowed) {
        if (updates[key] !== undefined) patch[key] = updates[key];
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'custom_domain')) {
        patch.custom_domain = patch.custom_domain || null;
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'pipeline_event_type')) {
        patch.pipeline_event_type = patch.pipeline_event_type || null;
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'agent_id')) {
        patch.agent_id = patch.agent_id || null;
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'marketing_director_id')) {
        patch.marketing_director_id = patch.marketing_director_id || null;
      }
      ['business_launch_assignee_name', 'business_launch_assignee_phone', 'business_launch_assignee_email'].forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(patch, key)) {
          patch[key] = patch[key] || null;
        }
      });
      // Empty inputs must reach Postgres as NULL, not '' — numeric/timestamptz reject ''.
      ['early_bird_price', 'early_bird_deadline', 'late_price', 'price'].forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(patch, key) && patch[key] === '') {
          patch[key] = null;
        }
      });
      ['landing_headline', 'landing_badge'].forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(patch, key)) {
          const text = String(patch[key] ?? '').trim();
          patch[key] = text || null;
        }
      });
      if (Object.prototype.hasOwnProperty.call(patch, 'registration_scope')) {
        patch.registration_scope = patch.registration_scope === 'all_sessions' ? 'all_sessions' : 'single_session';
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'landing_sections') && !Array.isArray(patch.landing_sections)) {
        patch.landing_sections = [];
      }
      const { data, error } = await supabase
        .from('pm_events')
        .update(patch)
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      if (!id) throw new Error('Event id is required');
      const { error } = await supabase.from('pm_events').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  pmEventSessions: {
    create: async (payload) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('pm_event_sessions')
        .insert({
          event_id: payload.event_id,
          agency_id: payload.agency_id || profile.agency_id,
          title: payload.title ?? null,
          starts_at: payload.starts_at,
          ends_at: payload.ends_at ?? null,
          timezone: payload.timezone ?? 'America/New_York',
          capacity: payload.capacity ?? null,
          location_text: payload.location_text ?? null,
          meeting_url: payload.meeting_url ?? null,
          requires_payment: payload.requires_payment ?? false,
          sort_order: payload.sort_order ?? 0,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    update: async (id, updates) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const allowed = [
        'title', 'starts_at', 'ends_at', 'timezone', 'capacity',
        'location_text', 'meeting_url', 'requires_payment', 'sort_order',
      ];
      const patch = {};
      for (const key of allowed) {
        if (updates[key] !== undefined) patch[key] = updates[key];
      }
      const { data, error } = await supabase
        .from('pm_event_sessions')
        .update(patch)
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const { error } = await supabase.from('pm_event_sessions').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  pmEventSpeakers: {
    create: async (payload) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('pm_event_speakers')
        .insert({
          event_id: payload.event_id,
          agency_id: payload.agency_id || profile.agency_id,
          user_id: payload.user_id ?? null,
          role: payload.role ?? 'speaker',
          name: payload.name ?? null,
          photo_url: payload.photo_url ?? null,
          title: payload.title ?? null,
          bio: payload.bio ?? null,
          partner_id: payload.partner_id ?? null,
          sort_order: payload.sort_order ?? 0,
          is_mc: payload.is_mc === true,
        })
        .select('*, user:user_id(id, full_name, title, headshot_url), partner:partner_id(id, name, logo_url)')
        .single();
      if (error) throw error;
      return data;
    },

    update: async (id, updates) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const allowed = ['user_id', 'role', 'name', 'photo_url', 'title', 'bio', 'partner_id', 'sort_order', 'is_mc'];
      const patch = {};
      for (const key of allowed) {
        if (updates[key] !== undefined) patch[key] = updates[key];
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'is_mc')) {
        patch.is_mc = patch.is_mc === true;
      }
      const { data, error } = await supabase
        .from('pm_event_speakers')
        .update(patch)
        .eq('id', id)
        .select('*, user:user_id(id, full_name, title, headshot_url), partner:partner_id(id, name, logo_url)')
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const { error } = await supabase.from('pm_event_speakers').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  pmEventPartners: {
    create: async (payload) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('pm_event_partners')
        .insert({
          event_id: payload.event_id,
          agency_id: payload.agency_id || profile.agency_id,
          name: payload.name,
          logo_url: payload.logo_url ?? null,
          sort_order: payload.sort_order ?? 0,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    update: async (id, updates) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const allowed = ['name', 'logo_url', 'sort_order'];
      const patch = {};
      for (const key of allowed) {
        if (updates[key] !== undefined) patch[key] = updates[key];
      }
      const { data, error } = await supabase
        .from('pm_event_partners')
        .update(patch)
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const { error } = await supabase.from('pm_event_partners').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  pmEventEmailSettings: {
    getByEvent: async (eventId) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      if (!eventId) return null;
      const { data, error } = await supabase
        .from('pm_event_email_settings')
        .select('*')
        .eq('event_id', eventId)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },

    create: async (payload) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data, error } = await supabase
        .from('pm_event_email_settings')
        .insert({
          event_id: payload.event_id,
          agency_id: payload.agency_id || profile.agency_id,
          confirmation_enabled: payload.confirmation_enabled ?? true,
          confirmation_subject: payload.confirmation_subject ?? null,
          confirmation_body: payload.confirmation_body ?? null,
          reminder_enabled: payload.reminder_enabled ?? true,
          reminder_subject: payload.reminder_subject ?? null,
          reminder_body: payload.reminder_body ?? null,
          reminder_offsets: payload.reminder_offsets ?? [{ hours_before: 24 }],
          paid_confirmation_enabled: payload.paid_confirmation_enabled ?? true,
          paid_confirmation_subject: payload.paid_confirmation_subject ?? null,
          paid_confirmation_body: payload.paid_confirmation_body ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    update: async (id, updates) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const allowed = [
        'confirmation_enabled',
        'confirmation_subject',
        'confirmation_body',
        'reminder_enabled',
        'reminder_subject',
        'reminder_body',
        'reminder_offsets',
        'paid_confirmation_enabled',
        'paid_confirmation_subject',
        'paid_confirmation_body',
      ];
      const patch = {};
      for (const key of allowed) {
        if (updates[key] !== undefined) patch[key] = updates[key];
      }
      const { data, error } = await supabase
        .from('pm_event_email_settings')
        .update(patch)
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },

    /** Upsert full settings row on event_id (one row per event). */
    upsert: async (payload) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');
      if (!payload?.event_id) throw new Error('event_id is required');

      const row = {
        event_id: payload.event_id,
        agency_id: payload.agency_id || profile.agency_id,
        confirmation_enabled: payload.confirmation_enabled ?? true,
        confirmation_subject: payload.confirmation_subject ?? null,
        confirmation_body: payload.confirmation_body ?? null,
        reminder_enabled: payload.reminder_enabled ?? true,
        reminder_subject: payload.reminder_subject ?? null,
        reminder_body: payload.reminder_body ?? null,
        reminder_offsets: payload.reminder_offsets ?? [{ hours_before: 24 }],
      };

      // Paid-confirmation fields are only written when the caller supplies them.
      // A merge-duplicates upsert updates just the columns present in the row, so
      // omitting them leaves a free event's seeded paid template untouched instead
      // of nulling it out on every Emails-tab save.
      ['paid_confirmation_enabled', 'paid_confirmation_subject', 'paid_confirmation_body'].forEach((key) => {
        if (payload[key] !== undefined) row[key] = payload[key];
      });

      const { data, error } = await supabase
        .from('pm_event_email_settings')
        .upsert(row, { onConflict: 'event_id' })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const { error } = await supabase.from('pm_event_email_settings').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  pmEventInvitationLinks: {
    create: async (payload) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      let slug = (payload.slug || '').trim().toLowerCase();
      if (!slug && payload.prefer_agent_public_slug) {
        const { data: page } = await supabase
          .from('agent_public_pages')
          .select('slug')
          .eq('user_id', payload.user_id || profile.id)
          .maybeSingle();
        if (page?.slug) slug = page.slug;
      }
      if (!slug) throw new Error('Invitation slug is required');
      if (!api.agentPublicPages.isValidSlug(slug)) {
        throw new Error(
          'Slug must be 1–50 characters: lowercase letters, numbers, and hyphens only (no leading/trailing hyphen).',
        );
      }

      const placeholderName = String(payload.placeholder_owner_name || '').trim() || null;
      const placeholderTitle = String(payload.placeholder_owner_title || '').trim() || null;
      const showInviterCard = payload.show_inviter_card !== false;

      let ownerUserId = null;
      let mentorsUserId = null;
      if (placeholderName) {
        // Display-only owner — XOR requires both FKs null
      } else if (payload.mentors_user_id) {
        mentorsUserId = payload.mentors_user_id;
      } else {
        ownerUserId = payload.user_id || profile.id;
      }

      const { data, error } = await supabase
        .from('pm_event_invitation_links')
        .insert({
          event_id: payload.event_id,
          agency_id: payload.agency_id || profile.agency_id,
          user_id: ownerUserId,
          mentors_user_id: mentorsUserId,
          placeholder_owner_name: placeholderName,
          placeholder_owner_title: placeholderTitle,
          show_inviter_card: showInviterCard,
          slug,
          created_by: profile.id,
        })
        .select('*, user:user_id(id, full_name, email)')
        .single();
      if (error) {
        const msg = String(error?.message || '');
        if (error?.code === '23505' || /duplicate key|unique constraint/i.test(msg)) {
          throw new Error('That invite slug is already used for this event. Please choose another.');
        }
        throw error;
      }
      return data;
    },

    getByEventAndUser: async (eventId, userId) => {
      if (useMock) return null;
      if (!eventId || !userId) return null;
      const { data, error } = await supabase
        .from('pm_event_invitation_links')
        .select('*')
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },

    update: async (id, updates = {}) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      if (!id) throw new Error('Invitation link id is required');

      const patch = {};
      if ('user_id' in updates) patch.user_id = updates.user_id || null;
      if ('mentors_user_id' in updates) patch.mentors_user_id = updates.mentors_user_id || null;
      if ('placeholder_owner_name' in updates) {
        const name = String(updates.placeholder_owner_name || '').trim();
        patch.placeholder_owner_name = name || null;
      }
      if ('placeholder_owner_title' in updates) {
        const title = String(updates.placeholder_owner_title || '').trim();
        patch.placeholder_owner_title = title || null;
      }
      if ('show_inviter_card' in updates) {
        patch.show_inviter_card = Boolean(updates.show_inviter_card);
      }

      if (Object.keys(patch).length === 0) {
        throw new Error('No invitation link fields to update');
      }

      const { data, error } = await supabase
        .from('pm_event_invitation_links')
        .update(patch)
        .eq('id', id)
        .select('*, user:user_id(id, full_name, email)')
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Invitation link not found or permission denied');
      return data;
    },

    delete: async (id) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const { error } = await supabase.from('pm_event_invitation_links').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  pmEventRegistrations: {
    getByEvent: async (eventId) => {
      if (useMock) return [];
      if (!eventId) return [];
      const { data, error } = await supabase
        .from('pm_event_registrations')
        .select(`
          *,
          session:session_id(id, title, starts_at),
          invitation_link:invitation_link_id(id, slug, user_id, user:user_id(id, full_name)),
          lead:lead_id(
            id,
            status,
            created_by,
            exclusivity_status,
            owner:created_by(id, full_name)
          )
        `)
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },

    createDoor: async (payload) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data: event, error: eventErr } = await supabase
        .from('pm_events')
        .select('id, agency_id, agent_id, marketing_director_id, title, business_launch_assignee_name, business_launch_assignee_phone, business_launch_assignee_email')
        .eq('id', payload.event_id)
        .maybeSingle();
      if (eventErr) throw eventErr;
      if (!event) throw new Error('Event not found');
      if (event.agency_id !== profile.agency_id && profile.role !== 'super_admin') {
        throw new Error('Not authorized for this event');
      }

      const firstName = String(payload.first_name || '').trim();
      const lastName = String(payload.last_name || '').trim();
      if (!firstName || !lastName) throw new Error('First and last name are required');

      // Same normalization as submit-pm-event / findDuplicatesByPhoneOrEmail
      const email = normalizeImportEmail(payload.email);
      const phone = normalizeImportPhone(payload.phone);

      let invite = null;
      if (payload.invitation_link_id) {
        const { data: inviteRow, error: inviteErr } = await supabase
          .from('pm_event_invitation_links')
          .select('id, slug, user_id, mentors_user_id, placeholder_owner_name, event_id')
          .eq('id', payload.invitation_link_id)
          .eq('event_id', event.id)
          .maybeSingle();
        if (inviteErr) throw inviteErr;
        invite = inviteRow;
      }

      // Shared DB helper — same resolution order as submit-pm-event.
      const { data: ownershipRaw, error: ownerErr } = await supabase.rpc(
        'resolve_pm_event_registration_owner',
        {
          p_event_id: event.id,
          p_invitation_link_id: invite?.id ?? null,
        },
      );
      if (ownerErr) throw ownerErr;
      const ownership = ownershipRaw && typeof ownershipRaw === 'object' ? ownershipRaw : {};
      const ownerId = ownership.owner_id || null;
      const ownerSource = ownership.source || 'none';

      let owner = null;
      if (ownerId) {
        const { data: ownerRow } = await supabase
          .from('users')
          .select('id, full_name, email')
          .eq('id', ownerId)
          .maybeSingle();
        owner = ownerRow;
      }

      const capturingName = owner?.full_name
        || event.business_launch_assignee_name
        || null;
      const capturingEmail = owner?.email
        || event.business_launch_assignee_email
        || null;

      const inviterUserIdForMatch =
        ownerSource === 'invite_agent' || ownerSource === 'invite_student_sponsor'
          ? ownerId
          : (invite?.user_id || null);

      let inviterName = null;
      if (invite?.user_id) {
        const { data: inviterUser } = await supabase
          .from('users')
          .select('full_name')
          .eq('id', invite.user_id)
          .maybeSingle();
        inviterName = inviterUser?.full_name?.trim() || null;
      } else if (invite?.placeholder_owner_name?.trim()) {
        inviterName = invite.placeholder_owner_name.trim();
      } else if (ownership.student_name) {
        inviterName = ownership.student_name;
      }

      let leadId = await findMatchingLeadForAgency(
        event.agency_id,
        email,
        phone,
        inviterUserIdForMatch,
      );

      if (!leadId) {
        const { data: lead, error: leadErr } = await supabase
          .from('leads')
          .insert({
            agency_id: event.agency_id,
            created_by: ownerId,
            first_name: firstName,
            last_name: lastName,
            email,
            phone,
            lead_type: 'event',
            channel: 'IM',
            lead_source: 'event',
            status: 'new',
            event_title: event.title,
            capturing_agent_name: capturingName,
            capturing_agent_email: capturingEmail,
            metadata: {
              pm_event_id: event.id,
              source: 'door',
              invitation_link_id: invite?.id ?? null,
            },
          })
          .select('id')
          .single();
        if (leadErr) throw leadErr;
        leadId = lead.id;
      }

      const nowIso = new Date().toISOString();
      const checkInNow = Boolean(payload.check_in_now);

      let matchedUserId = null;
      if (email) {
        const { data: userRows, error: userMatchErr } = await supabase
          .from('users')
          .select('id, email')
          .not('email', 'is', null)
          .ilike('email', email)
          .limit(10);
        if (userMatchErr) throw userMatchErr;
        matchedUserId = (userRows ?? []).find(
          (row) => normalizeImportEmail(row.email) === email,
        )?.id ?? null;
      }

      const { data: reg, error: regErr } = await supabase
        .from('pm_event_registrations')
        .insert({
          event_id: event.id,
          agency_id: event.agency_id,
          session_id: payload.session_id || null,
          lead_id: leadId,
          invitation_link_id: invite?.id ?? null,
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          matched_user_id: matchedUserId,
          source: 'door',
          checked_in_at: checkInNow ? nowIso : null,
          checked_in_by: checkInNow ? profile.id : null,
        })
        .select()
        .single();
      if (regErr) throw regErr;

      // UI already offers session select; empty → nearest upcoming / most recent for the note.
      const session = await resolvePmSessionForNote(event.id, payload.session_id || null);
      const timezone = session?.timezone?.trim() || PM_DEFAULT_TZ;
      const { error: logErr } = await supabase.from('activity_logs').insert({
        agency_id: event.agency_id,
        lead_id: leadId,
        agent_id: ownerId || profile.id,
        activity_type: 'event_registration',
        activity_date: todayDateInTimezone(timezone),
        notes: buildPmEventRegistrationNotes(
          {
            eventTitle: event.title,
            inviteSlug: invite?.slug ?? null,
            inviterName,
            session,
            timezone,
            ownerSource,
            studentName: ownership.student_name || null,
            sponsorName: ownership.sponsor_name || null,
            marketingDirectorName: ownership.marketing_director_name || null,
            businessLaunchAssigneeName: event.business_launch_assignee_name,
            placeholderOwnerName: invite?.placeholder_owner_name || null,
            isDoor: true,
            doorStaffName: profile.full_name,
          },
          checkInNow ? nowIso : (reg.created_at || nowIso),
        ),
      });
      if (logErr) {
        console.error('[api.pmEventRegistrations.createDoor] activity_logs insert failed', logErr);
        throw logErr;
      }

      return reg;
    },

    checkIn: async (id, checkedIn = true) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);

      const patch = checkedIn
        ? { checked_in_at: new Date().toISOString(), checked_in_by: profile.id }
        : { checked_in_at: null, checked_in_by: null };

      const { data, error } = await supabase
        .from('pm_event_registrations')
        .update(patch)
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Check-in failed — not found or permission denied');
      return data;
    },

    setAssociateFlag: async (id, value) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('pm_event_registrations')
        .update({ manually_flagged_associate: Boolean(value) })
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Could not update associate flag — not found or permission denied');
      return data;
    },

    /**
     * Manual CRM outreach/RSVP outcome (not email delivery).
     * Relies on pm_event_registrations_update_manage RLS (can_manage_pm_events).
     * @param {string} id registration id
     * @param {'confirmed'|'cancelled'|'no_answer'|'not_sure'|null} status
     */
    setConfirmationStatus: async (id, status) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      const allowed = new Set(['confirmed', 'cancelled', 'no_answer', 'not_sure']);
      if (status !== null && !allowed.has(status)) {
        throw new Error('Invalid confirmation status');
      }
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('pm_event_registrations')
        .update({ confirmation_status: status })
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Could not update confirmation status — not found or permission denied');
      return data;
    },

    /**
     * Link an existing agency lead to a registration (sets lead_id only).
     * Relies on pm_event_registrations_update_manage RLS (can_manage_pm_events).
     */
    linkToLead: async (registrationId, leadId) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      if (!registrationId) throw new Error('Registration id is required');
      if (!leadId) throw new Error('Lead id is required');

      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data: reg, error: regErr } = await supabase
        .from('pm_event_registrations')
        .select('id, agency_id, lead_id')
        .eq('id', registrationId)
        .maybeSingle();
      if (regErr) throw regErr;
      if (!reg) throw new Error('Registration not found or permission denied');
      if (reg.agency_id !== profile.agency_id && profile.role !== 'super_admin') {
        throw new Error('Not authorized for this registration');
      }

      const { data: lead, error: leadErr } = await supabase
        .from('leads')
        .select('id, agency_id, first_name, last_name, email, phone, status')
        .eq('id', leadId)
        .maybeSingle();
      if (leadErr) throw leadErr;
      if (!lead) throw new Error('Lead not found or permission denied');
      if (lead.agency_id !== reg.agency_id) {
        throw new Error('Lead must belong to the same agency as this registration');
      }

      const { data, error } = await supabase
        .from('pm_event_registrations')
        .update({ lead_id: leadId })
        .eq('id', registrationId)
        .select(`
          *,
          session:session_id(id, title, starts_at),
          invitation_link:invitation_link_id(id, slug, user_id, user:user_id(id, full_name)),
          lead:lead_id(
            id,
            status,
            first_name,
            last_name,
            email,
            phone,
            created_by,
            exclusivity_status,
            owner:created_by(id, full_name)
          )
        `)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Could not link lead — not found or permission denied');
      return data;
    },

    /**
     * Create a CRM lead from an unlinked registration, then set registration.lead_id.
     * Matches submit-pm-event / createDoor lead_type + lead_source conventions.
     */
    createLeadForRegistration: async (registrationId) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      if (!registrationId) throw new Error('Registration id is required');

      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const { data: reg, error: regErr } = await supabase
        .from('pm_event_registrations')
        .select(`
          id, agency_id, event_id, lead_id, first_name, last_name, email, phone,
          invitation_link_id, source,
          event:event_id(id, title, agency_id)
        `)
        .eq('id', registrationId)
        .maybeSingle();
      if (regErr) throw regErr;
      if (!reg) throw new Error('Registration not found or permission denied');
      if (reg.agency_id !== profile.agency_id && profile.role !== 'super_admin') {
        throw new Error('Not authorized for this registration');
      }
      if (reg.lead_id) {
        throw new Error('This registration is already linked to a lead');
      }

      const firstName = String(reg.first_name || '').trim();
      const lastName = String(reg.last_name || '').trim();
      if (!firstName || !lastName) throw new Error('Registration is missing first or last name');

      const email = normalizeImportEmail(reg.email);
      const phone = normalizeImportPhone(reg.phone);
      const eventTitle = reg.event?.title || null;

      const { data: lead, error: leadErr } = await supabase
        .from('leads')
        .insert({
          agency_id: reg.agency_id,
          created_by: profile.id,
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          lead_type: 'event',
          channel: 'IM',
          lead_source: 'event',
          status: 'new',
          event_title: eventTitle,
          metadata: {
            pm_event_id: reg.event_id,
            source: 'registration_create_lead',
            registration_id: reg.id,
            invitation_link_id: reg.invitation_link_id ?? null,
          },
        })
        .select('id, first_name, last_name, email, phone, status, lead_type')
        .single();
      if (leadErr) throw leadErr;

      const { data: updatedReg, error: updateErr } = await supabase
        .from('pm_event_registrations')
        .update({ lead_id: lead.id })
        .eq('id', registrationId)
        .select('id, lead_id')
        .maybeSingle();
      if (updateErr) throw updateErr;
      if (!updatedReg) {
        throw new Error('Lead was created but could not link it to the registration — permission denied');
      }

      return lead;
    },

    sendRemindersNow: async (eventId) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      if (!eventId) throw new Error('Event id is required');
      const { data, error } = await supabase.functions.invoke('send-pm-event-reminders', {
        body: { event_id: eventId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(typeof data.error === 'string' ? data.error : 'Send failed');
      return data;
    },

    /** One-off HTML broadcast to every registration with an email on file. */
    sendCustomEmail: async (eventId, { subject, htmlBody } = {}) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      if (!eventId) throw new Error('Event id is required');
      const trimmedSubject = String(subject || '').trim();
      const trimmedBody = String(htmlBody || '').trim();
      if (!trimmedSubject) throw new Error('Subject is required');
      if (!trimmedBody) throw new Error('Email body is required');

      const { data, error } = await supabase.functions.invoke('send-pm-event-custom-email', {
        body: {
          event_id: eventId,
          subject: trimmedSubject,
          html_body: trimmedBody,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(typeof data.error === 'string' ? data.error : 'Send failed');
      return data;
    },

    /**
     * Bulk-invite selected registrations to another published Mentors event.
     * → Edge `send-pm-event-reinvite` (staff JWT + can_manage_pm_events).
     */
    sendReinvite: async (registrationIds, targetEventId) => {
      if (useMock) throw new Error('Mentors events require Supabase.');
      if (!targetEventId) throw new Error('Target event id is required');
      const ids = Array.isArray(registrationIds)
        ? [...new Set(registrationIds.filter(Boolean).map((id) => String(id)))]
        : [];
      if (!ids.length) throw new Error('Select at least one registration');

      const { data, error } = await supabase.functions.invoke('send-pm-event-reinvite', {
        body: {
          registration_ids: ids,
          target_event_id: targetEventId,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(typeof data.error === 'string' ? data.error : 'Send failed');
      return data;
    },
  },

  businessLaunches: {
    getAllForAssociate: async (associateId) => {
      if (useMock) return [];
      if (!associateId) return [];
      const { data, error } = await supabase
        .from('business_launches')
        .select('*')
        .eq('associate_id', associateId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },

    /**
     * Agency-wide launches for leadership views. Optional associateIds scopes to My Team.
     * Relies on leadership SELECT RLS.
     */
    listForAgency: async ({ agencyId, associateIds = null } = {}) => {
      if (useMock) return [];
      if (!agencyId) return [];
      if (Array.isArray(associateIds) && associateIds.length === 0) return [];

      let q = supabase
        .from('business_launches')
        .select(`
          id, agency_id, associate_id, launch_type, status, scheduled_at, completed_at,
          created_at, pm_event_id,
          associate:associate_id(id, full_name, email, role)
        `)
        .eq('agency_id', agencyId)
        .order('created_at', { ascending: false });
      if (Array.isArray(associateIds)) {
        q = q.in('associate_id', associateIds);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },

    getById: async (id) => {
      if (useMock) return null;
      if (!id) throw new Error('Launch id is required');
      const { data, error } = await supabase
        .from('business_launches')
        .select(`
          *,
          associate:associate_id(id, full_name, email, role),
          launch_steps(*),
          pm_event:pm_event_id(
            id, title, status, slug, pipeline_event_type,
            pm_event_sessions(id, title, starts_at, ends_at, timezone, location_text, meeting_url, sort_order)
          )
        `)
        .eq('id', id)
        .single();
      if (error) throw error;
      const launch_steps = (data.launch_steps ?? []).slice().sort(
        (a, b) => (a.step_number ?? 0) - (b.step_number ?? 0),
      );
      const rawEvent = data.pm_event ?? null;
      const sessions = (rawEvent?.pm_event_sessions ?? []).slice().sort(
        (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.starts_at).localeCompare(String(b.starts_at)),
      );
      const pm_event = rawEvent
        ? (() => {
            const { pm_event_sessions: _s, ...event } = rawEvent;
            return { ...event, sessions };
          })()
        : null;
      return { ...data, launch_steps, pm_event };
    },

    create: async ({ associateId, launchType, supportedAssociateId }) => {
      if (useMock) throw new Error('Pipeline launches require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');
      if (!associateId) throw new Error('associateId is required');
      if (!launchType) throw new Error('launchType is required');
      if (launchType === 'support_teammate' && !supportedAssociateId) {
        throw new Error('supportedAssociateId is required when launchType is support_teammate');
      }

      const { data: launch, error } = await supabase
        .from('business_launches')
        .insert({
          agency_id: profile.agency_id,
          associate_id: associateId,
          launch_type: launchType,
          supported_associate_id: launchType === 'support_teammate' ? supportedAssociateId : null,
          pm_event_id: null,
        })
        .select()
        .single();
      if (error) throw error;

      const stepRows = [
        { step_type: 'flyer', step_number: 1, status: 'current' },
        { step_type: 'crm_invite', step_number: 2, status: 'upcoming' },
        { step_type: 'remind_confirm', step_number: 3, status: 'upcoming' },
        { step_type: 'event_day', step_number: 4, status: 'upcoming' },
        { step_type: 'follow_up', step_number: 5, status: 'upcoming' },
      ].map((step) => ({
        launch_id: launch.id,
        agency_id: launch.agency_id,
        ...step,
      }));

      const { data: insertedSteps, error: stepsError } = await supabase
        .from('launch_steps')
        .insert(stepRows)
        .select();
      if (stepsError) {
        await supabase.from('business_launches').delete().eq('id', launch.id);
        throw stepsError;
      }

      const launch_steps = (insertedSteps ?? []).slice().sort(
        (a, b) => (a.step_number ?? 0) - (b.step_number ?? 0),
      );
      return { ...launch, launch_steps };
    },

    updateStatus: async (id, status) => {
      if (useMock) throw new Error('Pipeline launches require Supabase.');
      if (!id) throw new Error('Launch id is required');
      if (!status) throw new Error('status is required');
      const patch = { status };
      if (status === 'completed') patch.completed_at = new Date().toISOString();
      const { data, error } = await supabase
        .from('business_launches')
        .update(patch)
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },

    getCompletedCountForAssociate: async (associateId) => {
      if (useMock) return 0;
      if (!associateId) return 0;
      const { count, error } = await supabase
        .from('business_launches')
        .select('id', { count: 'exact', head: true })
        .eq('associate_id', associateId)
        .eq('status', 'completed');
      if (error) throw error;
      return count ?? 0;
    },

    linkToPmEvent: async (launchId, pmEventId) => {
      if (useMock) throw new Error('Pipeline launches require Supabase.');
      if (!launchId) throw new Error('Launch id is required');
      const { data, error } = await supabase
        .from('business_launches')
        .update({ pm_event_id: pmEventId ?? null })
        .eq('id', launchId)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },
  },

  launchSteps: {
    updateStatus: async (stepId, status) => {
      if (useMock) throw new Error('Pipeline launches require Supabase.');
      if (!stepId) throw new Error('stepId is required');
      if (!status) throw new Error('status is required');

      const { data: step, error: fetchError } = await supabase
        .from('launch_steps')
        .select('id, launch_id, step_number, status')
        .eq('id', stepId)
        .single();
      if (fetchError) throw fetchError;

      const patch = { status };
      if (status === 'done') patch.completed_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('launch_steps')
        .update(patch)
        .eq('id', stepId)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');

      if (status === 'done') {
        const { data: next, error: nextErr } = await supabase
          .from('launch_steps')
          .select('id, status')
          .eq('launch_id', step.launch_id)
          .eq('step_number', step.step_number + 1)
          .maybeSingle();
        if (nextErr) throw nextErr;
        if (next && next.status === 'upcoming') {
          const { error: advanceErr } = await supabase
            .from('launch_steps')
            .update({ status: 'current' })
            .eq('id', next.id);
          if (advanceErr) throw advanceErr;
        }
      }

      return data;
    },
  },

  top25Contacts: {
    getAllForAssociate: async (associateId) => {
      if (useMock) return [];
      if (!associateId) return [];
      const { data, error } = await supabase
        .from('top25_contacts')
        .select('*')
        .eq('associate_id', associateId)
        .order('category', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },

    /**
     * Contacts for many associates (leadership team view). Empty associateIds → [].
     */
    listForAssociates: async (associateIds) => {
      if (useMock) return [];
      const ids = Array.isArray(associateIds) ? associateIds.filter(Boolean) : [];
      if (ids.length === 0) return [];
      const { data, error } = await supabase
        .from('top25_contacts')
        .select(`
          id, agency_id, associate_id, name, phone, email, category, status,
          converted_lead_id, created_at,
          associate:associate_id(id, full_name, email)
        `)
        .in('associate_id', ids)
        .order('category', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },

    getCategoryCounts: async (associateId) => {
      if (useMock) {
        return {
          trust_respect: 0,
          center_of_influence: 0,
          last_10_contacted: 0,
          entrepreneurship: 0,
          money_conversations: 0,
        };
      }
      if (!associateId) {
        return {
          trust_respect: 0,
          center_of_influence: 0,
          last_10_contacted: 0,
          entrepreneurship: 0,
          money_conversations: 0,
        };
      }
      const { data, error } = await supabase
        .from('top25_contacts')
        .select('category')
        .eq('associate_id', associateId);
      if (error) throw error;
      const counts = {
        trust_respect: 0,
        center_of_influence: 0,
        last_10_contacted: 0,
        entrepreneurship: 0,
        money_conversations: 0,
      };
      (data ?? []).forEach((row) => {
        if (row.category && Object.prototype.hasOwnProperty.call(counts, row.category)) {
          counts[row.category] += 1;
        }
      });
      return counts;
    },

    create: async (contact) => {
      if (useMock) throw new Error('Pipeline contacts require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');

      const name = contact?.name;
      const category = contact?.category;
      const associateId = contact?.associateId ?? contact?.associate_id;
      if (!associateId) throw new Error('associateId is required');
      if (!name) throw new Error('name is required');
      if (!category) throw new Error('category is required');

      const { data, error } = await supabase
        .from('top25_contacts')
        .insert({
          agency_id: profile.agency_id,
          associate_id: associateId,
          name,
          phone: contact.phone ?? null,
          email: contact.email ?? null,
          category,
          status: contact.status ?? 'added',
          linked_launch_id: contact.linkedLaunchId ?? contact.linked_launch_id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    updateStatus: async (id, status) => {
      if (useMock) throw new Error('Pipeline contacts require Supabase.');
      if (!id) throw new Error('Contact id is required');
      if (!status) throw new Error('status is required');
      const { data, error } = await supabase
        .from('top25_contacts')
        .update({ status })
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },

    linkToLaunch: async (id, launchId) => {
      if (useMock) throw new Error('Pipeline contacts require Supabase.');
      if (!id) throw new Error('Contact id is required');
      const { data, error } = await supabase
        .from('top25_contacts')
        .update({ linked_launch_id: launchId ?? null })
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },

    updateMacho: async (contactId, macho = {}) => {
      if (useMock) throw new Error('Pipeline contacts require Supabase.');
      if (!contactId) throw new Error('Contact id is required');
      const patch = {};
      if (macho.married !== undefined) patch.macho_married = Boolean(macho.married);
      if (macho.age25to55 !== undefined) patch.macho_age_25_55 = Boolean(macho.age25to55);
      if (macho.children !== undefined) patch.macho_children = Boolean(macho.children);
      if (macho.homeowner !== undefined) patch.macho_homeowner = Boolean(macho.homeowner);
      if (macho.occupation !== undefined) {
        const occupation = String(macho.occupation ?? '').trim();
        patch.macho_occupation = occupation || null;
      }
      if (!Object.keys(patch).length) throw new Error('No MACHO fields to update');
      const { data, error } = await supabase
        .from('top25_contacts')
        .update(patch)
        .eq('id', contactId)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },

    convertToLead: async (contactId, agencyId, associateId) => {
      if (useMock) throw new Error('Pipeline contacts require Supabase.');
      if (!contactId) throw new Error('Contact id is required');
      if (!agencyId) throw new Error('agencyId is required');
      if (!associateId) throw new Error('associateId is required');

      const { data: contact, error: contactErr } = await supabase
        .from('top25_contacts')
        .select('*')
        .eq('id', contactId)
        .maybeSingle();
      if (contactErr) throw contactErr;
      if (!contact) throw new Error('Contact not found');
      if (contact.agency_id !== agencyId) throw new Error('Contact does not belong to this agency');
      if (contact.associate_id !== associateId) throw new Error('Contact does not belong to this associate');

      if (contact.converted_lead_id) {
        const { data: existing, error: existingErr } = await supabase
          .from('leads')
          .select('*')
          .eq('id', contact.converted_lead_id)
          .maybeSingle();
        if (existingErr) throw existingErr;
        if (existing) return existing;
      }

      const { first_name, last_name } = splitPersonName(contact.name);
      if (!first_name) throw new Error('Contact name is required');

      const { data: associate, error: associateErr } = await supabase
        .from('users')
        .select('id, default_channel')
        .eq('id', associateId)
        .maybeSingle();
      if (associateErr) throw associateErr;
      const channel = associate?.default_channel || 'IM';

      const { data: lead, error: leadErr } = await supabase
        .from('leads')
        .insert({
          agency_id: agencyId,
          created_by: associateId,
          first_name,
          last_name: last_name || '',
          phone: contact.phone || null,
          email: normalizeImportEmail(contact.email),
          lead_type: 'top_25',
          channel,
          lead_source: 'top25',
          status: 'new',
          notes: formatTop25ConversionNotes(contact),
          exclusivity_status: 'exclusive',
          exclusive_to: associateId,
        })
        .select()
        .single();
      if (leadErr) throw leadErr;

      const { error: linkErr } = await supabase
        .from('top25_contacts')
        .update({ converted_lead_id: lead.id })
        .eq('id', contactId);
      if (linkErr) throw linkErr;

      return lead;
    },
  },

  pipelineEvents: {
    getForAssociate: async (associateId, pipelineEventType = null) => {
      if (useMock) return [];

      const userId = await getSessionUserId();
      let agencyId = null;
      if (userId) {
        const profile = await fetchProfileRowForAuthUser(userId);
        agencyId = profile?.agency_id ?? null;
      }
      if (!agencyId && associateId) {
        const { data: associate, error: associateErr } = await supabase
          .from('users')
          .select('agency_id')
          .eq('id', associateId)
          .maybeSingle();
        if (associateErr) throw associateErr;
        agencyId = associate?.agency_id ?? null;
      }
      if (!agencyId) return [];

      let q = supabase
        .from('pm_events')
        .select(`
          *,
          agent:agent_id(id, full_name, email, title, headshot_url),
          marketing_director:marketing_director_id(id, full_name, email),
          pm_event_sessions(*),
          pm_event_registrations(count)
        `)
        .eq('agency_id', agencyId)
        .eq('status', 'published')
        .order('created_at', { ascending: false });
      if (pipelineEventType) {
        q = q.eq('pipeline_event_type', pipelineEventType);
      }

      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row) => {
        const { pm_event_sessions: sessRows, pm_event_registrations: regAgg, ...event } = row;
        const sessions = (Array.isArray(sessRows) ? sessRows : []).slice().sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.starts_at).localeCompare(String(b.starts_at)),
        );
        const registrationCount = Array.isArray(regAgg) && regAgg[0]?.count != null ? regAgg[0].count : 0;
        return {
          ...event,
          sessions,
          session_count: sessions.length,
          registration_count: registrationCount,
        };
      });
    },

    getInvitationLink: async (eventId, userId) => {
      return api.pmEventInvitationLinks.getByEventAndUser(eventId, userId);
    },

    /**
     * Invite URLs for an event (+ optional invitation row).
     * primaryUrl: finplan.club/{eventSlug}?invite={inviteSlug} (multi-event alias; ?invite= required)
     * customUrl: https://{custom_domain}/{inviteSlug} when the event has a dedicated custom domain
     * (bare invite path — single-event host only).
     */
    buildInviteUrls: (event, invite = null) => {
      const eventSlug = event?.slug || null;
      const inviteSlug = invite?.slug || null;
      const primaryUrl = getPmEventLiveUrl(eventSlug, inviteSlug);
      const host = normalizeCustomDomain(event?.custom_domain);
      const customUrl = host && inviteSlug ? `https://${host}/${inviteSlug}` : null;
      return { primaryUrl, customUrl };
    },

    createInvitationLink: async (eventId, userId, chosenSlug = null) => {
      if (useMock) throw new Error('Pipeline events require Supabase.');
      if (!eventId) throw new Error('Event id is required');
      if (!userId) throw new Error('User id is required');

      const existing = await api.pmEventInvitationLinks.getByEventAndUser(eventId, userId);
      if (existing) return existing;

      const requested = String(chosenSlug || '').trim().toLowerCase();
      if (requested) {
        if (!api.agentPublicPages.isValidSlug(requested)) {
          throw new Error(
            'Slug must be 1–50 characters: lowercase letters, numbers, and hyphens only (no leading/trailing hyphen).',
          );
        }
        return api.pmEventInvitationLinks.create({
          event_id: eventId,
          user_id: userId,
          slug: requested,
        });
      }

      const profile = await fetchProfileRowForAuthUser(userId);
      const { data: page } = await supabase
        .from('agent_public_pages')
        .select('slug')
        .eq('user_id', userId)
        .maybeSingle();
      const { data: existingRows } = await supabase
        .from('pm_event_invitation_links')
        .select('slug')
        .eq('event_id', eventId);
      const taken = new Set((existingRows || []).map((r) => String(r.slug || '').toLowerCase()));
      let base = (page?.slug || slugify(profile?.full_name || 'invite'))
        .slice(0, 50)
        .replace(/-+$/g, '') || 'invite';
      if (!api.agentPublicPages.isValidSlug(base)) base = 'invite';
      let slug = base;
      if (taken.has(slug)) {
        slug = null;
        for (let n = 2; n < 100; n += 1) {
          const suffix = `-${n}`;
          const candidate = `${base.slice(0, Math.max(1, 50 - suffix.length))}${suffix}`;
          if (api.agentPublicPages.isValidSlug(candidate) && !taken.has(candidate)) {
            slug = candidate;
            break;
          }
        }
        if (!slug) slug = `${base.slice(0, 40)}-${Date.now().toString(36)}`.slice(0, 50);
      }

      return api.pmEventInvitationLinks.create({
        event_id: eventId,
        user_id: userId,
        slug,
      });
    },

    getRegistrationsForEvent: async (eventId) => {
      return api.pmEventRegistrations.getByEvent(eventId);
    },
  },

  cftProgress: {
    getTraineesForCft: async (cftUserId) => {
      if (useMock) return [];
      if (!cftUserId) return [];
      const { data, error } = await supabase
        .from('cft_progress')
        .select('*')
        .eq('cft_user_id', cftUserId)
        .order('started_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },

    getOwnProgress: async (traineeUserId) => {
      if (useMock) return null;
      if (!traineeUserId) return null;
      const { data, error } = await supabase
        .from('cft_progress')
        .select('*')
        .eq('trainee_user_id', traineeUserId)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },

    create: async ({ cftUserId, traineeUserId, isFirstTeammate }) => {
      if (useMock) throw new Error('CFT progress requires Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');
      if (!cftUserId) throw new Error('cftUserId is required');
      if (!traineeUserId) throw new Error('traineeUserId is required');

      const { data, error } = await supabase
        .from('cft_progress')
        .insert({
          agency_id: profile.agency_id,
          cft_user_id: cftUserId,
          trainee_user_id: traineeUserId,
          is_first_teammate: Boolean(isFirstTeammate),
        })
        .select()
        .single();
      if (error) {
        const msg = String(error?.message || '');
        if (error?.code === '23505' || /duplicate key|unique constraint/i.test(msg)) {
          throw new Error('This CFT already has a first teammate. Only one first-teammate trainee is allowed.');
        }
        throw error;
      }
      return data;
    },

    updateTier: async (id, currentTier) => {
      if (useMock) throw new Error('CFT progress requires Supabase.');
      if (!id) throw new Error('CFT progress id is required');
      if (!currentTier) throw new Error('currentTier is required');
      const { data, error } = await supabase
        .from('cft_progress')
        .update({ current_tier: currentTier })
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },

    markGraduated: async (id) => {
      if (useMock) throw new Error('CFT progress requires Supabase.');
      if (!id) throw new Error('CFT progress id is required');
      const { data, error } = await supabase
        .from('cft_progress')
        .update({ graduated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },
  },

  reminders: {
    _select: `
      *,
      assignee:users!reminders_assigned_to_fkey(id, full_name, email),
      creator:users!reminders_created_by_fkey(id, full_name, email),
      lead:leads!reminders_lead_id_fkey(id, first_name, last_name)
    `,

    getForLead: async (leadId) => {
      if (useMock) return [];
      if (!leadId) return [];
      const { data, error } = await supabase
        .from('reminders')
        .select(api.reminders._select)
        .eq('lead_id', leadId)
        .order('due_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },

    getMine: async () => {
      if (useMock) return [];
      const userId = await getSessionUserId();
      if (!userId) return [];
      const { data, error } = await supabase
        .from('reminders')
        .select(api.reminders._select)
        .eq('assigned_to', userId)
        .order('due_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },

    getCreatedByMe: async () => {
      if (useMock) return [];
      const userId = await getSessionUserId();
      if (!userId) return [];
      const { data, error } = await supabase
        .from('reminders')
        .select(api.reminders._select)
        .eq('created_by', userId)
        .order('due_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },

    create: async (input) => {
      if (useMock) throw new Error('Reminders require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const profile = await fetchProfileRowForAuthUser(userId);
      if (!profile?.agency_id) throw new Error('No agency profile');
      const title = String(input?.title || '').trim();
      if (!title) throw new Error('title is required');
      if (!input?.due_at) throw new Error('due_at is required');
      const assignedTo = input.assigned_to || userId;
      const { data, error } = await supabase
        .from('reminders')
        .insert({
          agency_id: profile.agency_id,
          lead_id: input.lead_id ?? null,
          title,
          notes: input.notes ?? null,
          due_at: input.due_at,
          assigned_to: assignedTo,
          created_by: userId,
          notify_email: input.notify_email !== false,
        })
        .select(api.reminders._select)
        .single();
      if (error) throw error;
      return data;
    },

    update: async (id, patch = {}) => {
      if (useMock) throw new Error('Reminders require Supabase.');
      if (!id) throw new Error('Reminder id is required');
      const updates = {};
      if (patch.title !== undefined) updates.title = String(patch.title || '').trim();
      if (patch.notes !== undefined) updates.notes = patch.notes;
      if (patch.assigned_to !== undefined) updates.assigned_to = patch.assigned_to;
      if (patch.notify_email !== undefined) updates.notify_email = Boolean(patch.notify_email);
      if (patch.lead_id !== undefined) updates.lead_id = patch.lead_id || null;
      if (patch.status !== undefined) updates.status = patch.status;
      if (patch.completed_at !== undefined) updates.completed_at = patch.completed_at;
      if (patch.due_at !== undefined) {
        updates.due_at = patch.due_at;
        const dueMs = new Date(patch.due_at).getTime();
        if (Number.isFinite(dueMs) && dueMs > Date.now()) {
          updates.notified_at = null;
        }
      }
      if (Object.keys(updates).length === 0) throw new Error('No reminder fields to update');
      const { data, error } = await supabase
        .from('reminders')
        .update(updates)
        .eq('id', id)
        .select(api.reminders._select)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Update failed — not found or permission denied');
      return data;
    },

    complete: async (id) => {
      return api.reminders.update(id, {
        status: 'completed',
        completed_at: new Date().toISOString(),
      });
    },

    dismiss: async (id) => {
      return api.reminders.update(id, {
        status: 'dismissed',
        completed_at: null,
      });
    },

    delete: async (id) => {
      if (useMock) throw new Error('Reminders require Supabase.');
      if (!id) throw new Error('Reminder id is required');
      const { error } = await supabase.from('reminders').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
  },

  badges: {
    getSidebarCounts: async () => {
      if (useMock) {
        return { mentionsUnread: 0, exclusivityPending: 0, transfersPending: 0 };
      }
      const { data, error } = await supabase.rpc('get_sidebar_badge_counts');
      if (error) throw error;
      const row = data && typeof data === 'object' ? data : {};
      const toCount = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
      };
      return {
        mentionsUnread: toCount(row.mentions_unread),
        exclusivityPending: toCount(row.exclusivity_pending),
        transfersPending: toCount(row.transfers_pending),
      };
    },
  },

  notifications: {
    getMine: async (limit = 50) => {
      if (useMock) return [];
      const userId = await getSessionUserId();
      if (!userId) return [];
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(Math.min(Number(limit) || 50, 200));
      if (error) throw error;
      return data ?? [];
    },

    getUnreadCount: async () => {
      if (useMock) return 0;
      const userId = await getSessionUserId();
      if (!userId) return 0;
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .is('read_at', null);
      if (error) throw error;
      return count ?? 0;
    },

    markRead: async (id) => {
      if (useMock) throw new Error('Notifications require Supabase.');
      if (!id) throw new Error('Notification id is required');
      const { data, error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id)
        .is('read_at', null)
        .select()
        .maybeSingle();
      if (error) throw error;
      return data;
    },

    markAllRead: async () => {
      if (useMock) throw new Error('Notifications require Supabase.');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .is('read_at', null);
      if (error) throw error;
      return { success: true };
    },
  },

  /** Per-user, per-page list/table column visibility preferences. */
  userListViewPreferences: {
    get: async (viewKey) => {
      if (useMock) return { hidden_columns: [] };
      if (!viewKey) throw new Error('viewKey is required');
      const userId = await getSessionUserId();
      if (!userId) return { hidden_columns: [] };
      const { data, error } = await supabase
        .from('user_list_view_preferences')
        .select('view_key, hidden_columns, updated_at')
        .eq('user_id', userId)
        .eq('view_key', viewKey)
        .maybeSingle();
      if (error) throw error;
      return data ?? { view_key: viewKey, hidden_columns: [] };
    },

    save: async (viewKey, hiddenColumns) => {
      if (useMock) {
        return { view_key: viewKey, hidden_columns: hiddenColumns ?? [] };
      }
      if (!viewKey) throw new Error('viewKey is required');
      const userId = await getSessionUserId();
      if (!userId) throw new Error('Not authenticated');
      const cols = Array.isArray(hiddenColumns) ? hiddenColumns : [];
      const { data, error } = await supabase
        .from('user_list_view_preferences')
        .upsert(
          {
            user_id: userId,
            view_key: viewKey,
            hidden_columns: cols,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,view_key' },
        )
        .select('view_key, hidden_columns, updated_at')
        .single();
      if (error) throw error;
      return data;
    },
  },
};